/**
 * Rendering.
 *
 * Placeholder geometry, built procedurally, so every system can be played and
 * judged before any art exists. Models load through the same code path once there
 * is something to load, and nothing above this file knows the difference.
 */

import {
  GroundKind,
  isDiagonal,
  SEA_LEVEL,
  SHORE_BAND,
  STOREY_HEIGHT,
  tileOf,
} from '@inverse/common';
import { GROUND_STEPS, normalsFor, PIECE_TILES, type Piece, pieceOf, piecesOf } from './pieces';
import { aimWater, flowOf, stepWater, giveShoreMap, waterMaterial } from './water';
import { BED_TEXELS, drawShoreMap, SHORE_TEXELS, stillCovers, type ShoreMap } from './shore';
import { PatchKind, type EntityLook, type WorldPatchPacket } from '@inverse/protocol';
import { Avatar, DEFAULT_APPEARANCE, loadUploaded, modelExists, MODEL_SCALE } from './avatars';
import type { GameSettings } from './camera';
import * as THREE from 'three';
import { type LocalRegion, RemoteEntity, type WorldState } from './world-state';
import { clutterFor, forgetClutter } from './clutter';

/**
 * How a chart reading is read back.
 *
 * Named here rather than imported, because a chart is arithmetic on numbers and
 * the same three constants are written where it is made.
 */
const CHART_WET = 0x80;
const CHART_LEVELS = 64;
const CHART_FLOOR = -10;
const CHART_CEILING = 100;

/** Water is a flag on the tile, and the only one that changes how ground is drawn. */
const WATER_FLAG = 1 << 6;

/**
 * How far past the region the sea is drawn, in tiles.
 *
 * Far enough that it runs past the fog in every direction, so the island sits in
 * open water rather than on a visible slab of it.
 */
const SEA_MARGIN = 280;

/**
 * How many chart readings the distant landscape puts between its vertices, and
 * how far below the real ground it sits.
 *
 * Coarse, because it is only ever seen from a long way off, and low, because
 * the detailed squares have to win wherever they exist: a backdrop poking
 * through the ground somebody is standing on would be worse than no backdrop.
 *
 * Dropping it was not enough on its own. The chart holds a height in one of
 * sixty-four steps between ten below the sea and a hundred above, so a step is
 * a tile and three quarters, and it is read every ten tiles: a facet with one
 * corner on the terrace behind Ardor's house and the other out in the wood is
 * drawn straight across the wood, well above it. It came out as a grey slab
 * lying over half the island.
 *
 * So the backdrop is also cut away around whoever is reading it. The ground is
 * sent a square either side of the one somebody stands in, and a square is
 * sixty-four tiles, so there is always detailed ground for sixty-four tiles in
 * every direction: a hole of fifty-two is covered by it wherever somebody
 * stands, and the cut can be a clean one with nothing drawn to soften it.
 */
const FAR_STEP = 2;
const FAR_BELOW = 3.2;
const FAR_HOLE = 52;

/**
 * What the far landscape is coloured, which is what the world map is coloured.
 *
 * The same bands, so the horizon and the map agree about where the snow line is
 * and where the desert starts. Muted a little, because everything at that
 * distance is seen through air.
 */
function farColour(wet: boolean, high: number, painted: number): THREE.Color {
  const held = new THREE.Color();

  if (wet) return held.copy(COLORS.deep);
  if (painted !== 0) return held.copy(groundColour(high, painted));

  return held.copy(groundColour(high, 0));
}

/** Segments across the sea surface. Enough for a swell to read, cheap to animate. */
const SEA_SEGMENTS = 72;

/**
 * How far from anything the open sea counts as being, in tiles.
 *
 * Ripples run in from a bank, and the sea has none worth speaking of within sight
 * of the island: the shallows are drawn by the ground showing through rather than
 * by the edge of the water.
 */
const SEA_OPENNESS = 12;

/** How fast the sea slides under itself, in tiles per second. */
const SEA_DRIFT = 0.22;

/**
 * How deep the water has to be for the bed under it to be as dark as it gets,
 * in tiles, and how far towards the colour of deep water that is.
 *
 * Long and gentle, both of them. What the bed is made of is what it looks like;
 * this only says that there is a lot of water in the way. Anything shorter and
 * the shelves in a lake bed come through as bands, which is the fault it was
 * written for.
 */
const BED_FADES = 9;
const BED_DARKEST = 0.75;

/**
 * How deep water has to be before it is drawn as deep as it gets, in tiles.
 *
 * It was a tile and a half, which is fine for a pond and wrong for anything
 * bigger: a lake bed rolls by about that much across its middle, so the whole
 * ramp was spent out there and every shelf in the ground came through the
 * surface as a patch of its own colour.
 *
 * With the bed no longer painted by depth as well, this can be long and gentle
 * and the shallows still read as shallows.
 */
const WATER_DEEPENS = 2.2;

/**
 * How clear water is at its clearest, where it is only just covering the ground.
 *
 * Not clear enough to see a tile boundary through, which is the whole reason
 * for the number: the ground under water is drawn per tile and the water over
 * it is not, so the water has to carry enough of its own colour to be water.
 */
const WATER_CLEAREST = 0.72;

/**
 * How far either side the bed is averaged when shading the water, in tiles.
 *
 * Only for the colour. The surface still sits where the ground says it sits;
 * this is a gentler reading of the same ground, so a flat shelf that ends in a
 * step is shaded as a slope rather than as a boundary.
 */
const DEPTH_SMOOTH = 1;

/** Name plate width in world units. Small, because sprites do not shrink with crowding. */
const LABEL_WIDTH = 1.5;

/** Names stop being drawn past this many tiles, so distant crowds do not become text. */
const LABEL_VISIBLE_DISTANCE = 22;

/** Names fade out over the last few tiles rather than blinking off. */
const LABEL_FADE_DISTANCE = 6;

/**
 * The distance a name plate is drawn at full width. Closer than this it shrinks in
 * step with the distance, which holds its apparent size steady. Without that, a name
 * a metre from a third person camera covers a third of the screen.
 */
const LABEL_REFERENCE_DISTANCE = 9;

/** Width of the grid patch drawn around the player, in tiles. */
const GRID_PATCH_TILES = 40;

/** How far overlay lines sit above the ground, so they are not lost inside it. */
const GRID_LIFT = 0.03;

/**
 * How far one storey sits above the one under it.
 *
 * Tall enough to walk under and short enough that a two storey building does not
 * read as a tower. Everything a storey holds is lifted by exactly this, so a
 * floor laid on the first storey meets the walls that stand on it.
 */
/**
 * One storey, re-exported from where the game's measurements live.
 *
 * Kept as an export here because everything drawing a building already imports
 * it from the scene, and the number itself belongs beside the rest of how big
 * things are rather than in the renderer.
 */
export { STOREY_HEIGHT };

/** The ring on the ground where somebody is going, and the one saying they are not. */
const MARKER_ACCENT = 0x0cd5e3;
const MARKER_REFUSED = 0xd8574a;

/** How long the refused tile keeps its mark before fading out of the way. */
const MARKER_REFUSED_MS = 2400;

/**
 * How sharply one kind of ground gives way to the next.
 *
 * A vertex takes a share of every tile whose middle is within 1 / this of it.
 * Under two, so that the vertices along the line between two tiles take half of
 * each; over one and a half, so that everything further in than a third of a
 * tile takes only its own.
 */
/**
 * How far a tile's colour reaches past its own edge, as one over the distance.
 *
 * How far a tile's colour reaches, as the reciprocal of a distance in tiles.
 *
 * Just under seven eighths of a tile. It has to be more than the half diagonal,
 * which is 0.708, or the corner where four tiles meet is out of reach of all of
 * them; and the further past it goes, the better a border running corner to
 * corner holds together. At 1.35 the reach was 0.74, barely over the line, and
 * a diagonal of single tiles was a dotted one.
 */
const GROUND_BLEND = 1.16;

/**
 * How far a tile's say about what it is painted carries, in tiles.
 *
 * This is what rounds a border. Read over the four tiles nearest a spot, the
 * line between two kinds is made of straight pieces and comes to a point
 * wherever it changes direction. Read over everything within a couple of tiles,
 * with a soft round falloff, the same line curves instead. Wider is rounder;
 * much wider and a single painted tile stops being able to hold its own ground.
 */
const GROUND_ROUNDING = 2.1;

/** How high a laid floor sits above the ground it covers. Matches the board shape. */
const FLOOR_LIFT = 0.08;

const COLORS = {
  grass: new THREE.Color(0x4a6b3a),
  grassDry: new THREE.Color(0x6b7340),
  dirt: new THREE.Color(0x5c4c34),
  rock: new THREE.Color(0x6a6560),
  water: new THREE.Color(0x2b4c63),
  shallow: new THREE.Color(0x4e97a0),
  deep: new THREE.Color(0x14313f),
  sand: new THREE.Color(0xa9926a),
  path: new THREE.Color(0x7a6a52),
  snow: new THREE.Color(0xdfe6ea),
  ash: new THREE.Color(0x3b3733),
  lava: new THREE.Color(0x7a3826),
  shingle: new THREE.Color(0x8a8578),
  mud: new THREE.Color(0x574a38),
  /** The stone a pool of lava is sitting in, seen through it. */
  moltenBed: new THREE.Color(0x2a0f08),
  surface: new THREE.Color(0x2f6f80),
  sky: new THREE.Color(0x1d2a30),
  fog: new THREE.Color(0x1d2a30),
  player: new THREE.Color(0x0cd5e3),
  otherPlayer: new THREE.Color(0x8fa9c4),
  npc: new THREE.Color(0x9c6b5a),
  boss: new THREE.Color(0xa33b3b),
};

interface EntityView {
  group: THREE.Group;
  /** The stand-in shape, shown until the model this one wants has come down. */
  body: THREE.Mesh;
  /** The disc under the feet, which goes when the body does. */
  shadow: THREE.Mesh;
  /** True while this one is standing under the player and is not being drawn. */
  underfoot: boolean;
  label: THREE.Sprite | null;
  /** The animated body, once there is one. */
  avatar: Avatar | null;
  /** The health bar over their head, once anything has happened to them. */
  bar: THREE.Sprite | null;
  /** When the bar stops being worth showing. */
  barUntil: number;
  /** While a body is falling over, and should not be taken away yet. */
  dyingUntil: number;
  /** Where it was last frame, which is how walking is told from standing. */
  wasAt: { x: number; z: number } | null;
}

export class Scene {
  readonly renderer: THREE.WebGLRenderer;
  /** The chain the picture is drawn through, which is what puts a stroke on things. */
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;

  /**
   * The ground, one mesh per chunk rather than one for the whole place.
   *
   * A region was one mesh, which is fine while a region is an island and
   * impossible once it is a mainland: the ground is drawn three vertices to the
   * tile, so twelve million tiles would be a hundred million vertices in one
   * buffer. Cut into squares it is a hundred meshes of a size a graphics card
   * has an opinion about, and painting a fence redraws one of them.
   */
  private readonly terrain = new Map<string, THREE.Mesh>();
  private sea: THREE.Mesh | null = null;
  private region: LocalRegion | null = null;
  private readonly entityViews = new Map<number, EntityView>();
  private localView: EntityView | null = null;

  /**
   * Whether your own character is drawn.
   *
   * Taken off screen while the editor holds the world: the camera is not on it
   * any more, and a figure standing in the middle of whatever is being built is
   * in the way of looking at it.
   */
  private showLocal = true;

  showLocalCharacter(showing: boolean): void {
    this.showLocal = showing;
    if (this.localView) this.localView.group.visible = showing;
  }

  /** Marker showing where a click sent the player. */
  private readonly clickMarker: THREE.Mesh;
  /** Where the walk really ends, when that is not where the click landed. */
  private readonly stopMarker: THREE.Mesh;
  /** How far the markers have breathed, so they throb rather than sit there. */
  private markerBeat = 0;
  /** When the markers went up, for the grace a finished walk is given. */
  private markerSince = 0;

  /** Tile grid patch and the outline of the tile the player occupies. */
  private readonly grid: THREE.LineSegments;
  /**
   * Instances not drawn because the player is standing in them, and the matrix
   * each one had before it was taken away, so it can be put back.
   */
  private readonly underfootScenery = new Map<string, THREE.Matrix4>();
  /** What that was worked out for, so it is not worked out every frame. */
  private underfootTile = '';

  /** A square inside every tile nothing can stand on, for the editor. */
  private readonly blockedTiles: THREE.LineSegments;
  /** When that was last worked out, so it is not worked out every frame. */
  private blockedAt = 0;
  private readonly tileMarker: THREE.Line;
  /** The rectangle the editor is dragging out, if any. */
  private readonly areaMarker: THREE.Line;

  /** Border drawn around whatever the pointer is over. */
  private readonly hoverMarker: THREE.Line;
  private overlayTile = { tx: Number.NaN, tz: Number.NaN };
  /** Where the grid patch is centred, which is not always where the player is. */
  private gridTile = { tx: Number.NaN, tz: Number.NaN };
  private overlayBuilt = false;

  /** Instanced scenery, kept so a ray hit can be turned back into a placed object. */
  /**
   * Everywhere in sight that something is burning, and what it is burning like.
   *
   * Written down as the scenery is built and lit a handful at a time. A town at
   * night has a hundred windows and a browser has room for about six real
   * lights, so the ones nearest to whoever is looking get them and the rest are
   * their own brightness and nothing more, which from any distance is the same
   * thing.
   */
  private readonly lamps: {
    x: number;
    y: number;
    z: number;
    reach: number;
    colour: number;
    atNight: boolean;
    flickers: boolean;
    shifts: boolean;
    /** Where in its own flicker this one is, so no two of them beat together. */
    beat: number;
  }[] = [];

  /** The real lights there are to go round, and which lamp each is standing in. */
  private readonly lit: { light: THREE.PointLight; at: number }[] = [];

  private readonly objectMeshes: {
    mesh: THREE.InstancedMesh;
    id: number;
    /** True for a part that is only there to be clicked on, never to be seen. */
    unseen: boolean;
    placements: {
      tx: number;
      tz: number;
      plane: number;
      rotation: number;
      scale: number;
      /** The height it was built to, for anything standing out over water. */
      lift?: number;
    }[];
  }[] = [];

  /** Piles of items on the ground, one small shape each. */
  private readonly groundItems = new Map<number, THREE.Mesh>();
  private readonly groundGeometry = new THREE.BoxGeometry(0.34, 0.16, 0.34);

  /** Settings the scene applies itself rather than asking about every frame. */
  private renderScale = 1;
  private showNames = true;
  private markerEnabled = true;

  private readonly labelCache = new Map<string, THREE.Texture>();
  private readonly raycaster = new THREE.Raycaster();

  /** Which region the surface was built for. */
  private seaKey = '';

  /** Scenery that is laid on the ground and stood on, which the pointer lands on. */
  private readonly floorIds = new Set<number>();

  /**
   * Which tiles have a floor laid on them, and how high it sits.
   *
   * Worked out when the scenery is built rather than looked up by walking the
   * region's objects, because it is asked for every character every frame and a
   * region holds thousands of things.
   *
   * The value is the height the floor was built to, or not a number where it
   * simply follows the ground under it.
   */
  private readonly floorTiles = new Map<number, number>();

  /** Scenery drawn overhead, which is taken away while somebody is under it. */
  private readonly roofIds = new Set<number>();

  /** Outlines of the spawn zones, drawn only while somebody is editing. */
  private readonly zoneLines = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.85 }),
  );

  /** Outline around everything the editor is holding. */
  private readonly selectionLines = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0xa8f0f7, transparent: true, opacity: 0.95 }),
  );

  /**
   * The marks over whatever the followed quest step points at.
   *
   * A ring on the ground and a marker above it, the way a helper plugin does it:
   * enough to say "this one" across a clearing, quiet enough to be turned off
   * and not missed.
   */
  private readonly questMarks = new THREE.Group();

  /**
   * The ground one character is allowed to wander.
   *
   * Its own overlay rather than part of the selection, because it is a fact about
   * whoever is selected rather than a thing being selected, and both are on screen
   * at once while somebody paints one.
   */
  private readonly roamLines = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0x9ce6a0, transparent: true, opacity: 0.9 }),
  );

  /** The tiles a freehand drag has covered so far. */
  private readonly trailLines = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0xc8a86a, transparent: true, opacity: 0.95 }),
  );

  /** Set when the ground has changed under an edit, cleared when it is redrawn. */
  private readonly terrainDirty = new Set<string>();

  /** Whether the ground under the scenery has moved since it was last placed. */
  private groundMoved = false;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      // The stroke around whatever is under the pointer is drawn everywhere the
      // thing itself is not, and the stencil is what remembers where that is.
      stencil: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2) * this.renderScale);
    this.renderer.shadowMap.enabled = false;

    this.scene = new THREE.Scene();
    // The sky and the fog share a colour, so the ground meets the horizon instead of
    // ending against a black band.
    this.scene.background = COLORS.sky;
    this.scene.fog = new THREE.Fog(COLORS.fog, 45, 130);

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 400);
    this.camera.position.set(0, 12, 12);

    this.sun = new THREE.DirectionalLight(0xffeccd, 1.9);
    this.sun.position.set(40, 70, 20);
    this.scene.add(this.sun);

    this.sky = new THREE.HemisphereLight(0x9dc0e0, 0x4a4030, 1.1);
    this.scene.add(this.sky);

    /*
     * The handful of real lights, made once and moved about.
     *
     * Made here rather than when something needs one, because adding a light to
     * a scene makes every material in it compile again: a torch lit by walking
     * round a corner would stall the frame it was lit on. These are in from the
     * first frame with nothing to shine on, and are moved to whatever is
     * nearest afterwards.
     */
    for (let index = 0; index < LIGHTS_TO_GO_ROUND; index++) {
      const light = new THREE.PointLight(0xffd27a, 0, 8, 1.2);
      light.visible = false;
      this.scene.add(light);
      this.lit.push({ light, at: -1 });
    }

    this.clickMarker = new THREE.Mesh(
      new THREE.RingGeometry(0.28, 0.4, 24),
      new THREE.MeshBasicMaterial({
        color: MARKER_ACCENT,
        transparent: true,
        side: THREE.DoubleSide,
      }),
    );
    this.clickMarker.rotation.x = -Math.PI / 2;
    this.clickMarker.visible = false;
    this.scene.add(this.clickMarker);

    // The second marker: where the walk will actually end, for a click on
    // somewhere that cannot be reached. This one is the ordinary gold ring,
    // because it is the ordinary thing it means: here is where you will stop.
    // The refused tile is the one drawn differently, in red, and it goes.
    this.stopMarker = new THREE.Mesh(
      new THREE.RingGeometry(0.28, 0.4, 24),
      new THREE.MeshBasicMaterial({
        color: MARKER_ACCENT,
        transparent: true,
        side: THREE.DoubleSide,
      }),
    );
    this.stopMarker.rotation.x = -Math.PI / 2;
    this.stopMarker.visible = false;
    this.scene.add(this.stopMarker);

    this.grid = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color: 0xd6e2ea, transparent: true, opacity: 0.16 }),
    );
    this.grid.visible = false;
    this.grid.frustumCulled = false;
    this.scene.add(this.grid);

    // A square inside every tile nothing can stand on. Red, and inside rather
    // than on the tile edge, so it reads as the tile itself being closed rather
    // than as a line between two of them.
    this.blockedTiles = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color: 0xd8574a, transparent: true, opacity: 0.7 }),
    );
    this.blockedTiles.visible = false;
    this.blockedTiles.frustumCulled = false;
    this.scene.add(this.blockedTiles);

    this.tileMarker = new THREE.Line(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color: 0x0cd5e3, transparent: true, opacity: 0.85 }),
    );
    this.tileMarker.visible = false;
    this.tileMarker.frustumCulled = false;
    this.scene.add(this.tileMarker);

    this.hoverMarker = new THREE.Line(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color: 0xd6e2ea, transparent: true, opacity: 0.5 }),
    );
    this.hoverMarker.visible = false;
    this.hoverMarker.frustumCulled = false;
    this.hoverMarker.renderOrder = 2;
    this.scene.add(this.hoverMarker);

    this.zoneLines.visible = false;
    this.zoneLines.frustumCulled = false;
    this.zoneLines.renderOrder = 2;
    this.scene.add(this.zoneLines);

    this.selectionLines.visible = false;
    this.selectionLines.frustumCulled = false;
    this.selectionLines.renderOrder = 3;
    this.scene.add(this.selectionLines);

    this.areaMarker = new THREE.Line(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color: 0xc8a86a, transparent: true, opacity: 0.95 }),
    );
    this.areaMarker.visible = false;
    this.areaMarker.frustumCulled = false;
    this.areaMarker.renderOrder = 2;
    this.scene.add(this.areaMarker);

    this.trailLines.visible = false;
    this.trailLines.frustumCulled = false;
    this.trailLines.renderOrder = 2;
    this.scene.add(this.trailLines);

    this.roamLines.visible = false;
    this.roamLines.frustumCulled = false;
    this.roamLines.renderOrder = 3;
    this.scene.add(this.roamLines);

    this.questMarks.visible = false;
    this.scene.add(this.questMarks);

    this.resize();
  }

  /**
   * Applies the settings the world itself cares about.
   *
   * Resolution, draw distance and name plates are the three that cost frames, so
   * they are the three a player on a weak machine wants to reach for.
   */
  applySettings(settings: GameSettings): void {
    this.showNames = settings.showNames;
    this.markerEnabled = settings.showClickMarker;
    this.dayAndNight = settings.dayAndNight;
    this.lightTheWorld();

    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.far = settings.viewDistance;
      this.scene.fog.near = Math.max(10, settings.viewDistance * 0.35);
    }
    this.camera.far = settings.viewDistance + 120;
    this.camera.updateProjectionMatrix();

    this.renderScale = settings.renderScale;
    this.resize();

    this.setClutter(settings.groundClutter);
  }

  /** The canvas the world is drawn on, for anything that has to measure it. */
  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  resize(): void {
    // How large the page says the canvas is, rather than how large the window
    // is: the world is a panel now, and in the docked layout it is inset by
    // whatever is docked round it.
    //
    // The style is left alone on purpose. Writing the size back into it would
    // make the canvas its own authority on how large it is, and the first
    // measurement would then be the only one that ever counted: it latched at
    // three hundred by a hundred and fifty, which is what an unstyled canvas is.
    const held = this.renderer.domElement;
    const width = Math.max(1, held.clientWidth || window.innerWidth);
    const height = Math.max(1, held.clientHeight || window.innerHeight);
    // The style is set as well as the buffer. Leaving it alone means the canvas
    // lays out at its buffer size, which on a screen with more than one device
    // pixel per CSS pixel is twice the window: the picture is then drawn at double
    // size with only its top left corner on screen, so the player sits off the
    // right edge and every click lands on the wrong tile.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2) * this.renderScale);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    // Two pixels and a half, whatever the window is: the stroke is grown on the
    // screen, so its width is measured there.
    STROKE_WIDTH.value.set((STROKE_PIXELS * 2) / width, (STROKE_PIXELS * 2) / height);
  }

  dispose(): void {
    this.renderer.dispose();
    this.groundGeometry.dispose();
    for (const texture of this.labelCache.values()) texture.dispose();
    this.labelCache.clear();
  }

  // -------------------------------------------------------------------------
  // Terrain
  // -------------------------------------------------------------------------

  /** Bilinear sample of the ground, matching the server's authored corner heights. */
  /**
   * Where the surface of the water on a tile lies, or null where there is none.
   *
   * Sea level counts: a jetty out over the shallows is standing on the sea, and
   * the sea is drawn by the plane rather than by a tile of its own.
   */
  waterLevelAt(x: number, z: number): number | null {
    const region = this.region;
    if (!region) return null;

    const tx = Math.floor(x) - region.originX;
    const tz = Math.floor(z) - region.originZ;
    if (tx < 0 || tz < 0 || tx >= region.width || tz >= region.height) return null;

    const index = tz * region.width + tx;
    if (((region.flags[index] ?? 0) & WATER_FLAG) === 0) return null;

    const level = region.water[index] ?? SEA_LEVEL;
    return Math.abs(level) < 0.001 ? SEA_LEVEL : level;
  }

  heightAt(x: number, z: number): number {
    const region = this.region;
    if (!region) return 0;

    const localX = Math.min(Math.max(x - region.originX, 0), region.width);
    const localZ = Math.min(Math.max(z - region.originZ, 0), region.height);

    const stride = region.width + 1;
    const x0 = Math.min(Math.floor(localX), region.width);
    const z0 = Math.min(Math.floor(localZ), region.height);
    const x1 = Math.min(x0 + 1, region.width);
    const z1 = Math.min(z0 + 1, region.height);

    const fx = localX - x0;
    const fz = localZ - z0;

    const h00 = region.heights[z0 * stride + x0] ?? 0;
    const h10 = region.heights[z0 * stride + x1] ?? 0;
    const h01 = region.heights[z1 * stride + x0] ?? 0;
    const h11 = region.heights[z1 * stride + x1] ?? 0;

    const top = h00 + (h10 - h00) * fx;
    const bottom = h01 + (h11 - h01) * fx;
    return top + (bottom - top) * fz;
  }

  buildRegion(region: LocalRegion): void {
    this.region = region;

    // The whole place, coarsely, so there is a horizon to look at while the
    // squares near the reader are still arriving.
    this.buildFarLandscape(region);

    for (const mesh of this.terrain.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.terrain.clear();
    this.terrainDirty.clear();

    for (const dressed of this.clutter.values()) {
      this.scene.remove(dressed);
      forgetClutter(dressed);
    }
    this.clutter.clear();

    // Nothing is drawn yet: the ground arrives a square at a time and each one
    // is drawn as it turns up. What is built here is an empty place of the
    // right size for them to fill.

    this.buildSea(region);
    // The water is drawn a square at a time round whoever is reading, and they
    // say where that is on the next frame.
    for (const mesh of this.waters.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.waters.clear();
    this.waterDirty.clear();
    this.wateredAt = null;

    this.buildObjects(region);

    // Overlay geometry is sampled off the terrain, so it has to be rebuilt with it.
    this.overlayBuilt = false;
  }

  /**
   * A square of ground has arrived.
   *
   * The square itself is drawn, and so are the ones beside it: a tile on an edge
   * changes how its neighbour blends and how its neighbour is lit, so a square
   * turning up makes the ground next to it slightly wrong until it is redrawn.
   * Only squares that have actually arrived are drawn, which is why the region
   * remembers which those are.
   */
  groundArrived(region: LocalRegion, tx: number, tz: number): void {
    this.region = region;

    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        this.terrainDirty.add(pieceOf(tx + dx * PIECE_TILES, tz + dz * PIECE_TILES));
      }
    }

    this.groundMoved = true;
    this.forgetShoreMap();

    // And the liquid on it has to be gathered again. The bodies of water are
    // worked out once for a band around the reader and then only when the
    // reader walks out of that band, so a square arriving inside it brought
    // ground with no water on it: teleport into the crater at Broadspire and
    // whether there was lava in it depended on whether the ground had arrived
    // before the gathering ran or after it.
    this.wateredAt = null;
  }

  /** A square nobody is near any more. Its mesh and its scenery go with it. */
  groundGone(region: LocalRegion, tx: number, tz: number): void {
    this.region = region;

    const key = pieceOf(tx, tz);
    const mesh = this.terrain.get(key);
    if (mesh) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.terrain.delete(key);
    }

    // And whatever was lying on it, which goes with the ground it was lying on.
    const dressed = this.clutter.get(key);
    if (dressed) {
      this.scene.remove(dressed);
      forgetClutter(dressed);
      this.clutter.delete(key);
    }

    this.terrainDirty.delete(key);
    this.groundMoved = true;
    this.forgetShoreMap();
    this.wateredAt = null;

    // The ones beside it are drawn again, because their edges were blended
    // against ground that is no longer there.
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dz === 0) continue;
        this.terrainDirty.add(pieceOf(tx + dx * PIECE_TILES, tz + dz * PIECE_TILES));
      }
    }
  }

  /**
   * How much water is drawn, in tiles each way around the reader.
   *
   * The view reaches about fifty tiles, so a hundred and forty covers
   * everything in sight with room to spare. It is not free: every tile of water
   * is cut into pieces so its surface can lean on its bank, so twice the width
   * is four times the work. Three hundred and twenty tiles of it took a second
   * and a half, which is a frame dropped every time it was rebuilt.
   */
  private static readonly WATER_TILES = 140;

  /** The stretch of water worth drawing, given where the reader is standing. */
  private waterWindow(region: LocalRegion): {
    tx: number;
    tz: number;
    width: number;
    height: number;
  } {
    const span = Scene.WATER_TILES;
    return {
      tx: Math.floor(this.focusTile.tx - region.originX - span / 2),
      tz: Math.floor(this.focusTile.tz - region.originZ - span / 2),
      width: span,
      height: span,
    };
  }

  /** Where the water is drawn around, which is wherever the reader is. */
  private focusTile = { tx: 0, tz: 0 };

  /**
   * The map of where the land is, for the waves that run at it.
   *
   * Drawn round the reader and drawn again when they walk out of it. Not every
   * frame: it is a distance transform over sixty five thousand texels, which is
   * a handful of milliseconds and would be a stutter every frame for an answer
   * that changes when somebody walks a hundred tiles.
   */
  private shoreMap: ShoreMap | null = null;
  private shoreStale = true;
  private shoreDrawnAt = 0;

  /** Says the coastline has moved, so the map is drawn again shortly. */
  private forgetShoreMap(): void {
    this.shoreStale = true;
  }

  private keepShoreMap(wet: readonly (THREE.Mesh | null)[]): void {
    const region = this.region;
    if (!region) return;

    const at = this.focusTile;
    // Painting a coast marks it stale on every tile of every stroke, and a
    // distance transform over sixty five thousand texels on every one of those
    // is a stutter for an answer nobody could see change that fast.
    const behind = performance.now() - this.shoreDrawnAt > Scene.SHORE_WAIT;
    const gone = !this.shoreMap || !stillCovers(this.shoreMap.rect, at);

    if (gone || (this.shoreStale && behind)) this.redrawShoreMap(region, at);

    // Handed over every frame rather than only when it is drawn again: a pond
    // built a moment ago has a material of its own and has never been told.
    giveShoreMap(wet, this.shoreMap, SHORE_TEXELS, BED_TEXELS);
  }

  private redrawShoreMap(region: LocalRegion, at: { tx: number; tz: number }): void {
    const drawn = drawShoreMap(
      {
        width: region.width,
        height: region.height,
        originX: region.originX,
        originZ: region.originZ,
        flags: region.flags,
        squares: region.squares,
        heights: region.heights,
        water: region.water,
        sea: !region.underground,
      },
      at,
    );

    this.shoreMap?.texture.dispose();
    this.shoreMap?.bed.dispose();
    this.shoreMap = drawn;
    this.shoreStale = false;
    this.shoreDrawnAt = performance.now();
  }

  /** How often the map of the coast may be drawn again, in milliseconds. */
  private static readonly SHORE_WAIT = 400;

  /** How far the reader may stray before the water is drawn again around them. */
  private static readonly WATER_STRAY = 34;

  /** The middle of the water that is currently drawn. */
  private wateredAt: { tx: number; tz: number } | null = null;

  /** One surface per square of ground, the same squares the terrain is drawn in. */
  private readonly waters = new Map<string, THREE.Mesh>();

  /**
   * The bodies of water in the band being drawn, gathered once for the band.
   *
   * They used to be gathered again inside every square, which gave two seams
   * nobody could explain. A river crossing a square boundary was two bodies, and
   * each took the lowest tile on its own side for its surface, so the water
   * stepped down at the boundary. And each body was grown a tile past its bank
   * before it was drawn, so both squares drew the tiles between them and the
   * overlap showed as a darker line, sixty four tiles apart, all down the river.
   *
   * Gathered for the whole band, a river is one body with one surface and every
   * tile of it belongs to exactly one square.
   */
  private waterPlan: WaterBody[] = [];

  /** The shore of each body, worked out the first time it is drawn and kept. */
  private readonly waterFields = new Map<WaterBody, PlacedWater>();

  /** Squares whose water is out of date, built one a frame. */
  private readonly waterDirty = new Set<string>();

  /**
   * Told where the reader is, every frame.
   *
   * The water is drawn in a band round them rather than across the whole world,
   * so it has to know where they are, and it has to be drawn again when they
   * have walked far enough for the edge of that band to matter. Once every
   * ninety tiles rather than once every square, and the picture never runs out
   * before it is replaced.
   */
  lookAt(tx: number, tz: number): void {
    this.focusTile = { tx, tz };

    const region = this.region;
    if (!region) return;

    const strayed =
      !this.wateredAt ||
      Math.abs(tx - this.wateredAt.tx) > Scene.WATER_STRAY ||
      Math.abs(tz - this.wateredAt.tz) > Scene.WATER_STRAY;

    if (!strayed) return;
    this.wateredAt = { tx, tz };

    // The band moved, so the water in it is gathered again and every square in
    // it is drawn again from the new gathering.
    this.waterPlan = waterBodies(region, this.waterWindow(region));
    this.waterFields.clear();
    for (const key of this.waters.keys()) this.waterDirty.add(key);

    // Which squares are near enough to have water drawn on them. Asked for
    // rather than built: they are built one a frame, so walking into a new
    // stretch of coast costs one square of surface per frame rather than the
    // whole coast at once.
    const near = Scene.WATER_TILES / 2;
    const wanted = new Set<string>();

    for (let dz = -near; dz <= near; dz += PIECE_TILES) {
      for (let dx = -near; dx <= near; dx += PIECE_TILES) {
        const key = pieceOf(tx - region.originX + dx, tz - region.originZ + dz);
        wanted.add(key);
        if (!this.waters.has(key)) this.waterDirty.add(key);
      }
    }

    // And which are far enough to let go of.
    for (const [key, mesh] of this.waters) {
      if (wanted.has(key)) continue;

      this.scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.waters.delete(key);
    }
  }

  /** Builds the water on one square of ground, replacing whatever was there. */
  private buildWaterPiece(region: LocalRegion, key: string): void {
    const already = this.waters.get(key);
    if (already) {
      this.scene.remove(already);
      already.geometry.dispose();
      (already.material as THREE.Material).dispose();
      this.waters.delete(key);
    }

    const [cx = 0, cz = 0] = key.split('.').map(Number);
    const mesh = this.waterMesh(region, {
      tx: cx * PIECE_TILES,
      tz: cz * PIECE_TILES,
      width: PIECE_TILES,
      height: PIECE_TILES,
    });

    if (!mesh) return;
    this.waters.set(key, mesh);
    this.scene.add(mesh);
  }

  /**
   * Whether boards have been laid over the ground on a tile.
   *
   * A dock, a floor, a jetty. Nothing lies on top of those: the ground beneath
   * is still whatever it was painted, so without asking, grass grew up through
   * the planks of a pier and pebbles were scattered over somebody's floor.
   */
  private laidOver(region: LocalRegion, tx: number, tz: number): boolean {
    for (const object of region.objects) {
      if (object.tx !== tx || object.tz !== tz) continue;
      if ((object.plane ?? 0) !== 0) continue;
      if (this.floorIds.has(object.id)) return true;
    }

    return false;
  }

  /**
   * The whole region, drawn coarsely, for everything past the squares.
   *
   * The ground arrives a square at a time and only a square or two either side
   * of the reader ever exists, so past a hundred tiles there is no landscape at
   * all: standing on a shore you saw open water where a continent is, and the
   * squares that had arrived ended in a straight edge against nothing.
   *
   * The region carries a chart of itself, which is what the world map is drawn
   * from: one reading every few tiles, holding how high the ground is and what
   * it is made of. That is more than enough for something seen from half a mile
   * away, so it is built once as a single mesh and left there. The detailed
   * squares stand slightly above it and cover it wherever they exist.
   */
  private far: THREE.Mesh | null = null;

  /** Where the hole in the backdrop is, moved to the reader every frame. */
  private farNear: THREE.Vector3 | null = null;

  private buildFarLandscape(region: LocalRegion): void {
    if (this.far) {
      this.scene.remove(this.far);
      this.far.geometry.dispose();
      (this.far.material as THREE.Material).dispose();
      this.far = null;
      this.farNear = null;
    }

    const chart = region.chart;
    if (!chart || chart.width < 2 || chart.height < 2) return;

    // Coarser again than the chart, because this is scenery on the horizon and
    // a vertex every few tiles over a continent is a million vertices.
    const every = FAR_STEP;
    const across = Math.floor((chart.width - 1) / every) + 1;
    const down = Math.floor((chart.height - 1) / every) + 1;

    const positions = new Float32Array(across * down * 3);
    const colours = new Float32Array(across * down * 3);
    /** Which readings are under water, so a square of open sea is left out. */
    const drowned = new Uint8Array(across * down);
    const held = new THREE.Color();

    for (let row = 0; row < down; row++) {
      for (let column = 0; column < across; column++) {
        const bx = Math.min(chart.width - 1, column * every);
        const bz = Math.min(chart.height - 1, row * every);
        const at = bz * chart.width + bx;

        const kind = chart.kinds[at] ?? 0;
        const wet = (kind & CHART_WET) !== 0;
        const high =
          ((chart.heights[at] ?? 0) / (CHART_LEVELS - 1)) * (CHART_CEILING - CHART_FLOOR) +
          CHART_FLOOR;

        const index = row * across + column;
        drowned[index] = wet ? 1 : 0;
        positions[index * 3] = region.originX + bx * chart.step;
        // Dropped, so the real ground always wins where it exists rather than
        // fighting it for the same pixels.
        positions[index * 3 + 1] = (wet ? Math.min(high, -0.4) : high) - FAR_BELOW;
        positions[index * 3 + 2] = region.originZ + bz * chart.step;

        held.copy(farColour(wet, high, kind & 0x0f));
        colours[index * 3] = held.r;
        colours[index * 3 + 1] = held.g;
        colours[index * 3 + 2] = held.b;
      }
    }

    /*
     * Only the land.
     *
     * The sea is one plane over the whole world and draws itself; a second
     * surface of water underneath it shows through wherever the two disagree,
     * which over a coarse reading of a continent is everywhere. So a square of
     * this whose corners are all under water is simply not drawn, and what is
     * left is a coastline with sea on both sides of it.
     */
    const indices: number[] = [];
    for (let row = 0; row + 1 < down; row++) {
      for (let column = 0; column + 1 < across; column++) {
        const a = row * across + column;
        const b = a + 1;
        const c = a + across;
        const d = c + 1;

        if (drowned[a] && drowned[b] && drowned[c] && drowned[d]) continue;
        indices.push(a, c, b, b, c, d);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    const material = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: false });

    /*
     * Cut away around the reader.
     *
     * Done in the shader rather than by leaving triangles out, because where
     * the hole goes changes every time somebody walks and the mesh is built
     * once for a whole continent.
     */
    const near = new THREE.Vector3();
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uFarNear = { value: near };

      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vFarAt;')
        .replace(
          '#include <fog_vertex>',
          '#include <fog_vertex>\nvFarAt = (modelMatrix * vec4(position, 1.0)).xyz;',
        );

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          '#include <common>\nvarying vec3 vFarAt;\nuniform vec3 uFarNear;',
        )
        .replace(
          '#include <dithering_fragment>',
          `if (length(vFarAt.xz - uFarNear.xz) < ${FAR_HOLE.toFixed(1)}) discard;
#include <dithering_fragment>`,
        );
    };

    const mesh = new THREE.Mesh(geometry, material);

    // Never in the way of anything: it is a backdrop, and the pointer, the
    // outlines and the shadows all have nothing to do with it.
    mesh.raycast = () => undefined;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = -1;

    this.far = mesh;
    this.farNear = near;
    this.scene.add(mesh);
  }

  /** Builds one square of ground, replacing whatever was there. */
  private buildPiece(region: LocalRegion, piece: Piece): void {
    const already = this.terrain.get(piece.key);
    if (already) {
      this.scene.remove(already);
      already.geometry.dispose();
      (already.material as THREE.Material).dispose();
    }

    const mesh = this.buildTerrainMesh(region, piece);
    this.terrain.set(piece.key, mesh);
    this.scene.add(mesh);

    this.dressPiece(region, piece);
  }

  /**
   * What lies on a square of ground: grass, pebbles, sticks.
   *
   * Built with the square and thrown away with it, as instanced meshes, so the
   * cost is a handful of draw calls for a square and nothing at all per frame.
   * Only the squares near the reader are dressed: clutter this small is not
   * visible at any distance, and building it for a whole view would be building
   * it for ground nobody can see it on.
   */
  private dressPiece(region: LocalRegion, piece: Piece): void {
    const had = this.clutter.get(piece.key);
    if (had) {
      this.scene.remove(had);
      forgetClutter(had);
      this.clutter.delete(piece.key);
    }

    if (!this.showClutter) return;

    const group = clutterFor(
      region,
      piece,
      (x, z) => this.surfaceAt(x, z),
      (index) => ((region.flags[index] ?? 0) & WATER_FLAG) !== 0,
      (tx, tz) => this.laidOver(region, tx, tz),
    );

    if (group.children.length === 0) return;

    this.clutter.set(piece.key, group);
    this.scene.add(group);
  }

  /** The clutter on each square, kept so it can be thrown away with the square. */
  private readonly clutter = new Map<string, THREE.Group>();

  /** Whether the ground is dressed at all, which is a setting. */
  private showClutter = true;

  /**
   * Turns the clutter on or off, and rebuilds whatever is on screen.
   *
   * Rebuilt rather than hidden: it costs memory as well as draw calls, and
   * somebody turning it off is usually asking for the memory back.
   */
  setClutter(showing: boolean): void {
    if (this.showClutter === showing) return;

    this.showClutter = showing;
    const region = this.region;
    if (!region) return;

    for (const piece of piecesOf(region)) {
      if (!this.terrain.has(piece.key)) continue;
      this.dressPiece(region, piece);
    }
  }

  /**
   * The ground, coloured by the tile rather than by the corner.
   *
   * Every tile is drawn as four quads rather than one. The middle of a tile
   * takes its own colour and nothing else, so a patch of path is a patch of
   * path; the vertices along its edges are shared with the neighbour and take
   * half of each, so where two kinds of ground meet they meet in a soft band
   * rather than a hard line; and the corners take a quarter each way, which is
   * what makes a single painted tile read as a stepping stone with the grass
   * closing in on it.
   *
   * Colouring the corner vertices by whichever tile happened to be south east
   * of them, which is what this did before, put the paint half a tile away from
   * the tile that was painted.
   *
   * This builds one square of the region at a time. Two squares meeting share a
   * row of vertices, and each works that row out from the same tiles, so the
   * positions and the colours agree exactly. The normals are worked out from
   * the whole ground rather than from the triangles in the mesh being built,
   * which is the part that would otherwise show: a vertex on the join would be
   * lit by half its neighbours and the join would appear as a crease.
   */
  private buildTerrainMesh(region: LocalRegion, piece: Piece): THREE.Mesh {
    const heightStride = region.width + 1;
    const stride = piece.width * GROUND_STEPS + 1;
    const down = piece.height * GROUND_STEPS + 1;
    const vertexCount = stride * down;

    // Where this square starts, counted in vertices rather than tiles.
    const fromX = piece.tx * GROUND_STEPS;
    const fromZ = piece.tz * GROUND_STEPS;

    const positions = new Float32Array(vertexCount * 3);
    const colors = new Float32Array(vertexCount * 3);
    /** How much grain each vertex takes, blended the way its colour is. */
    const grains = new Float32Array(vertexCount);
    /** How far under water each vertex is, from nought to one, for how it is lit. */
    const wets = new Float32Array(vertexCount);
    /** Which ground each vertex came out as, for laying the triangles along the border. */
    const painting = new Int16Array(vertexCount);

    // What each tile is made of if it were dry, worked out once from the tile
    // itself, and beside it the height of the water over it where there is any.
    //
    // Kept apart because they answer different questions. What a tile is made of
    // is a fact about the tile and blends with its neighbours; whether a
    // particular spot is under water is a fact about that spot's height against
    // the surface above it, and a tile that the water only half covers is half
    // covered rather than covered.
    //
    // A tile beyond the square is worked out too, because the blending reaches
    // one tile out and stopping at the join would put a pale line along it.
    const tileWater = new Map<number, number | null>();
    const tileGrain = new Map<number, number>();
    /** Which of the wet tiles have lava on them rather than water. */
    const tileMolten = new Map<number, number>();

    for (let tz = piece.tz - 1; tz <= piece.tz + piece.height; tz++) {
      for (let tx = piece.tx - 1; tx <= piece.tx + piece.width; tx++) {
        if (tx < 0 || tz < 0 || tx >= region.width || tz >= region.height) continue;

        const tile = tz * region.width + tx;
        const wet = ((region.flags[tile] ?? 0) & WATER_FLAG) !== 0;

        tileWater.set(tile, wet ? (region.water?.[tile] ?? SEA_LEVEL) : null);
        tileGrain.set(tile, wet ? 0 : grainOf(region.ground?.[tile] ?? 0));
        tileMolten.set(tile, (region.ground?.[tile] ?? 0) === GroundKind.Lava ? 1 : 0);
      }
    }

    /** The height of the ground at a vertex, wherever in the region it is. */
    const groundAt = (sx: number, sz: number): number => {
      const x = sx / GROUND_STEPS;
      const z = sz / GROUND_STEPS;

      const x0 = Math.min(Math.max(Math.floor(x), 0), region.width - 1);
      const z0 = Math.min(Math.max(Math.floor(z), 0), region.height - 1);
      const fx = x - x0;
      const fz = z - z0;

      const h00 = region.heights[z0 * heightStride + x0] ?? 0;
      const h10 = region.heights[z0 * heightStride + x0 + 1] ?? 0;
      const h01 = region.heights[(z0 + 1) * heightStride + x0] ?? 0;
      const h11 = region.heights[(z0 + 1) * heightStride + x0 + 1] ?? 0;

      const top = h00 + (h10 - h00) * fx;
      const bottom = h01 + (h11 - h01) * fx;
      return top + (bottom - top) * fz;
    };

    const blended = new THREE.Color();

    for (let cz = 0; cz < down; cz++) {
      for (let cx = 0; cx < stride; cx++) {
        const index = cz * stride + cx;

        // Where this vertex sits in tiles, which is what everything below is
        // measured in.
        const x = (fromX + cx) / GROUND_STEPS;
        const z = (fromZ + cz) / GROUND_STEPS;

        const x0 = Math.min(Math.floor(x), region.width - 1);
        const z0 = Math.min(Math.floor(z), region.height - 1);

        positions[index * 3] = region.originX + x;
        positions[index * 3 + 1] = groundAt(fromX + cx, fromZ + cz);
        positions[index * 3 + 2] = region.originZ + z;

        /*
         * Which ground this spot is painted, along a line rather than a grid.
         *
         * A tile is painted one thing and its neighbour another, so a border
         * between them runs along the tile grid and turns a right angle at
         * every step. Read straight, ground painted across the grid comes out
         * as a staircase.
         *
         * Cutting each corner off separately does not fix it: every step gets
         * its own little triangle and a run of them is a row of teeth, which is
         * a smaller staircase with more corners in it. Mixing the four nearest
         * tiles does better and still leaves a sharp point wherever the border
         * changes direction, because mixing four of anything gives a line made
         * of straight pieces.
         *
         * So the border is drawn as a contour instead. Every kind is one where
         * its tiles are and nought where they are not; that is read across the
         * tiles round this spot with a soft round falloff, and this spot is
         * painted whichever kind comes out highest. Where two kinds are equal
         * is the border, and a soft round falloff puts that line on the grid
         * where the grid is straight, along one clean diagonal where the tiles
         * step, and round a curve where it turns a corner.
         *
         * Highest wins outright, so it is a line and not a wash: the mesh
         * carries three vertices to a tile, so the change takes a third of a
         * tile and comes from the triangles rather than from an average.
         */
        const kindAt = (tx: number, tz: number): number => {
          if (tx < 0 || tz < 0 || tx >= region.width || tz >= region.height) return -1;
          return region.ground?.[tz * region.width + tx] ?? 0;
        };

        let painted = kindAt(x0, z0);

        // Nearly all of a landscape is the middle of somewhere. Where the tiles
        // all round agree there is no border to draw and no reason to weigh
        // twenty five of them to find that out.
        let alone = true;
        for (let dz = -1; dz <= 1 && alone; dz++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (kindAt(x0 + dx, z0 + dz) !== painted) {
              alone = false;
              break;
            }
          }
        }

        if (!alone) {
          let strongest = -1;

          for (let dz = -2; dz <= 2; dz++) {
            for (let dx = -2; dx <= 2; dx++) {
              const kind = kindAt(x0 + dx, z0 + dz);
              if (kind < 0) continue;

              // Everything of this kind within reach, so two tiles lying
              // diagonally speak with one voice and the border runs between
              // them rather than around each of them.
              let held = 0;
              for (let oz = -2; oz <= 2; oz++) {
                for (let ox = -2; ox <= 2; ox++) {
                  if (kindAt(x0 + ox, z0 + oz) !== kind) continue;

                  const away =
                    Math.hypot(x - (x0 + ox + 0.5), z - (z0 + oz + 0.5)) / GROUND_ROUNDING;
                  if (away >= 1) continue;

                  const near = 1 - away;
                  held += near * near * (3 - 2 * near);
                }
              }

              if (held > strongest) {
                strongest = held;
                painted = kind;
              }
            }
          }
        }

        painting[index] = painted;

        // The colour of that ground here, worked out from this spot's own height
        // rather than from the middle of a tile, so unpainted country still
        // shades smoothly from beach to grass as it rises.
        blended.copy(groundColour(positions[index * 3 + 1] ?? 0, Math.max(0, painted)));

        // And whatever liquid is over it, weighed across the tiles near enough
        // to have a say: a shoreline crossing a tile crosses it rather than
        // stopping at its edge.
        let surface = 0;
        let wetness = 0;
        let molten = 0;
        let grain = 0;
        let total = 0;

        for (let tz = z0 - 1; tz <= z0 + 1; tz++) {
          for (let tx = x0 - 1; tx <= x0 + 1; tx++) {
            if (tx < 0 || tz < 0 || tx >= region.width || tz >= region.height) continue;

            const away = Math.hypot(x - (tx + 0.5), z - (tz + 0.5)) * GROUND_BLEND;
            if (away >= 1) continue;

            const held = 1 - away;
            const weight = held * held * (3 - 2 * held);

            grain += (tileGrain.get(tz * region.width + tx) ?? 0) * weight;
            total += weight;

            const over = tileWater.get(tz * region.width + tx);
            if (over !== null && over !== undefined) {
              surface += over * weight;
              wetness += weight;
              molten += (tileMolten.get(tz * region.width + tx) ?? 0) * weight;
            }
          }
        }

        // Under water where this spot is actually under it. A tile the water
        // half covers used to be painted as sea floor from edge to edge, which
        // put a straight line across a shore that is not straight.
        if (wetness > 0) {
          const level = surface / wetness;
          const depth = level - (positions[index * 3 + 1] ?? 0);
          if (depth > 0) {
            /*
             * What is under the liquid, seen through it.
             *
             * The ground it actually is, darkened by how much water is standing
             * on it, and under lava the stone it is sitting in: the bed of the
             * crater at Broadspire was painted the colour of a shallow sea,
             * which from inside it read as standing in a lagoon on top of a
             * volcano.
             *
             * It used to be painted the colour of water, from shallow to deep
             * over a couple of tiles of depth. Two things were wrong with that.
             * The water over it is already drawn from its own depth, so the
             * depth was being told twice; and a lake bed is not smooth, it is
             * flat shelves with steps between them, so every shelf came out as
             * a patch of its own colour and a lake read as a mosaic of rounded
             * squares. Now the bed is bed, and going deeper only puts more
             * water in the way.
             */
            const hot = molten / wetness;
            const under = new THREE.Color()
              .copy(blended)
              .lerp(COLORS.deep, Math.min(depth / BED_FADES, 1) * BED_DARKEST)
              .lerp(COLORS.moltenBed, hot);

            const sunk = Math.min(1, depth / 0.35);
            blended.lerp(under, sunk);
            wets[index] = sunk;
          }
        }

        colors[index * 3] = blended.r;
        colors[index * 3 + 1] = blended.g;
        colors[index * 3 + 2] = blended.b;

        // Grain follows the paint, and stops at the waterline: a seabed does not
        // want dust on it, and the water over it is drawn separately anyway.
        grains[index] = total > 0 ? (grain / total) * (wetness > 0 ? 0 : 1) : 0;
      }
    }

    /*
     * The triangles, laid along whatever border runs through them.
     *
     * Every square of the mesh is drawn as two triangles, and until now the cut
     * between them always ran the same way. A border running the same way as
     * that cut comes out as a clean straight line; a border running the other
     * way has to zigzag along the edges of the triangles, because a colour
     * cannot change inside one. Which is why a wedge of grass came out with one
     * clean side and one made of teeth, and why it was one side and not both.
     *
     * So the cut is chosen. Where two opposite corners are painted the same
     * thing and the other two are not, the square is split between those two,
     * and the border falls along the cut instead of across it.
     */
    const indices: number[] = [];
    for (let cz = 0; cz < down - 1; cz++) {
      for (let cx = 0; cx < stride - 1; cx++) {
        const a = cz * stride + cx;
        const b = a + 1;
        const c = a + stride;
        const d = c + 1;

        const alongOther = painting[a] === painting[d] && painting[b] !== painting[c];

        if (alongOther) indices.push(a, c, d, a, d, b);
        else indices.push(a, c, b, b, c, d);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aGrain', new THREE.BufferAttribute(grains, 1));
    geometry.setAttribute('aWet', new THREE.BufferAttribute(wets, 1));
    geometry.setAttribute(
      'normal',
      new THREE.BufferAttribute(normalsFor(region, piece, groundAt), 3),
    );
    geometry.setIndex(indices);

    return new THREE.Mesh(geometry, groundMaterial());
  }

  /**
   * The sea.
   *
   * One plane, well past the fog on every side, so the island has open water
   * around it however far the draw distance is turned up. It is drawn over the
   * ground rather than instead of it, which is what gives the shallows their
   * colour: the sand and the silt show through, and the deeper the ground lies
   * the less of it comes back.
   */
  private buildSea(region: LocalRegion): void {
    // Nothing reaches a cave. The sea plane covers every region, so underground it
    // has to be taken away rather than simply not built.
    if (region.underground) {
      this.lightUnderground(true);

      if (this.sea) {
        this.scene.remove(this.sea);
        this.sea.geometry.dispose();
        (this.sea.material as THREE.Material).dispose();
        this.sea = null;
      }
      this.seaKey = '';
      return;
    }

    this.lightUnderground(false);

    // The surface only depends on where the region is, so editing the ground under
    // it leaves it alone.
    const key = `${region.originX},${region.originZ},${region.width},${region.height}`;
    if (this.sea && this.seaKey === key) return;
    this.seaKey = key;

    if (this.sea) {
      this.scene.remove(this.sea);
      this.sea.geometry.dispose();
      (this.sea.material as THREE.Material).dispose();
      this.sea = null;
    }

    const width = region.width + SEA_MARGIN * 2;
    const depth = region.height + SEA_MARGIN * 2;

    const geometry = new THREE.PlaneGeometry(width, depth, SEA_SEGMENTS, SEA_SEGMENTS);
    geometry.rotateX(-Math.PI / 2);

    // The sea is drawn by the same shader as everything else that is wet, so it
    // is given what that shader reads: one colour all over, no bank anywhere near,
    // and a slow drift across it. The swell on top of that is its own.
    const points = geometry.getAttribute('position').count;
    const colours = new Float32Array(points * 4);
    const shores = new Float32Array(points).fill(SEA_OPENNESS);
    const flows = new Float32Array(points * 2);
    // Flat. Its pieces are tens of tiles wide, which is wider than the swell.
    const swells = new Float32Array(points);
    // The sea is as open as water gets, so it takes the whole of the surf.
    const opens = new Float32Array(points).fill(1);

    for (let index = 0; index < points; index++) {
      colours[index * 4] = COLORS.surface.r;
      colours[index * 4 + 1] = COLORS.surface.g;
      colours[index * 4 + 2] = COLORS.surface.b;
      colours[index * 4 + 3] = 0.62;
      flows[index * 2] = SEA_DRIFT;
      flows[index * 2 + 1] = SEA_DRIFT * 0.4;
    }

    geometry.setAttribute('color', new THREE.BufferAttribute(colours, 4));
    geometry.setAttribute('aShore', new THREE.BufferAttribute(shores, 1));
    geometry.setAttribute('aFlow', new THREE.BufferAttribute(flows, 2));
    geometry.setAttribute('aSwell', new THREE.BufferAttribute(swells, 1));
    geometry.setAttribute('aOpen', new THREE.BufferAttribute(opens, 1));

    const mesh = new THREE.Mesh(geometry, waterMaterial());

    mesh.position.set(
      region.originX + region.width / 2,
      SEA_LEVEL,
      region.originZ + region.height / 2,
    );
    mesh.renderOrder = 1;

    this.sea = mesh;
    this.scene.add(mesh);
  }

  /**
   * Every piece of water that is not the sea.
   *
   * A pond in a hollow, a lake on a plateau, a river running down a valley. The
   * hard part is not the water, it is the edge: a sheet of flat tiles stopping
   * dead at the last tile somebody painted reads as tiles, not as water.
   *
   * So the tiles are gathered into bodies first, each body given one surface
   * height, and then the surface is grown outwards over the bank until the ground
   * comes up through it. Every tile is cut into a few smaller pieces and each is
   * kept only where the bed beneath it is lower than the surface, which is what
   * puts a curve on the shoreline instead of a staircase.
   *
   * Tiles whose water sits at sea level are left to the sea plane, which already
   * covers the whole region and reaches past the fog.
   */
  private waterMesh(
    region: LocalRegion,
    from: { tx: number; tz: number; width: number; height: number },
  ): THREE.Mesh | null {
    const stride = region.width + 1;
    const cornerAt = (cx: number, cz: number): number => region.heights[cz * stride + cx] ?? 0;

    /** The bed under a point inside a tile, taken across the tile's four corners. */
    const bedAt = (tx: number, tz: number, alongX: number, alongZ: number): number => {
      const north = cornerAt(tx, tz) * (1 - alongX) + cornerAt(tx + 1, tz) * alongX;
      const south = cornerAt(tx, tz + 1) * (1 - alongX) + cornerAt(tx + 1, tz + 1) * alongX;
      return north * (1 - alongZ) + south * alongZ;
    };

    /**
     * The bed under a point, read gently, for shading only.
     *
     * The same reading spread over a tile either way. Nothing is moved by it:
     * the surface still sits where the exact bed puts it, and the shallows are
     * still shallow. It is the steps between flat shelves that go.
     */
    const shadedBedAt = (tx: number, tz: number, alongX: number, alongZ: number): number => {
      let total = 0;
      let counted = 0;

      for (let awayZ = -DEPTH_SMOOTH; awayZ <= DEPTH_SMOOTH; awayZ++) {
        for (let awayX = -DEPTH_SMOOTH; awayX <= DEPTH_SMOOTH; awayX++) {
          const nx = Math.min(region.width - 1, Math.max(0, tx + awayX));
          const nz = Math.min(region.height - 1, Math.max(0, tz + awayZ));
          // The middle counts for as much as the ring round it, so the reading
          // still follows the ground it is over rather than drifting off it.
          const weight = awayX === 0 && awayZ === 0 ? 4 : 1;
          total += bedAt(nx, nz, alongX, alongZ) * weight;
          counted += weight;
        }
      }

      return total / counted;
    };

    const lowestOf = (tx: number, tz: number): number =>
      Math.min(
        cornerAt(tx, tz),
        cornerAt(tx + 1, tz),
        cornerAt(tx, tz + 1),
        cornerAt(tx + 1, tz + 1),
      );

    const bodies = this.waterPlan;

    // Only this square's own tiles are drawn, whatever the body reaches. The
    // bodies are gathered for the whole band, so every tile of every one of them
    // is drawn exactly once, by whichever square it falls in.
    const mine = (index: number): boolean => {
      const tx = index % region.width;
      const tz = Math.floor(index / region.width);
      return (
        tx >= from.tx && tz >= from.tz && tx < from.tx + from.width && tz < from.tz + from.height
      );
    };

    const positions: number[] = [];
    const colours: number[] = [];
    const shores: number[] = [];
    const flows: number[] = [];
    const swells: number[] = [];

    /**
     * How much of an open sea each corner belongs to, from nought to one.
     *
     * A pond is not a small ocean. Surf is what a body of water big enough to
     * carry a swell does when that swell runs out of depth; a pond a dozen
     * paces across has no swell to break, and painting one with the same white
     * band read as a bright ring round a puddle. So a body says how open it is,
     * and what it gets is decided by that: an ocean breaks, a lake laps, a pond
     * only ripples.
     */
    const opens: number[] = [];

    /**
     * Which way the nearest bank lies from each corner.
     *
     * Waves run at the shore. Out in the open they come from wherever the wind
     * is, but a swell reaching shallow water turns to face the beach, and round
     * a headland it bends with the headland: that is refraction, and it is the
     * one thing that makes water read as being at the edge of a landmass rather
     * than as a tiled surface with a coastline drawn on it.
     *
     * The shore field already knows how far away the bank is at every point.
     * Which way it lies is that field's gradient, which is two subtractions.
     */
    const toShore: number[] = [];

    /** One where this corner is lava, nought where it is water. */
    const molten: number[] = [];

    // Shallow water is thin and lets the bed through; deep water is not. Colour
    // alone does not do that, so the depth is carried in the transparency as
    // well, worked out for each corner rather than each piece so the shading runs
    // smoothly out to the bank instead of banding.
    const shallow = new THREE.Color(COLORS.surface).lerp(new THREE.Color(0x9fd3dc), 0.5);
    const deep = new THREE.Color(COLORS.surface).lerp(new THREE.Color(0x06222c), 0.5);
    const tint = new THREE.Color();

    for (const body of bodies) {
      /*
       * How open this body is, which decides what its edges do.
       *
       * A pond of a hundred tiles is a puddle and gets none of the surf; a lake
       * of a few thousand carries enough water to lap; only the sea gets the
       * whole of it. Counted in tiles, because that is what a body is, and the
       * two numbers are a small pond and a lake worth rowing across.
       */
      const openness = Math.min(
        1,
        Math.max(0, (body.tiles.length - SMALL_BODY) / (OPEN_BODY - SMALL_BODY)),
      );

      // Worked out the first time this body is drawn and kept for as long as the
      // band stands still, since every square it crosses wants the same answer.
      let placed = this.waterFields.get(body);

      if (!placed) {
        // Grown one tile out, so the surface runs into the bank rather than
        // stopping at the last tile that happens to be marked.
        const covered = new Set(body.tiles);
        for (const index of body.tiles) {
          const tx = index % region.width;
          const tz = Math.floor(index / region.width);

          for (let awayZ = -1; awayZ <= 1; awayZ++) {
            for (let awayX = -1; awayX <= 1; awayX++) {
              const nx = tx + awayX;
              const nz = tz + awayZ;
              if (nx < 0 || nz < 0 || nx >= region.width || nz >= region.height) continue;
              // Against the surface over the tile the bank is beside, so the
              // skirt of a sloping river follows the river down.
              const beside = body.levels.get(tz * region.width + tx) ?? body.level;
              if (lowestOf(nx, nz) >= beside + WATER_SKIRT) continue;
              covered.add(nz * region.width + nx);
            }
          }
        }

        placed = {
          covered,
          // What can be guessed from the shape of the body, which is what every
          // piece of water had before the current was written down. Still used
          // for anything the world says nothing about: a pond drawn by hand, an
          // old region, the sea.
          flow: body.tiles.some(
            (at) => region.flow[at * 2] !== 0 || region.flow[at * 2 + 1] !== 0,
          )
            ? { x: 0, z: 0 }
            : flowOf(body.tiles, region.width),
          // How far every point of the surface is from the nearest bank. The
          // ripples are drawn from it, and so is the lift: water leans on its
          // bank rather than rising off it.
          shoreAt: shoreField(covered, body.level, region.width, bedAt),
        };
        this.waterFields.set(body, placed);
      }

      const { covered, flow, shoreAt } = placed;
      const level = body.level;

      /**
       * Which way the water runs at one corner of the surface.
       *
       * Taken from the tiles that meet there, the same way their height is, so
       * a river that bends carries its current round the bend instead of
       * running one way along its whole length.
       *
       * Where none of the tiles meeting at a corner has a current, the body's
       * own answer is used: nothing at all for a river, whose fringes are slack
       * water at the bank, and the guess from its shape for a body the world
       * says nothing about.
       */
      const flowAt = (cornerX: number, cornerZ: number): { x: number; z: number } => {
        let alongX = 0;
        let alongZ = 0;
        let counted = 0;

        for (const [awayX, awayZ] of [
          [0, 0],
          [-1, 0],
          [0, -1],
          [-1, -1],
        ] as const) {
          const tx = cornerX + awayX;
          const tz = cornerZ + awayZ;
          if (tx < 0 || tz < 0 || tx >= region.width || tz >= region.height) continue;

          const at = tz * region.width + tx;
          const runsX = (region.flow[at * 2] ?? 0) / 100;
          const runsZ = (region.flow[at * 2 + 1] ?? 0) / 100;
          if (runsX === 0 && runsZ === 0) continue;

          alongX += runsX;
          alongZ += runsZ;
          counted++;
        }

        if (counted === 0) return flow;
        return { x: alongX / counted, z: alongZ / counted };
      };

      /**
       * How high the water stands at one corner of the surface.
       *
       * A corner is shared by up to four tiles, so the surface there is the
       * average of whichever of them are this water. That is what lets a river
       * slope: the height comes from the tiles rather than from the body, and
       * two tiles a step apart in height give a surface that tilts between them
       * instead of a seam.
       */
      const surfaceAt = (cornerX: number, cornerZ: number): number => {
        let total = 0;
        let counted = 0;

        for (const [awayX, awayZ] of [
          [0, 0],
          [-1, 0],
          [0, -1],
          [-1, -1],
        ] as const) {
          const tx = cornerX + awayX;
          const tz = cornerZ + awayZ;
          if (tx < 0 || tz < 0 || tx >= region.width || tz >= region.height) continue;

          const held = body.levels.get(tz * region.width + tx);
          if (held === undefined) continue;

          total += held;
          counted++;
        }

        return counted > 0 ? total / counted : level;
      };

      /** One corner of one piece: where it sits, how it is shaded, and its water. */
      const corner = (tx: number, tz: number, alongX: number, alongZ: number): void => {
        const here = surfaceAt(tx + alongX, tz + alongZ);
        const atX = tx + alongX;
        const atZ = tz + alongZ;

        positions.push(region.originX + atX, here, region.originZ + atZ);
        shores.push(shoreAt(atX, atZ));

        const runs = flowAt(atX, atZ);
        flows.push(runs.x, runs.z);
        swells.push(1);
        opens.push(openness);

        /*
         * Downhill in the distance to the bank, which is towards it. Sampled a
         * whole tile out so a step in the field does not turn the swell round.
         *
         * Only where there is a bank worth running at. Out in the middle of a
         * body of water every bank is equally far away, the field has a ridge
         * along the middle of it, and the direction to the nearest one swings
         * through every angle there is as that ridge is crossed. A wave whose
         * direction rotates like that draws concentric rings, which is what the
         * open sea was ruled with: a bullseye a couple of hundred tiles across,
         * sitting exactly halfway between two coasts.
         */
        if (shoreAt(atX, atZ) <= SWELL_REACH) {
          const awayX = shoreAt(atX + 1, atZ) - shoreAt(atX - 1, atZ);
          const awayZ = shoreAt(atX, atZ + 1) - shoreAt(atX, atZ - 1);
          const reach = Math.hypot(awayX, awayZ);

          if (reach > 0.0001) toShore.push(-awayX / reach, -awayZ / reach);
          else toShore.push(0, 0);
        } else {
          toShore.push(0, 0);
        }

        molten.push(body.lava ? 1 : 0);

        const deepness = Math.min(
          1,
          Math.max(0, (here - shadedBedAt(tx, tz, alongX, alongZ)) / WATER_DEEPENS),
        );
        // Eased, so the shallows hold their colour for a moment before falling
        // away, which is what stops a pond looking like a pane of glass.
        const shade = deepness * deepness;
        tint.copy(shallow).lerp(deep, shade);
        colours.push(tint.r, tint.g, tint.b, WATER_CLEAREST + shade * (1 - WATER_CLEAREST));
      };

      for (const index of covered) {
        if (!mine(index)) continue;

        const tx = index % region.width;
        const tz = Math.floor(index / region.width);

        // How finely this tile is cut up.
        //
        // The subdivision exists so the surface can lean on its bank and so the
        // shoreline is not a staircase. Out in the middle of a lake there is no
        // bank to lean on and one piece does the same job as thirty six. On a
        // continent nearly all the water is middle, and cutting all of it up was
        // most of a second every time the water was drawn.
        const near = Math.min(
          shoreAt(tx, tz),
          shoreAt(tx + 1, tz),
          shoreAt(tx, tz + 1),
          shoreAt(tx + 1, tz + 1),
        );
        const pieces = near > 2.5 ? 1 : near > 1.2 ? 2 : WATER_PIECES;

        for (let pieceZ = 0; pieceZ < pieces; pieceZ++) {
          for (let pieceX = 0; pieceX < pieces; pieceX++) {
            const fromX = pieceX / pieces;
            const toX = (pieceX + 1) / pieces;
            const fromZ = pieceZ / pieces;
            const toZ = (pieceZ + 1) / pieces;

            const beds = [
              bedAt(tx, tz, fromX, fromZ),
              bedAt(tx, tz, toX, fromZ),
              bedAt(tx, tz, fromX, toZ),
              bedAt(tx, tz, toX, toZ),
            ];
            // Kept while any corner of the piece still has ground below the
            // surface, or a little above it: the last stretch is under the bank
            // and never seen, which is what leaves the shoreline smooth. Judged
            // against the surface over this tile rather than the lowest in the
            // body, or the upper reaches of a river would be thrown away for
            // standing above the sea it runs into.
            const here = body.levels.get(tz * region.width + tx) ?? level;
            if (Math.min(...beds) >= here + WATER_SKIRT) continue;

            corner(tx, tz, fromX, fromZ);
            corner(tx, tz, fromX, toZ);
            corner(tx, tz, toX, fromZ);
            corner(tx, tz, toX, fromZ);
            corner(tx, tz, fromX, toZ);
            corner(tx, tz, toX, toZ);
          }
        }
      }
    }

    if (positions.length === 0) return null;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colours, 4));
    geometry.setAttribute('aShore', new THREE.Float32BufferAttribute(shores, 1));
    geometry.setAttribute('aFlow', new THREE.Float32BufferAttribute(flows, 2));
    geometry.setAttribute('aSwell', new THREE.Float32BufferAttribute(swells, 1));
    geometry.setAttribute('aToShore', new THREE.Float32BufferAttribute(toShore, 2));
    geometry.setAttribute('aMolten', new THREE.Float32BufferAttribute(molten, 1));
    geometry.setAttribute('aOpen', new THREE.Float32BufferAttribute(opens, 1));
    geometry.computeVertexNormals();

    const mesh = new THREE.Mesh(geometry, waterMaterial());

    mesh.renderOrder = 1;
    mesh.frustumCulled = false;
    return mesh;
  }

  private readonly sun: THREE.DirectionalLight;
  private readonly sky: THREE.HemisphereLight;

  /**
   * The difference between standing outside and standing in a hole.
   *
   * The sun goes, whatever light there is comes from the rock rather than the
   * sky, and the fog closes in until the far wall of a cavern is somewhere the
   * torch does not reach.
   */
  private lightUnderground(below: boolean): void {
    this.sun.intensity = below ? 0.55 : 1.9;
    this.sky.color.setHex(below ? 0x6b5c4a : 0x9dc0e0);
    this.sky.groundColor.setHex(below ? 0x2a2118 : 0x4a4030);
    this.sky.intensity = below ? 1.35 : 1.1;

    const air = below ? 0x1a1510 : COLORS.fog.getHex();
    this.scene.background = new THREE.Color(below ? 0x0d0b09 : COLORS.sky.getHex());
    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.color.setHex(air);
      this.scene.fog.near = below ? 8 : 45;
      this.scene.fog.far = below ? 46 : 130;
    }
  }

  /** Still water, held apart from the sea because it does not move with it. */

  /** When the last frame was drawn, for animations that move with real time. */
  private lastDrawn = performance.now();

  /** And when bodies were last placed, which is what turning eases against. */
  private lastSynced = performance.now();

  /**
   * The sea does not heave.
   *
   * It used to: the whole plane rose and fell by a quarter of a tile on swells
   * seventy tiles long. On an island that read as a sea. On a continent the
   * plane is four thousand tiles across and cut into seventy two, so a swell was
   * shorter than a segment, and what showed was sheets of water tilting and
   * sliding up the beach and back down it in slabs.
   *
   * The swell lives in the shader now, where it is worked out per corner from
   * where that corner is in the world and held back to nothing at the bank, so
   * water leans on a beach instead of climbing it.
   */

  /**
   * Scenery, drawn with instanced meshes.
   *
   * The starter region has over a thousand trees. As individual meshes that is a
   * thousand draw calls a frame; instanced it is one per shape.
   */
  /**
   * The storey being worked on, above which nothing is drawn.
   *
   * Building an upper floor with the roof still on is building blind: what is
   * above has to go, and what is below has to stay, because that is what the
   * new floor is being laid on top of. Nought is not the same as nothing here:
   * nought means the ground floor and nothing above it.
   */
  private upToStorey: number | null = null;

  /** Draws nothing above the storey given, or everything again for nothing. */
  showUpToStorey(storey: number | null): void {
    if (this.upToStorey === storey) return;

    this.upToStorey = storey;
    if (this.region) this.buildObjects(this.region);
  }

  /**
   * Draws everything standing on the ground again, without touching the ground.
   *
   * For when a definition changes rather than the world: a tree given a new
   * shape in the tools is a different tree everywhere it stands, and everybody
   * looking at one should see it change rather than see it next time they walk
   * away and come back.
   */
  redrawScenery(): void {
    if (!this.region) return;

    this.buildObjects(this.region);
  }

  private buildObjects(region: LocalRegion): void {
    for (const mesh of this.objectMeshes) {
      this.scene.remove(mesh.mesh);
      mesh.mesh.geometry.dispose();
    }
    this.objectMeshes.length = 0;
    this.underfootScenery.clear();
    this.underfootTile = '';
    this.lamps.length = 0;

    this.rebuildFloors();

    const byId = new Map<
      string,
      {
        tx: number;
        tz: number;
        plane: number;
        rotation: number;
        scale?: number;
        lift?: number;
        looks?: number;
      }[]
    >();
    // Grouped by what each is and by what it is drawn as, because a wood of
    // pines and oaks is one kind of tree wearing two shapes. Anything without a
    // shape of its own falls in with the rest of its kind, which is nearly
    // everything and stays one batch.
    for (const object of region.objects) {
      // Above the storey being worked on, so it does not stand between the
      // work and the camera.
      if (this.upToStorey !== null && (object.plane ?? 0) > this.upToStorey) continue;

      const key = object.looks ? `${object.id}:${object.looks}` : `${object.id}`;
      let bucket = byId.get(key);
      if (!bucket) {
        bucket = [];
        byId.set(key, bucket);
      }
      bucket.push(object);
    }

    for (const [key, placements] of byId) {
      const id = Number(key.split(':')[0]);
      const wearing = placements[0]?.looks ?? 0;
      // Something made from inside the game borrows the shape of something that
      // already has one, so a new kind of signpost is a signpost rather than a
      // grey box.
      // What this batch is drawn as: the shape this one wears, or the shape its
      // kind borrows, or its own.
      const drawnAs = wearing > 0 ? wearing : (this.borrowedShapes.get(id) ?? id);
      const shapeOf = wearing > 0 ? wearing : builtShapes.has(id) ? id : drawnAs;

      for (const part of shapeParts(shapeOf)) {
        const mesh = new THREE.InstancedMesh(
          part.geometry(),
          underSnow(part.material()),
          placements.length,
        );
        mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);

        /** Which of these stand on snow, so the tops of them can carry some. */
        const snowed = new Float32Array(placements.length);

        const matrix = new THREE.Matrix4();
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3(1, 1, 1);
        const position = new THREE.Vector3();

        placements.forEach((placement, index) => {
          const worldX = region.originX + placement.tx + 0.5;
          const worldZ = region.originZ + placement.tz + 0.5;

          // A little variation so a forest does not look stamped out. Anything
          // built stands exactly where it was put.
          const jitter = part.aligned ? 0 : ((placement.tx * 31 + placement.tz * 17) % 100) / 100;
          const drawn = part.aligned ? part.scale : part.scale * (0.85 + jitter * 0.35);
          const height = drawn * (placement.scale || 1);

          // A storey is a fixed lift over the ground below it, so a building
          // walks up rather than following the hill it stands on twice. Boards
          // laid over water float on it rather than sinking to the bed, which is
          // what makes a jetty a jetty.
          // Anything built to a height of its own keeps it: that is what makes a
          // dock a dock rather than a raft riding the swell.
          const bed = this.heightAt(worldX, worldZ);
          const afloat = this.waterLevelAt(worldX, worldZ);
          const ground = placement.lift ?? (afloat !== null && afloat > bed ? afloat : bed);
          const floor = ground + (placement.plane ?? 0) * STOREY_HEIGHT;
          /*
           * Eight positions rather than four.
           *
           * The first four put a wall along each of the tile's sides, which is
           * what the world was built with. The next four turn it another eighth
           * so it runs corner to corner, which is the only way to fence a line
           * that is not north to south or east to west without building it as a
           * staircase.
           */
          const across45 = isDiagonal(placement.rotation);
          const facing =
            ((placement.rotation & 3) * Math.PI) / 2 + (across45 ? Math.PI / 4 : 0) + jitter;

          // A part sitting off the middle of its tile turns with the thing it
          // belongs to, or a staircase would climb north whichever way it faced.
          const across = (part.across ?? 0) * height;

          // A wall placed corner to corner comes off its edge and stands across
          // the middle instead, which is where the corners it joins are.
          const along = ((part.along ?? 0) - (across45 ? (part.edged ?? 0) : 0)) * height;
          position.set(
            worldX + across * Math.cos(facing) + along * Math.sin(facing),
            floor + part.offset * height,
            worldZ - across * Math.sin(facing) + along * Math.cos(facing),
          );

          quaternion.setFromEuler(new THREE.Euler(0, facing, 0));
          scale.set(height, height, height);

          // And it is longer, because a tile's diagonal is longer than its side.
          // Without this a fence turned across the corner left a gap at each end
          // of itself and read as a plank somebody had dropped.
          if (across45 && (part.edged ?? 0) > 0) scale.x = height * Math.SQRT2;

          // A post under a dock runs from the boards down to the bed, however
          // deep that is. The shape is a unit length, stretched here.
          if (part.spans) {
            const drop = Math.max(0.05, position.y - bed);
            scale.y = drop;
            position.y -= drop / 2;
          }

          // Somewhere a real light stands. Written down rather than lit here:
          // a world has thousands of these and a scene can afford a handful, so
          // which ones are burning is decided every so often by what is nearest
          // to whoever is looking.
          if (part.casts !== undefined && this.lamps.length < MOST_LAMPS_KNOWN) {
            this.lamps.push({
              x: position.x,
              y: position.y,
              z: position.z,
              reach: part.casts * height,
              colour: part.lightColour ?? 0xffd27a,
              atNight: part.lightAt === 'night',
              flickers: part.flickers === true,
              shifts: part.shifts === true,
              // So two torches side by side do not flicker in step.
              beat: ((placement.tx * 37 + placement.tz * 71) % 628) / 100,
            });
          }

          matrix.compose(position, quaternion, scale);
          mesh.setMatrixAt(index, matrix);

          // Snow lies on what is under it. A tree standing in a snowfield in
          // full summer green is the one thing in a winter wood that says
          // nobody thought about it.
          const under = region.ground?.[placement.tz * region.width + placement.tx] ?? 0;
          snowed[index] = under === SNOW_GROUND ? 1 : 0;
        });

        mesh.geometry.setAttribute('aSnow', new THREE.InstancedBufferAttribute(snowed, 1));
        mesh.instanceMatrix.needsUpdate = true;
        mesh.frustumCulled = false;
        this.scene.add(mesh);

        // Instances are drawn in one call, so picking gets back an index rather than
        // an object. Keeping the placements in the same order is what turns that
        // index back into the thing the player clicked on.
        mesh.visible = this.sceneryShowing && (this.roofsShowing || !this.roofIds.has(id));

        this.objectMeshes.push({
          mesh,
          id,
          unseen: part.unseen === true,
          placements: placements.map((placement) => ({
            plane: placement.plane,
            tx: region.originX + placement.tx,
            tz: region.originZ + placement.tz,
            rotation: placement.rotation,
            scale: placement.scale ?? 1,
          })),
        });
      }
    }
  }

  // -------------------------------------------------------------------------
  // Entities
  // -------------------------------------------------------------------------

  syncEntities(state: WorldState, alpha = 1): void {
    const now = performance.now();

    // How long since the last frame, for anything that eases rather than snaps.
    const delta = Math.min(0.1, (now - this.lastSynced) / 1000);
    this.lastSynced = now;

    // Remove views for entities that have gone. Something that has just died
    // stays a moment longer, so it falls over where it was standing rather than
    // blinking out mid animation.
    for (const [id, view] of this.entityViews) {
      if (state.entities.has(id)) continue;
      if (view.dyingUntil > now) continue;

      this.scene.remove(view.group);
      view.avatar?.dispose();
      this.entityViews.delete(id);
    }

    // The tile the player is on, for anything that would be inside them.
    const standing = this.showLocal && state.ready ? tileOf(state.local.pos) : null;

    for (const entity of state.entities.values()) {
      let view = this.entityViews.get(entity.id);
      if (view?.dyingUntil) view.dyingUntil = 0;
      if (!view) {
        view = this.createEntityView(entity);
        this.entityViews.set(entity.id, view);
        this.scene.add(view.group);
      }

      // On the floor they are on, not on the ground under the building.
      const y = this.surfaceAt(entity.x, entity.z, entity.plane) + entity.plane * STOREY_HEIGHT;
      view.group.position.set(entity.x, y, entity.z);
      view.group.rotation.y = turnTowards(view.group.rotation.y, entity.heading, delta);
      this.poseBody(view, entity.moving, entity.running);

      // A quest mark hung on somebody goes where they go, on every frame. It
      // used to be worked out on a half second timer, which is exactly long
      // enough for an arrow to jerk along behind a walking character instead of
      // sitting over their head.
      if (this.questMark?.entity === entity.id) {
        this.questMark = { x: entity.x, z: entity.z, plane: entity.plane, entity: entity.id };

        const arrow = this.questMarks.children[0];
        if (arrow) arrow.position.set(entity.x, this.markHeight(this.questMark), entity.z);
      }

      // Two bodies on one tile are one body with somebody's arm coming out of it.
      // Whoever the player is standing on stops being drawn until they step off,
      // and keeps their name over them so it is clear who is down there.
      this.showBody(
        view,
        !standing ||
          Math.floor(entity.x) !== standing.tx ||
          Math.floor(entity.z) !== standing.tz ||
          entity.plane !== standing.plane,
      );

      this.fadeLabel(view);
    }

    if (!this.localView && state.ready) {
      this.localView = this.createBody(COLORS.player, state.localName, 1.0, state.localLook);
      this.scene.add(this.localView.group);
    }

    if (this.localView) {
      this.localView.group.visible = this.showLocal;
    }

    if (this.localView && this.showLocal) {
      const pos = state.renderPosition(alpha);
      const y = this.surfaceAt(pos.x, pos.z, pos.plane) + pos.plane * STOREY_HEIGHT;
      this.localView.group.position.set(pos.x, y, pos.z);
      this.localView.group.rotation.y = turnTowards(
        this.localView.group.rotation.y,
        state.local.heading,
        delta,
      );
      this.poseBody(this.localView, state.local.path.length > 0, state.local.running);
      this.fadeLabel(this.localView);
    }

    // The markers stay while the walk does. Arriving takes them away, and so does
    // a moment's grace for a walk that never started: clicking somewhere nobody
    // can reach still deserves an answer that can be read.
    if (
      state.ready &&
      state.local.path.length === 0 &&
      (this.clickMarker.visible || this.stopMarker.visible)
    ) {
      // Measured against the clock rather than against the animation, which is
      // clamped per frame: on a machine drawing once a second the two are not
      // the same thing at all.
      if (performance.now() - this.markerSince > MARKER_GRACE_MS) this.hideClickMarker();
    }
  }

  private createEntityView(entity: RemoteEntity): EntityView {
    const isNpc = entity.kind === 1;
    const colour = isNpc
      ? entity.combatLevel > 40
        ? COLORS.boss
        : COLORS.npc
      : COLORS.otherPlayer;

    const label =
      isNpc && entity.combatLevel > 0 ? `${entity.name} (${entity.combatLevel})` : entity.name;
    const view = this.createBody(colour, label, entity.combatLevel > 40 ? 1.6 : 1.0, entity.look);

    // Two entities standing side by side would otherwise print their names on top of
    // each other. Staggering by id spreads them over a few heights, which is enough
    // to keep a small crowd readable.
    if (view.label) view.label.position.y += ((entity.id * 7) % 4) * 0.22;

    return view;
  }

  private createBody(
    colour: THREE.Color,
    name: string,
    scale: number,
    look: EntityLook | null,
  ): EntityView {
    const group = new THREE.Group();

    const body = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.3 * scale, 0.9 * scale, 4, 10),
      new THREE.MeshLambertMaterial({ color: colour }),
    );
    body.position.y = 0.75 * scale;
    group.add(body);

    // A flat disc under the feet, so an entity on a slope still reads as standing
    // on the ground rather than floating near it.
    const shadow = new THREE.Mesh(
      new THREE.CircleGeometry(0.34 * scale, 16),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.28 }),
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.02;
    // A stroke around somebody should go round them, not round the patch of
    // shade they are standing in.
    shadow.userData.noStroke = true;
    group.add(shadow);

    let label: THREE.Sprite | null = null;
    if (name) {
      label = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this.labelTexture(name),
          // Drawn over the world so a name is never half swallowed by a hill, but
          // hidden past a short distance so the horizon does not fill with text.
          depthTest: false,
          transparent: true,
        }),
      );
      label.scale.set(LABEL_WIDTH, LABEL_WIDTH / 4, 1);
      label.position.y = 1.75 * scale + 0.35;
      label.renderOrder = 10;
      group.add(label);
    }

    // A model where one is asked for. The capsule stays until it arrives and is
    // hidden the moment it does, so somebody walking into view is never nothing
    // at all while a megabyte comes down the wire.
    let avatar: Avatar | null = null;
    if (look && modelExists(look.model)) {
      avatar = new Avatar(look.model, { skin: look.skin, eyes: look.eyes }, MODEL_SCALE * scale);
      group.add(avatar.group);
    }

    return {
      group,
      body,
      shadow,
      underfoot: false,
      label,
      avatar,
      bar: null,
      barUntil: 0,
      dyingUntil: 0,
      wasAt: null,
    };
  }

  /**
   * Puts a body into whichever animation matches what it is doing.
   *
   * Whether somebody is moving is known rather than guessed, so this is only a
   * matter of picking the clip. The stand-in capsule is taken away here as well:
   * the frame the model is dressed is the frame the placeholder is no longer
   * wanted.
   */
  private poseBody(view: EntityView, moving: boolean, running: boolean): void {
    const avatar = view.avatar;
    if (!avatar) return;

    // The stand-in is taken out of the scene the moment the model it stood in for
    // arrives. Keeping it about, even undrawn, meant the outline traced a capsule
    // around everybody: a bean shaped ghost round a goblin.
    if (avatar.dressed && view.body.parent) {
      view.group.remove(view.body);
      view.body.geometry.dispose();
      (view.body.material as THREE.Material).dispose();
    }

    avatar.pose(moving ? (running ? 'run' : 'walk') : 'idle');
  }

  /**
   * Whether a body is drawn at all.
   *
   * Somebody standing under the player is still there to be talked to or swung
   * at, so what changes is only whether they are drawn. The picking shape is
   * whatever they are made of, which is the model once one has arrived.
   */
  private showBody(view: EntityView, shown: boolean): void {
    if (view.underfoot === !shown) return;
    view.underfoot = !shown;

    if (view.avatar) view.avatar.group.visible = shown;
    view.shadow.visible = shown;

    if (view.body.parent) {
      const material = view.body.material as THREE.Material;
      material.colorWrite = shown;
    }
  }

  /**
   * Scenery the player is standing in is not drawn for them.
   *
   * The same courtesy the world already does for somebody standing under you: a
   * fire nobody can be blocked by is a fire somebody will walk into, and a fire
   * drawn over your own head is a fire you cannot see past. What is taken away
   * is only the drawing. The pointer still finds it, because every such shape
   * carries a plate over its tile that is there to be clicked and never seen.
   */
  hideSceneryUnderfoot(tile: { tx: number; tz: number } | null, ids: readonly number[]): void {
    // What is being stood in counts as well as where: a fire lit under somebody
    // who has not moved is still a fire that should not be drawn over them.
    const wanted = tile ? `${tile.tx},${tile.tz},${[...ids].sort((a, b) => a - b).join('.')}` : '';
    if (wanted === this.underfootTile) return;

    this.underfootTile = wanted;

    // Whatever was hidden goes back first, since the player has moved and the
    // thing they were standing in is behind them now.
    for (const [key, matrix] of this.underfootScenery) {
      const [meshAt, instance] = key.split(':').map(Number) as [number, number];
      const entry = this.objectMeshes[meshAt];
      if (!entry) continue;

      entry.mesh.setMatrixAt(instance, matrix);
      entry.mesh.instanceMatrix.needsUpdate = true;
    }

    this.underfootScenery.clear();
    if (!tile || ids.length === 0) return;

    // The placements kept beside each batch are in the world's own numbers, so
    // the tile is compared as it arrives rather than being moved into the
    // region's corner first.
    const held = new Set(ids);
    const taken = new THREE.Matrix4();
    const gone = new THREE.Matrix4().makeScale(0, 0, 0);

    this.objectMeshes.forEach((entry, meshAt) => {
      // The plate that is only there to be clicked stays exactly where it is,
      // or hiding the flame would hide the fire from the pointer as well.
      if (entry.unseen || !held.has(entry.id)) return;

      entry.placements.forEach((placement, instance) => {
        if (placement.tx !== tile.tx || placement.tz !== tile.tz) return;

        entry.mesh.getMatrixAt(instance, taken);
        this.underfootScenery.set(`${meshAt}:${instance}`, taken.clone());
        entry.mesh.setMatrixAt(instance, gone);
        entry.mesh.instanceMatrix.needsUpdate = true;
      });
    });
  }

  /** How many pieces of scenery are being held back for standing in them. */
  underfootCount(): number {
    return this.underfootScenery.size;
  }

  /** Moves every animation on by the time that has passed. */
  private stepAvatars(deltaSeconds: number): void {
    for (const view of this.entityViews.values()) view.avatar?.update(deltaSeconds);
    this.localView?.avatar?.update(deltaSeconds);
  }

  /**
   * A blow landing on somebody.
   *
   * The number rises and fades over their head, and a bar under it says what is
   * left. Both are drawn from the same packet, so the two can never disagree
   * about how much of a beating somebody has taken.
   */
  showHit(entityId: number, damage: number, health: number, maxHealth: number, fromId = 0): void {
    const view = this.viewFor(entityId);
    if (!view) return;

    // A blow that landed for nothing is worth showing: a fight where the screen
    // stays empty looks broken rather than close.
    if (damage > 0 || fromId > 0) {
      const mark = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this.splatTexture(damage),
          depthTest: false,
          transparent: true,
        }),
      );

      mark.scale.set(SPLAT_SIZE, SPLAT_SIZE, 1);
      mark.renderOrder = 12;
      // Over the body rather than over the head, where the name plate already is.
      mark.position.copy(view.group.position);
      mark.position.y += 1.15;

      // Scattered a little, so two blows landing together are two splats rather
      // than one drawn twice.
      mark.position.x += (Math.random() - 0.5) * 0.45;
      mark.position.z += (Math.random() - 0.5) * 0.45;

      this.scene.add(mark);
      this.hitMarks.push({
        sprite: mark,
        until: performance.now() + HIT_MARK_MS,
        born: performance.now(),
      });
    }

    // A bar is worth showing when something has happened. Full health and no
    // damage is the state everybody is in most of the time, and a bar over every
    // head saying so is clutter.
    if (damage > 0 || health < maxHealth) this.showHealth(view, health, maxHealth);
    else if (view.bar) view.bar.visible = false;
  }

  /** Puts, or moves, the bar over somebody's head. */
  private showHealth(view: EntityView, health: number, maxHealth: number): void {
    if (!view.bar) {
      const bar = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this.barTexture(health, maxHealth),
          depthTest: false,
          transparent: true,
        }),
      );
      bar.scale.set(1.05, 0.24, 1);
      bar.renderOrder = 11;
      view.group.add(bar);
      view.bar = bar;
    }

    // Under the name plate rather than across it: a name somebody is reading and a
    // bar somebody is watching are two things, and they were drawn on top of each
    // other.
    view.bar.position.y = (view.label?.position.y ?? 2) - 0.34;
    view.bar.material.map = this.barTexture(health, maxHealth);
    view.bar.material.needsUpdate = true;
    view.bar.visible = true;
    view.barUntil = performance.now() + BAR_SHOWN_MS;
  }

  /** Whichever body an id belongs to, including the one being played. */
  private viewFor(entityId: number): EntityView | null {
    return this.entityViews.get(entityId) ?? (entityId === this.localId ? this.localView : null);
  }

  /** The id of the character being played, so hits on them find their body. */
  private localId = 0;

  setLocalId(id: number): void {
    this.localId = id;
  }

  /** Somebody swings, whether or not it lands. */
  showSwing(entityId: number): void {
    this.viewFor(entityId)?.avatar?.pose('hit', true);
  }

  /** And somebody bends over whatever they are working at. */
  showBend(entityId: number): void {
    this.viewFor(entityId)?.avatar?.pose('bend', true);
  }

  /** Or stoops to pick something up off the ground. */
  showPickUp(entityId: number): void {
    this.viewFor(entityId)?.avatar?.pose('pickup', true);
  }

  /** And falls over. */
  showDeath(entityId: number): void {
    const view = this.viewFor(entityId);
    if (!view) return;

    view.avatar?.pose('die', true);
    view.dyingUntil = performance.now() + DEATH_HELD_MS;
    if (view.bar) view.bar.visible = false;
    if (view.label) view.label.visible = false;
  }

  /**
   * Puts something in the air between two fighters, or at a spot on the ground.
   *
   * An arrow, a bolt, or a spark of whichever element a spell was cast as, or
   * whatever a spell written in the tools says it looks like: its colours, its
   * size, how high it arcs, or a model from the library. It carries nothing
   * and decides nothing: the blow it belongs to has already happened, and this
   * is what makes it read as having come from over there rather than out of
   * nowhere.
   */
  showFlying(
    fromId: number,
    toId: number,
    kind: string,
    tenths: number,
    style?: FlyingStyle,
    toTile?: { tx: number; tz: number } | null,
  ): void {
    const from = this.viewFor(fromId);
    const to = toId > 0 ? this.viewFor(toId) : null;
    if (!from || (!to && !toTile)) return;

    // A colour is a spark of that colour, which is how a spell written in the
    // tools glows the colour of its own picture.
    const look =
      FLYING_LOOKS[kind] ??
      (/^#[0-9a-fA-F]{6}$/.test(kind)
        ? { colour: parseInt(kind.slice(1), 16), shaft: false }
        : ARROW);
    const colour = style?.colour ? parseInt(style.colour.slice(1), 16) : look.colour;
    const size = style?.size ?? 1;

    const holder = new THREE.Group();
    const owned: THREE.Mesh[] = [];
    const bead = new THREE.Mesh(
      // A shaft for anything shot, a bead for anything cast.
      look.shaft
        ? new THREE.CylinderGeometry(0.035 * size, 0.035 * size, 0.55 * size, 5)
        : new THREE.SphereGeometry(0.16 * size, 8, 6),
      new THREE.MeshBasicMaterial({ color: colour, transparent: true, opacity: 0.95 }),
    );
    bead.renderOrder = 11;
    holder.add(bead);
    owned.push(bead);

    // A second colour is a glow round the first, which is most of what makes a
    // spell look like more than a marble.
    if (style?.colour2) {
      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(0.28 * size, 8, 6),
        new THREE.MeshBasicMaterial({
          color: parseInt(style.colour2.slice(1), 16),
          transparent: true,
          opacity: 0.35,
          depthWrite: false,
        }),
      );
      glow.renderOrder = 10;
      holder.add(glow);
      owned.push(glow);
    }

    // A model from the library, when one is named: drawn instead of the bead
    // once it has come down, which is usually before the first shot of a fight.
    if (style?.model) {
      const model = style.model;
      void loadUploaded(model.file)
        .then((loaded) => {
          const shape = loaded.scene.clone(true);
          shape.scale.setScalar(model.scale * size);
          shape.position.set(model.offsetX, model.offsetY, model.offsetZ);
          holder.add(shape);
          bead.visible = false;
        })
        .catch(() => undefined);
    }

    holder.position.copy(from.group.position);
    holder.position.y += 1;
    this.scene.add(holder);

    const towards = to
      ? to.group.position.clone()
      : new THREE.Vector3(
          (toTile?.tx ?? 0) + 0.5,
          this.heightAt((toTile?.tx ?? 0) + 0.5, (toTile?.tz ?? 0) + 0.5),
          (toTile?.tz ?? 0) + 0.5,
        );

    const now = performance.now();
    this.flights.push({
      object: holder,
      owned,
      fromId,
      toId,
      shaft: look.shaft || style?.model !== undefined,
      arc: style?.arc ?? 0.55,
      born: now,
      until: now + Math.max(120, tenths * 100),
      at: holder.position.clone(),
      towards,
      grounded: !to,
    });
  }

  /**
   * Moves whatever is in the air along, and takes it away when it arrives.
   *
   * Both ends are read again every frame where they are still in view, so a
   * shot follows somebody who walks while it is crossing, and holds its last
   * known heading when they are gone.
   */
  private stepFlights(): void {
    const now = performance.now();

    this.flights = this.flights.filter((flight) => {
      if (now >= flight.until) {
        this.scene.remove(flight.object);
        // Only what was made for this flight: a model from the library is
        // shared with every other copy of it and stays.
        for (const mesh of flight.owned) {
          mesh.geometry.dispose();
          (mesh.material as THREE.Material).dispose();
        }
        return false;
      }

      const from = this.viewFor(flight.fromId);
      const to = flight.grounded ? null : this.viewFor(flight.toId);
      if (from)
        flight.at.set(from.group.position.x, from.group.position.y + 1, from.group.position.z);
      if (to) flight.towards.set(to.group.position.x, to.group.position.y + 1, to.group.position.z);

      const age = (now - flight.born) / (flight.until - flight.born);
      flight.object.position.lerpVectors(flight.at, flight.towards, age);

      // An arc, so it reads as thrown rather than slid along the ground.
      flight.object.position.y += Math.sin(Math.PI * age) * flight.arc;

      // A shaft, or a model, points the way it is going; a bead has no way
      // round to point.
      if (flight.shaft) {
        const heading = flight.towards.clone().sub(flight.at);
        if (heading.lengthSq() > 0.0001) {
          flight.object.quaternion.setFromUnitVectors(UP, heading.normalize());
        }
      }

      return true;
    });
  }

  private flights: {
    object: THREE.Object3D;
    owned: THREE.Mesh[];
    fromId: number;
    toId: number;
    shaft: boolean;
    arc: number;
    born: number;
    until: number;
    at: THREE.Vector3;
    towards: THREE.Vector3;
    /** Aimed at a spot on the ground rather than at somebody. */
    grounded: boolean;
  }[] = [];

  /**
   * What time the world thinks it is, and whether the sky is meant to say so.
   *
   * Held here rather than asked for every frame, because the day is an hour
   * long and a frame is sixteen milliseconds: reading a clock that slowly needs
   * nobody's help.
   */
  private daylightNow = 1;
  private dayAndNight = true;

  /**
   * Puts the sky where the clock says it should be.
   *
   * Every colour is a blend between the noon one, which is what the world
   * looked like before there was a clock, and a night one. So daylight leaves
   * the look exactly as it was, and nothing was retuned to fit a feature.
   */
  setDaylight(light: number): void {
    const wanted = Math.min(1, Math.max(0, light));
    if (Math.abs(wanted - this.daylightNow) < 0.002) return;

    this.daylightNow = wanted;
    this.lightTheWorld();
  }

  private lightTheWorld(): void {
    // Switched off, the world is the noon it always was.
    const light = this.dayAndNight ? this.daylightNow : 1;

    this.sun.color.copy(NIGHT.sun).lerp(NOON.sun, light);
    this.sun.intensity = NIGHT.sunStrength + (NOON.sunStrength - NIGHT.sunStrength) * light;

    this.sky.color.copy(NIGHT.above).lerp(NOON.above, light);
    this.sky.groundColor.copy(NIGHT.below).lerp(NOON.below, light);
    this.sky.intensity = NIGHT.skyStrength + (NOON.skyStrength - NIGHT.skyStrength) * light;

    const air = NIGHT.air.clone().lerp(NOON.air, light);
    if (this.scene.background instanceof THREE.Color) this.scene.background.copy(air);
    if (this.scene.fog instanceof THREE.Fog) this.scene.fog.color.copy(air);

    this.lightTheWindows(light);
  }

  /**
   * The windows, the lamps and the forges, turned up as the sun goes down.
   *
   * Full at midnight and out at noon, on the same number everything else in the
   * sky is blended on, so a lamp does not come on at a different hour from the
   * one the sky says it is.
   */
  private lightTheWindows(light: number): void {
    daylightForGlows = light;
    if (litByNight === 0) return;

    const dark = 1 - light;

    this.scene.traverse((held) => {
      const material = (held as THREE.Mesh).material;
      if (!material || Array.isArray(material)) return;

      const glow: unknown = material.userData?.nightGlow;
      if (typeof glow !== 'number') return;

      (material as THREE.MeshLambertMaterial).emissiveIntensity = glow * dark;
    });
  }

  /**
   * Which of the lights are burning, and how.
   *
   * Two jobs on two clocks. Which lamp each of the few real lights is standing
   * in changes slowly, because somebody walking down a street crosses a lamp
   * every second or so and sorting a townful of them every frame is work for
   * nothing. How bright each one is changes every frame, because that is the
   * flicker.
   */
  private stepLamps(delta: number): void {
    if (this.lamps.length === 0) {
      for (const held of this.lit) {
        if (!held.light.visible) continue;
        held.light.visible = false;
        held.at = -1;
      }
      return;
    }

    this.lampsPicked -= delta;
    if (this.lampsPicked <= 0) {
      this.lampsPicked = LAMPS_PICKED_EVERY;
      this.pickLamps();
    }

    const dark = 1 - this.daylightNow;

    for (const held of this.lit) {
      const lamp = this.lamps[held.at];
      if (!lamp) continue;

      // Out by day for anything that waits for dusk, and the sun does not put
      // out a forge.
      const strength = lamp.atNight ? dark : 1;
      if (strength <= 0.02) {
        held.light.visible = false;
        continue;
      }

      held.light.visible = true;

      /*
       * The same two waves the flame itself leans on, so the light and the
       * shape are one fire rather than two things that happen to be orange.
       */
      const beat = RIPPLE_CLOCK.value * 3.1 + lamp.beat;
      const lick = lamp.flickers ? Math.sin(beat) * 0.5 + Math.sin(beat * 1.7 + 1.3) * 0.3 : 0;

      // Bright enough to lay a pool of light on the ground a few tiles across,
      // which is what a torch does and what the first attempt did not.
      held.light.intensity = strength * (lamp.reach / 2.2) * (1 + lick * 0.35);

      if (lamp.shifts) {
        // Round the wheel, slowly enough to read as a colour rather than a
        // flash, and offset by the lamp so a string of them is a rainbow.
        held.light.color.setHSL((RIPPLE_CLOCK.value * 0.08 + lamp.beat / 6.28) % 1, 0.8, 0.6);
      }
    }
  }

  /** How long until the few real lights look again at what is nearest. */
  private lampsPicked = 0;

  /**
   * Hands the real lights to whichever lamps are nearest the camera.
   *
   * Nearest rather than brightest, because what is wanted is the light on the
   * ground somebody is standing on. A lamp that is already lit keeps its light
   * when it is still among the nearest, so walking down a street does not make
   * every light in it jump from one post to the next.
   */
  private pickLamps(): void {
    /*
     * Round whoever is playing rather than round the camera.
     *
     * The camera sits ten or twenty tiles behind and above them, so picking by
     * it hands the lights to whatever is behind the viewer: a street walked
     * down at night came out dark, with the lamps lit somewhere off the back of
     * the screen. What wants lighting is the ground somebody is standing on.
     */
    const eye = this.localView?.group.position ?? this.camera.position;

    const wanted = this.lamps
      .map((lamp, at) => {
        const dx = lamp.x - eye.x;
        const dy = lamp.y - eye.y;
        const dz = lamp.z - eye.z;
        return { at, away: dx * dx + dy * dy + dz * dz };
      })
      .sort((one, two) => one.away - two.away)
      .slice(0, this.lit.length)
      .map((one) => one.at);

    // The ones already standing where they should be keep their places, so only
    // the lights that have to move do.
    const keeping = new Set(this.lit.filter((held) => wanted.includes(held.at)).map((h) => h.at));
    const spare = wanted.filter((at) => !keeping.has(at));

    for (const held of this.lit) {
      if (keeping.has(held.at)) continue;

      const next = spare.pop();
      if (next === undefined) {
        held.light.visible = false;
        held.at = -1;
        continue;
      }

      const lamp = this.lamps[next];
      if (!lamp) continue;

      held.at = next;
      held.light.position.set(lamp.x, lamp.y, lamp.z);
      held.light.distance = lamp.reach;
      if (!lamp.shifts) held.light.color.setHex(lamp.colour);
    }
  }

  /** Moves the numbers up and takes them away, and hides bars nobody is watching. */
  private stepHits(): void {
    const now = performance.now();

    this.hitMarks = this.hitMarks.filter((mark) => {
      const left = mark.until - now;
      if (left <= 0) {
        this.scene.remove(mark.sprite);
        mark.sprite.material.dispose();
        return false;
      }

      const age = (now - mark.born) / HIT_MARK_MS;

      // Thrown up and falling back, the way a splat does, and swelling as it
      // arrives so a blow reads as a blow rather than as a number appearing.
      mark.sprite.position.y += 0.02 * (1 - age) - 0.004 * age;

      const swell = age < 0.12 ? 0.65 + (age / 0.12) * 0.45 : 1;
      mark.sprite.scale.set(SPLAT_SIZE * swell, SPLAT_SIZE * swell, 1);

      mark.sprite.material.opacity = age < 0.7 ? 1 : 1 - (age - 0.7) / 0.3;
      return true;
    });

    for (const view of this.entityViews.values()) {
      if (view.bar && view.barUntil < now) view.bar.visible = false;
    }
    if (this.localView?.bar && this.localView.barUntil < now) this.localView.bar.visible = false;
  }

  /**
   * A hit splat: a number on a mark shaped like what happened.
   *
   * A blow that landed is a splash, torn at the edges the way a thrown thing is.
   * A blow that was turned aside is a shield, because that is what turned it.
   * The pair is read at a glance and never confused for each other, which is the
   * whole job: a fight is a hundred of these and nobody reads any of them twice.
   */
  private splatTexture(damage: number): THREE.Texture {
    const held = this.splatCache.get(damage);
    if (held) return held;

    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    if (context) {
      const middle = size / 2;

      if (damage > 0) {
        drawSplash(context, middle, size * 0.34);
      } else {
        drawShield(context, middle, size * 0.34);
      }

      context.font = `bold ${Math.round(size * 0.36)}px system-ui, sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.lineWidth = 4;
      context.strokeStyle = 'rgba(12, 8, 6, 0.9)';
      context.strokeText(String(damage), middle, middle + (damage > 0 ? 2 : 4));
      context.fillStyle = '#fdf6ee';
      context.fillText(String(damage), middle, middle + (damage > 0 ? 2 : 4));
    }

    const texture = new THREE.CanvasTexture(canvas);
    this.splatCache.set(damage, texture);
    return texture;
  }

  private readonly splatCache = new Map<number, THREE.Texture>();

  /**
   * A bar with the numbers on it.
   *
   * The bar says at a glance how a fight is going; the numbers say whether one
   * more blow will finish it, which is a different question and the one somebody
   * asks when it matters. Both, because neither answers for the other.
   */
  private barTexture(health: number, maxHealth: number): THREE.Texture {
    const key = `${health}/${maxHealth}`;
    const held = this.barCache.get(key);
    if (held) return held;

    const width = 160;
    const height = 34;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const part = maxHealth > 0 ? Math.max(0, Math.min(1, health / maxHealth)) : 0;

    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = 'rgba(20, 16, 12, 0.86)';
      context.fillRect(0, 0, width, height);

      context.fillStyle = part > 0.4 ? '#68b06a' : part > 0.15 ? '#d8a24a' : '#c05a4e';
      context.fillRect(2, 2, Math.round((width - 4) * part), height - 4);

      context.strokeStyle = 'rgba(12, 8, 6, 0.9)';
      context.lineWidth = 2;
      context.strokeRect(1, 1, width - 2, height - 2);

      context.font = `bold ${Math.round(height * 0.62)}px system-ui, sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.lineWidth = 3;
      context.strokeStyle = 'rgba(12, 8, 6, 0.85)';
      context.strokeText(key, width / 2, height / 2 + 1);
      context.fillStyle = '#fdf6ee';
      context.fillText(key, width / 2, height / 2 + 1);
    }

    const texture = new THREE.CanvasTexture(canvas);
    this.barCache.set(key, texture);
    return texture;
  }

  private readonly barCache = new Map<string, THREE.Texture>();

  private hitMarks: { sprite: THREE.Sprite; until: number; born: number }[] = [];

  /** Repaints somebody else, for a change that arrives while they are in view. */
  dressEntity(id: number, look: EntityLook): void {
    this.entityViews.get(id)?.avatar?.wear({ skin: look.skin, eyes: look.eyes });
  }

  /** Repaints the character being played, for somebody changing their colours. */
  dressLocal(look: EntityLook): void {
    this.localView?.avatar?.wear({ skin: look.skin, eyes: look.eyes });
  }

  /** Hides a name plate once it is too far away to be worth reading. */
  private fadeLabel(view: EntityView): void {
    const label = view.label;
    if (!label) return;

    const distance = this.camera.position.distanceTo(view.group.position);

    if (!this.showNames || distance > LABEL_VISIBLE_DISTANCE) {
      label.visible = false;
      return;
    }

    label.visible = true;

    // Sprites are measured in world units, so one close to the camera is drawn huge.
    const size = Math.min(1, distance / LABEL_REFERENCE_DISTANCE);
    label.scale.set(LABEL_WIDTH * size, (LABEL_WIDTH / 4) * size, 1);

    const fadeStart = LABEL_VISIBLE_DISTANCE - LABEL_FADE_DISTANCE;
    const material = label.material;
    material.opacity = distance <= fadeStart ? 1 : 1 - (distance - fadeStart) / LABEL_FADE_DISTANCE;
  }

  private labelTexture(text: string): THREE.Texture {
    const cached = this.labelCache.get(text);
    if (cached) return cached;

    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const context = canvas.getContext('2d');

    if (context) {
      context.font = '600 26px ui-sans-serif, system-ui, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      // Outlined rather than boxed, so labels stay readable against any ground.
      context.lineWidth = 5;
      context.strokeStyle = 'rgba(0, 0, 0, 0.85)';
      context.strokeText(text, 128, 32);
      context.fillStyle = '#e8e3d8';
      context.fillText(text, 128, 32);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    this.labelCache.set(text, texture);
    return texture;
  }

  /**
   * Draws the rectangle being dragged out on the ground.
   *
   * Lifted the same amount as the tile outline and drawn without depth testing, so
   * it stays visible over a slope rather than sinking into the hill it covers.
   */
  showArea(from: { tx: number; tz: number }, to: { tx: number; tz: number }): void {
    const minX = Math.min(from.tx, to.tx);
    const minZ = Math.min(from.tz, to.tz);
    const maxX = Math.max(from.tx, to.tx) + 1;
    const maxZ = Math.max(from.tz, to.tz) + 1;

    const points: THREE.Vector3[] = [];
    const step = 1;

    const edge = (x0: number, z0: number, x1: number, z1: number): void => {
      const length = Math.max(Math.abs(x1 - x0), Math.abs(z1 - z0));
      const count = Math.max(1, Math.ceil(length / step));
      for (let i = 0; i <= count; i++) {
        const t = i / count;
        const x = x0 + (x1 - x0) * t;
        const z = z0 + (z1 - z0) * t;
        points.push(new THREE.Vector3(x, this.heightAt(x, z) + GRID_LIFT, z));
      }
    };

    edge(minX, minZ, maxX, minZ);
    edge(maxX, minZ, maxX, maxZ);
    edge(maxX, maxZ, minX, maxZ);
    edge(minX, maxZ, minX, minZ);

    this.areaMarker.geometry.dispose();
    this.areaMarker.geometry = new THREE.BufferGeometry().setFromPoints(points);
    this.areaMarker.visible = true;
  }

  /**
   * Draws the trail a freehand drag has covered.
   *
   * The same marker the rectangle uses, so only one of them can be on screen at a
   * time, which is right: they are two ways of saying where, not two things.
   * Every tile gets its own square rather than an outline of the whole shape,
   * because the shape is arbitrary and a border around it would cost more to work
   * out than it is worth mid drag.
   */
  showTrail(tiles: readonly { tx: number; tz: number }[]): void {
    if (tiles.length === 0) {
      this.trailLines.visible = false;
      return;
    }

    const points: THREE.Vector3[] = [];
    for (const tile of tiles) {
      const corners = [
        [tile.tx, tile.tz],
        [tile.tx + 1, tile.tz],
        [tile.tx + 1, tile.tz + 1],
        [tile.tx, tile.tz + 1],
        [tile.tx, tile.tz],
      ] as const;

      for (let corner = 0; corner < 4; corner++) {
        const from = corners[corner] ?? [0, 0];
        const to = corners[corner + 1] ?? [0, 0];
        points.push(
          new THREE.Vector3(from[0], this.heightAt(from[0], from[1]) + GRID_LIFT, from[1]),
        );
        points.push(new THREE.Vector3(to[0], this.heightAt(to[0], to[1]) + GRID_LIFT, to[1]));
      }
    }

    this.trailLines.geometry.dispose();
    this.trailLines.geometry = new THREE.BufferGeometry().setFromPoints(points);
    this.trailLines.visible = true;
  }

  hideTrail(): void {
    this.trailLines.visible = false;
  }

  /**
   * A see-through copy of what is being dragged, where it would land.
   *
   * Dragging something to move it used to draw the same box a selection drag
   * draws, which says where the pointer went rather than where the thing is
   * going. This is the thing itself, at the tiles it would end up on.
   */
  showGhost(
    items: readonly { id: number; tx: number; tz: number; rotation: number; scale?: number }[],
  ): void {
    this.hideGhost();
    if (items.length === 0) return;

    const group = new THREE.Group();

    for (const item of items.slice(0, 64)) {
      const drawnAs = this.borrowedShapes.get(item.id) ?? item.id;

      for (const part of shapeParts(builtShapes.has(item.id) ? item.id : drawnAs)) {
        const material = part.material();
        material.transparent = true;
        material.opacity = 0.45;
        material.depthWrite = false;

        const mesh = new THREE.Mesh(part.geometry(), material);
        const height = part.scale * (item.scale ?? 1);
        const x = item.tx + 0.5;
        const z = item.tz + 0.5;

        // The ghost is drawn the same way the real thing is, off centre parts
        // included, or what is previewed is not what lands.
        const facing = (item.rotation * Math.PI) / 2;
        const across = (part.across ?? 0) * height;
        const along = (part.along ?? 0) * height;

        mesh.position.set(
          x + across * Math.cos(facing) + along * Math.sin(facing),
          this.heightAt(x, z) + part.offset * height,
          z - across * Math.sin(facing) + along * Math.cos(facing),
        );
        mesh.rotation.y = facing;
        mesh.scale.setScalar(height);
        mesh.renderOrder = 3;
        group.add(mesh);
      }
    }

    this.ghost = group;
    this.scene.add(group);
  }

  /**
   * The other places a character stands, drawn where they are.
   *
   * A see-through figure at each, so somebody writing the conditions can see
   * where they are sending them without walking over to look. Only while the
   * world is being edited, and only for the character being edited.
   */
  showSpots(spots: readonly { tx: number; tz: number }[], model?: string): void {
    this.hideSpots();
    if (spots.length === 0) return;

    const group = new THREE.Group();

    // Drawn as the character, not as a shape standing in for one. A pill at each
    // spot says where somebody would be; it does not say who, which is the one
    // thing worth knowing when a character stands in four places.
    const wanted = (model ?? '').split(',')[0]?.trim() ?? '';
    const named = modelExists(wanted) ? wanted : null;

    for (const spot of spots.slice(0, 16)) {
      const x = spot.tx + 0.5;
      const z = spot.tz + 0.5;
      const ground = this.heightAt(x, z);

      if (named) {
        const avatar = new Avatar(named, DEFAULT_APPEARANCE, MODEL_SCALE);
        avatar.group.position.set(x, ground, z);

        // See-through, and drawn over what is behind it, so a ghost inside a
        // building is still a ghost somebody can see.
        avatar.group.traverse((child) => {
          if (!(child instanceof THREE.Mesh)) return;

          const materials = Array.isArray(child.material) ? child.material : [child.material];
          child.material = materials.map((held) => {
            const copy = (held as THREE.Material).clone();
            copy.transparent = true;
            copy.opacity = 0.4;
            copy.depthWrite = false;
            return copy;
          });
          child.renderOrder = 3;
        });

        this.spotAvatars.push(avatar);
        group.add(avatar.group);
      } else {
        const body = new THREE.Mesh(
          new THREE.CapsuleGeometry(0.28, 0.9, 4, 8),
          new THREE.MeshBasicMaterial({
            color: 0x0cd5e3,
            transparent: true,
            opacity: 0.3,
            depthWrite: false,
          }),
        );
        body.position.set(x, ground + 0.85, z);
        body.renderOrder = 3;
        group.add(body);
      }

      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.34, 0.46, 20),
        new THREE.MeshBasicMaterial({
          color: 0x0cd5e3,
          transparent: true,
          opacity: 0.55,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
      );
      ring.position.set(x, ground + 0.03, z);
      ring.rotation.x = -Math.PI / 2;
      ring.renderOrder = 3;
      group.add(ring);
    }

    this.spotGhosts = group;
    this.scene.add(group);
  }

  /** Takes the spot ghosts away again. */
  hideSpots(): void {
    for (const avatar of this.spotAvatars) {
      // The see-through copies made for the ghost, which the avatar never knew
      // about and so will not let go of on its own.
      avatar.group.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;

        for (const held of Array.isArray(child.material) ? child.material : [child.material]) {
          (held as THREE.Material).dispose();
        }
      });
      avatar.dispose();
    }
    this.spotAvatars.length = 0;

    if (!this.spotGhosts) return;

    this.scene.remove(this.spotGhosts);
    for (const child of this.spotGhosts.children) {
      // A body is a tree of meshes the avatar owns and has already been let go
      // of just above; only the pills made here are this one's to take away.
      if (!(child instanceof THREE.Mesh)) continue;

      (child.geometry as THREE.BufferGeometry).dispose();
      (child.material as THREE.Material).dispose();
    }

    this.spotGhosts = null;
  }

  private spotGhosts: THREE.Group | null = null;

  /** The bodies standing at the spots, kept so they can be let go of. */
  private readonly spotAvatars: Avatar[] = [];

  /**
   * Handles on what is held: an arrow each way to move it, a ring to turn it,
   * and a box to size it.
   *
   * Borrowed from every editor that has ever had a selection, for the same
   * reason they all have one: the tools say what a drag would do, and the gizmo
   * says where to start the drag. Drawn over everything, since a handle behind a
   * wall is a handle nobody can grab.
   */
  showGizmo(at: { tx: number; tz: number } | null, kind: 'move' | 'rotate' | 'scale'): void {
    this.hideGizmo();
    if (!at) return;

    const group = new THREE.Group();
    const x = at.tx + 0.5;
    const z = at.tz + 0.5;
    const y = this.surfaceAt(x, z) + 0.08;

    const material = (colour: number): THREE.Material =>
      new THREE.MeshBasicMaterial({
        color: colour,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
      });

    if (kind === 'move') {
      // One arrow along each axis, in the colours every editor uses for them.
      for (const [dx, dz, colour] of [
        [1, 0, 0xd6604d],
        [0, 1, 0x6f9ad6],
      ] as const) {
        const shaft = new THREE.Mesh(
          new THREE.BoxGeometry(dx ? 1.6 : 0.06, 0.06, dz ? 1.6 : 0.06),
          material(colour),
        );
        shaft.position.set(x, y, z);
        group.add(shaft);

        for (const way of [1, -1]) {
          const head = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.34, 8), material(colour));
          head.position.set(x + dx * way * 0.95, y, z + dz * way * 0.95);
          head.rotation.z = dx ? (way > 0 ? -Math.PI / 2 : Math.PI / 2) : 0;
          head.rotation.x = dz ? (way > 0 ? Math.PI / 2 : -Math.PI / 2) : 0;
          group.add(head);
        }
      }
    }

    if (kind === 'rotate') {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.95, 0.05, 8, 32), material(0x0cd5e3));
      ring.position.set(x, y, z);
      ring.rotation.x = Math.PI / 2;
      group.add(ring);

      for (const way of [1, -1]) {
        const nib = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.3, 8), material(0x0cd5e3));
        nib.position.set(x + way * 0.95, y, z);
        nib.rotation.x = way > 0 ? Math.PI / 2 : -Math.PI / 2;
        group.add(nib);
      }
    }

    if (kind === 'scale') {
      for (const [dx, dz] of [
        [1, 1],
        [1, -1],
        [-1, 1],
        [-1, -1],
      ] as const) {
        const knob = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.22, 0.22), material(0xc8a86a));
        knob.position.set(x + dx * 0.8, y, z + dz * 0.8);
        group.add(knob);
      }
    }

    group.renderOrder = 6;
    for (const child of group.children) child.renderOrder = 6;

    this.gizmo = group;
    this.scene.add(group);
  }

  hideGizmo(): void {
    if (!this.gizmo) return;

    this.scene.remove(this.gizmo);
    for (const child of this.gizmo.children) {
      const mesh = child as THREE.Mesh;
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }

    this.gizmo = null;
  }

  private gizmo: THREE.Group | null = null;

  hideGhost(): void {
    if (!this.ghost) return;

    this.scene.remove(this.ghost);
    for (const child of this.ghost.children) {
      const mesh = child as THREE.Mesh;
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }

    this.ghost = null;
  }

  private ghost: THREE.Group | null = null;

  hideArea(): void {
    this.areaMarker.visible = false;
  }

  /**
   * Draws the outline of every spawn zone.
   *
   * Only the edges are traced: a tile whose neighbour is outside the zone
   * contributes that side and nothing else, so what is drawn is the shape of the
   * area rather than a grid over it.
   */
  setZones(zones: readonly { id: number; tiles: { tx: number; tz: number }[] }[]): void {
    const points: number[] = [];
    const colours: number[] = [];
    const colour = new THREE.Color();

    for (const zone of zones) {
      // A colour from the zone's own number, so two that overlap can be told
      // apart at a glance rather than by clicking through them.
      colour.setHSL(((zone.id * 47) % 360) / 360, 0.65, 0.62);

      const held = new Set(zone.tiles.map((tile) => `${tile.tx},${tile.tz}`));

      for (const tile of zone.tiles) {
        const sides = [
          [0, 1, 0, 1, 1, 1],
          [1, 0, 1, 1, 1, 0],
          [0, 0, 0, 1, 0, 0],
          [0, 0, 0, 0, 1, 0],
        ] as const;

        const beyond = [
          [0, 1],
          [1, 0],
          [-1, 0],
          [0, -1],
        ] as const;

        for (let side = 0; side < 4; side++) {
          const step = beyond[side] ?? [0, 0];
          if (held.has(`${tile.tx + step[0]},${tile.tz + step[1]}`)) continue;

          const edge = sides[side] ?? [0, 0, 0, 0, 0, 0];
          const ax = tile.tx + (edge[0] ?? 0);
          const az = tile.tz + (edge[1] ?? 0);
          const bx = tile.tx + (edge[3] ?? 0);
          const bz = tile.tz + (edge[4] ?? 0);

          points.push(ax, this.surfaceAt(ax, az) + GRID_LIFT * 3, az);
          points.push(bx, this.surfaceAt(bx, bz) + GRID_LIFT * 3, bz);
          colours.push(colour.r, colour.g, colour.b, colour.r, colour.g, colour.b);
        }
      }
    }

    this.zoneLines.geometry.dispose();
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colours, 3));
    this.zoneLines.geometry = geometry;
    this.zoneLines.visible = this.showZones && points.length > 0;
    this.zoneTiles = zones.flatMap((zone) => zone.tiles);
  }

  /** Whether zone outlines are drawn at all, which is only true while editing. */
  showZoneOutlines(showing: boolean): void {
    this.showZones = showing;
    this.zoneLines.visible = showing && this.zoneTiles.length > 0;
  }

  private showZones = false;
  private zoneTiles: { tx: number; tz: number }[] = [];

  /** Traces a border around every tile the editor is holding. */
  /**
   * The ground a character keeps to, drawn where it is.
   *
   * Told as tiles rather than as a rectangle, so a painted shape and a dragged
   * box are the same thing to draw.
   */
  showRoam(tiles: readonly { tx: number; tz: number }[]): void {
    if (tiles.length === 0) {
      this.roamLines.visible = false;
      return;
    }

    const points: number[] = [];
    for (const tile of tiles) {
      const corners = [
        [tile.tx, tile.tz],
        [tile.tx + 1, tile.tz],
        [tile.tx + 1, tile.tz + 1],
        [tile.tx, tile.tz + 1],
      ] as const;

      for (let corner = 0; corner < 4; corner++) {
        const from = corners[corner] ?? [0, 0];
        const to = corners[(corner + 1) % 4] ?? [0, 0];
        points.push(from[0], this.surfaceAt(from[0], from[1]) + GRID_LIFT * 3, from[1]);
        points.push(to[0], this.surfaceAt(to[0], to[1]) + GRID_LIFT * 3, to[1]);
      }
    }

    this.roamLines.geometry.dispose();
    this.roamLines.geometry = new THREE.BufferGeometry().setAttribute(
      'position',
      new THREE.Float32BufferAttribute(points, 3),
    );
    this.roamLines.visible = true;
  }

  /**
   * Marks whatever the step somebody is following points at.
   *
   * Told where rather than what: the game works out which characters and which
   * pieces of scenery the step means, and this draws a ring on the ground at
   * each of them with an arrow over it. Nothing here knows what a quest is.
   */
  showQuestMark(place: { x: number; z: number; plane: number; entity?: number } | null): void {
    for (const held of [...this.questMarks.children]) {
      this.questMarks.remove(held);
      // Every mark is a mesh this made, so both halves of it are this one's to
      // take away again.
      if (!(held instanceof THREE.Mesh)) continue;

      (held.geometry as THREE.BufferGeometry).dispose();
      (held.material as THREE.Material).dispose();
    }

    this.questMark = place;

    if (!place) {
      this.questMarks.visible = false;
      return;
    }

    // One arrow above whatever the step wants, and nothing on the ground. A
    // ring around every tree in a wood is decoration; an arrow over the one
    // that matters is a hint. Held above head height so it clears a person.
    const arrow = new THREE.Mesh(
      new THREE.ConeGeometry(0.22, 0.5, 4),
      new THREE.MeshBasicMaterial({
        color: QUEST_MARK,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
      }),
    );

    arrow.rotation.x = Math.PI;
    arrow.position.set(place.x, this.markHeight(place), place.z);
    arrow.renderOrder = 4;
    this.questMarks.add(arrow);

    this.questMarks.visible = true;
  }

  /** Where the quest mark is pointing, for whatever needs to draw it flat. */
  questMarkAt(): { x: number; z: number; plane: number } | null {
    return this.questMark;
  }

  /**
   * Where the quest mark falls on the screen, and which way it lies.
   *
   * A mark behind a hill or off the side of the view is a mark nobody can
   * follow, so what comes back says whether it is in view and, when it is not,
   * where along the edge to point and at what angle. Worked out here because
   * the camera is here; drawn wherever the rest of the interface is drawn.
   */
  questMarkOnScreen(
    width: number,
    height: number,
  ): { x: number; y: number; inView: boolean; angle: number } | null {
    if (!this.questMark || !this.questMarks.visible) return null;

    const at = new THREE.Vector3(
      this.questMark.x,
      this.markHeight(this.questMark),
      this.questMark.z,
    );

    // Behind the camera projects to the same place as in front of it, mirrored,
    // which would send the pointer to the opposite edge. Caught by the sign of
    // the depth in the camera's own space.
    const depth = at.clone().applyMatrix4(this.camera.matrixWorldInverse).z;
    const projected = at.clone().project(this.camera);

    const middle = { x: width / 2, y: height / 2 };
    let x = (projected.x * 0.5 + 0.5) * width;
    let y = (-projected.y * 0.5 + 0.5) * height;

    if (depth > 0) {
      // Mirrored back to the side it is really on.
      x = middle.x - (x - middle.x);
      y = middle.y - (y - middle.y);
    }

    const inset = 42;
    const inView =
      depth < 0 && x >= inset && x <= width - inset && y >= inset && y <= height - inset;

    if (inView) return { x, y, inView, angle: 0 };

    // Pushed back to the edge along the line from the middle, so the pointer
    // sits where somebody would turn to look.
    const away = { x: x - middle.x, y: y - middle.y };
    const reach = Math.max(1e-3, Math.hypot(away.x, away.y));
    const scale = Math.min(
      (middle.x - inset) / Math.max(1e-3, Math.abs(away.x)),
      (middle.y - inset) / Math.max(1e-3, Math.abs(away.y)),
    );

    return {
      x: middle.x + (away.x / reach) * reach * scale,
      y: middle.y + (away.y / reach) * reach * scale,
      inView: false,
      angle: Math.atan2(away.y, away.x),
    };
  }

  private questMark: { x: number; z: number; plane: number; entity?: number } | null = null;

  /** How high above the ground the mark floats, clear of anybody standing there. */
  private markHeight(place: { x: number; z: number; plane: number }): number {
    return this.surfaceAt(place.x, place.z, place.plane) + place.plane * STOREY_HEIGHT + 2.6;
  }

  /** How far the quest markers have bobbed, so they are noticed without shouting. */
  private markBob = 0;

  /**
   * Moves the quest mark gently, once per drawn frame.
   *
   * Set from where it belongs rather than nudged from where it is: adding a
   * little to the height every frame is how a marker slowly climbs out of the
   * sky over a long session.
   */
  private stepQuestMarks(delta: number): void {
    if (!this.questMarks.visible || !this.questMark) return;

    this.markBob += delta;
    const bob = Math.sin(this.markBob * 2.2) * 0.14;
    const height = this.markHeight(this.questMark) + bob;

    for (const held of this.questMarks.children) held.position.y = height;
  }

  /**
   * Puts a model somebody is fitting down in the world, at a tile.
   *
   * The fitting is judged against everything else rather than against a preview
   * box: how big a barrel is only means anything beside a fence and a person.
   * Null takes it away again.
   */
  showFittedModel(
    at: { x: number; z: number; plane: number } | null,
    model: {
      file: string;
      scale: number;
      offsetX: number;
      offsetY: number;
      offsetZ: number;
      turns: number;
    } | null,
  ): void {
    if (this.fitted) {
      this.scene.remove(this.fitted);
      this.fitted = null;
    }

    if (!at || !model) {
      this.fittedFile = '';
      return;
    }

    this.fittedFile = model.file;

    void loadUploaded(model.file)
      .then((loaded) => {
        // Somebody may have moved on to another model while this came down.
        if (this.fittedFile !== model.file) return;

        const held = loaded.scene.clone(true);
        held.scale.setScalar(model.scale);
        held.rotation.y = (model.turns * Math.PI) / 2;
        held.position.set(
          at.x + model.offsetX,
          this.heightAt(at.x, at.z) + at.plane * STOREY_HEIGHT + model.offsetY,
          at.z + model.offsetZ,
        );

        if (this.fitted) this.scene.remove(this.fitted);
        this.fitted = held;
        this.scene.add(held);
      })
      .catch(() => undefined);
  }

  /** The model being fitted, and which file it is, so a late arrival is ignored. */
  private fitted: THREE.Object3D | null = null;
  private fittedFile = '';

  /**
   * Puts a stroke around whatever the pointer is over.
   *
   * A line around the shape rather than a colour over it: tinting a goblin red
   * turns it into a red goblin, which says something about the goblin instead of
   * something about the pointer.
   *
   * The line is a copy of the shape, grown a little along its own normals and
   * drawn inside out, so only the rim that sticks out past the real thing is
   * seen. It is part of the picture rather than a pass over the finished one,
   * which is what keeps it crisp: a pass works from a half sized copy of the
   * screen, and every camera move sent a band of stepped pixels crawling around
   * the inside of the line.
   */
  highlight(
    target: { entityId?: number; objectId?: number; index?: number } | null,
    colour = 0xffffff,
  ): void {
    this.clearHighlight();
    if (!target) return;

    this.strokeColour = colour;

    if (target.entityId !== undefined) {
      const view = this.entityViews.get(target.entityId);
      if (view) this.drawStrokeAround(view.group, colour);
      return;
    }

    if (target.objectId === undefined || target.index === undefined) return;

    // Scenery is drawn a thousand at a time out of one mesh, so the one under
    // the pointer is copied out on its own and the copy is what gets the line.
    for (const entry of this.objectMeshes) {
      // A part that is only there to be clicked on is not part of the shape, so
      // no line is drawn round it: a fishing spot is its ripples, not the square
      // of water they sit on.
      if (entry.id !== target.objectId || entry.unseen) continue;

      const matrix = new THREE.Matrix4();
      entry.mesh.getMatrixAt(target.index, matrix);

      // Whatever this part does to itself in the shader, the mask and the line
      // do too, so a stroke on a fishing spot travels out with the ripple
      // instead of sitting where the ripple started.
      const motion = motionOf(entry.mesh.material);
      const material = strokeMaterial(colour, motion);

      const mask = new THREE.Mesh(entry.mesh.geometry, maskMaterial(motion));
      mask.applyMatrix4(matrix);
      mask.frustumCulled = false;
      mask.renderOrder = MASK_ORDER;
      mask.userData.stroke = true;

      const shell = new THREE.Mesh(strokeShape(entry.mesh.geometry), material);
      shell.applyMatrix4(matrix);
      shell.frustumCulled = false;
      shell.renderOrder = STROKE_ORDER;
      shell.userData.stroke = true;

      this.scene.add(mask);
      this.scene.add(shell);
      this.strokes.push(mask, shell);
    }
  }

  /** A copy of every mesh under something, grown and turned inside out. */
  private drawStrokeAround(held: THREE.Object3D, colour: number): void {
    held.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.visible) return;
      if (child.userData.noStroke === true) return;

      const skinned = child instanceof THREE.SkinnedMesh;
      const motion = motionOf(child.material as THREE.Material | THREE.Material[]);

      /** A copy of this mesh, standing exactly where it stands. */
      const copyOf = (
        shape: THREE.BufferGeometry,
        material: THREE.Material,
        order: number,
      ): THREE.Mesh => {
        const made = skinned
          ? new THREE.SkinnedMesh(shape, material)
          : new THREE.Mesh(shape, material);

        if (made instanceof THREE.SkinnedMesh && child instanceof THREE.SkinnedMesh) {
          // The same bones, so the line bends with whatever the body is doing
          // rather than standing there in the pose the model was made in.
          made.bind(child.skeleton, child.bindMatrix);
          made.bindMode = child.bindMode;
        }

        made.position.copy(child.position);
        made.quaternion.copy(child.quaternion);
        made.scale.copy(child.scale);
        made.frustumCulled = false;
        made.renderOrder = order;
        // Marked, so anything walking the scene can tell the line from the
        // body it is drawn around.
        made.userData.stroke = true;

        child.parent?.add(made);
        this.strokes.push(made);

        return made;
      };

      // Typed on the way out: three describes a mesh's geometry loosely enough
      // that it arrives here as anything at all.
      const shape = child.geometry as THREE.BufferGeometry;
      copyOf(shape, maskMaterial(motion), MASK_ORDER);
      copyOf(strokeShape(shape), strokeMaterial(colour, motion), STROKE_ORDER);
    });
  }

  /** Takes the stroke off. */
  private clearHighlight(): void {
    for (const held of this.strokes) held.removeFromParent();
    this.strokes = [];
  }

  /** What is being outlined, for anything that wants to know. */
  get highlighted(): readonly THREE.Object3D[] {
    return this.strokes;
  }

  /** And what colour the line is, as a hex string. */
  get highlightColour(): string {
    return this.strokeColour.toString(16).padStart(6, '0');
  }

  private strokeColour = 0xffffff;

  /** The copies that make up the line, for as long as it is being drawn. */
  private strokes: THREE.Mesh[] = [];

  showSelection(tiles: readonly { tx: number; tz: number }[]): void {
    if (tiles.length === 0) {
      this.selectionLines.visible = false;
      return;
    }

    const points: number[] = [];
    for (const tile of tiles) {
      const corners = [
        [tile.tx, tile.tz],
        [tile.tx + 1, tile.tz],
        [tile.tx + 1, tile.tz + 1],
        [tile.tx, tile.tz + 1],
      ] as const;

      for (let corner = 0; corner < 4; corner++) {
        const from = corners[corner] ?? [0, 0];
        const to = corners[(corner + 1) % 4] ?? [0, 0];
        points.push(from[0], this.surfaceAt(from[0], from[1]) + GRID_LIFT * 4, from[1]);
        points.push(to[0], this.surfaceAt(to[0], to[1]) + GRID_LIFT * 4, to[1]);
      }
    }

    this.selectionLines.geometry.dispose();
    this.selectionLines.geometry = new THREE.BufferGeometry().setAttribute(
      'position',
      new THREE.Float32BufferAttribute(points, 3),
    );
    this.selectionLines.visible = true;
  }

  /**
   * Which scenery is drawn as something else.
   *
   * Set from the definitions, so a kind made while the world is running is drawn
   * from the moment it exists rather than after a reload.
   */
  setBorrowedShapes(pairs: Iterable<[number, number]>): void {
    this.borrowedShapes.clear();
    for (const [id, looksLike] of pairs) {
      if (looksLike > 0) this.borrowedShapes.set(id, looksLike);
    }
  }

  private readonly borrowedShapes = new Map<number, number>();

  /** Tells the renderer which scenery is a floor, so the pointer can land on it. */
  setFloors(ids: Iterable<number>): void {
    this.floorIds.clear();
    for (const id of ids) this.floorIds.add(id);
    this.rebuildFloors();
  }

  /** Notes where the floors are, from what the region is holding right now. */
  private rebuildFloors(): void {
    this.floorTiles.clear();

    const region = this.region;
    if (!region) return;

    for (const object of region.objects) {
      if (!this.floorIds.has(object.id)) continue;
      this.floorTiles.set(
        this.floorKey(region, object.tx, object.tz, object.plane ?? 0),
        object.lift ?? Number.NaN,
      );
    }
  }

  private floorKey(
    region: { width: number; height: number },
    tx: number,
    tz: number,
    plane: number,
  ): number {
    return (plane * region.height + tz) * region.width + tx;
  }

  /** Which scenery is overhead, so it can be taken away while somebody is under it. */
  setRoofs(ids: Iterable<number>): void {
    this.roofIds.clear();
    for (const id of ids) this.roofIds.add(id);
  }

  /**
   * Whether a tile has something overhead.
   *
   * Asked once a tile rather than every frame: a roof does not move, and the
   * only thing that changes is which tile somebody is standing on.
   */
  roofOver(tx: number, tz: number): boolean {
    const region = this.region;
    if (!region || this.roofIds.size === 0) return false;

    const localX = tx - region.originX;
    const localZ = tz - region.originZ;

    return region.objects.some(
      (object) => object.tx === localX && object.tz === localZ && this.roofIds.has(object.id),
    );
  }

  /** Takes the roofs away, or puts them back. */
  showRoofs(showing: boolean): void {
    if (this.roofsShowing === showing) return;
    this.roofsShowing = showing;

    for (const entry of this.objectMeshes) {
      if (this.roofIds.has(entry.id)) entry.mesh.visible = showing && this.sceneryShowing;
    }
  }

  /**
   * Lifts the roofs within a few tiles of a point, and puts back the rest.
   *
   * Held like a lamp over a model: the roofs near the pointer come off so what
   * is under them can be worked on, and everything further away stays roofed so
   * the place still reads as a place.
   */
  liftRoofsAround(at: { tx: number; tz: number } | null, reach = 4): void {
    if (this.roofPatch === null && at === null) return;
    this.roofPatch = at;

    for (const entry of this.objectMeshes) {
      if (!this.roofIds.has(entry.id)) continue;
      if (!this.sceneryShowing) continue;

      // One instanced mesh holds every roof of a kind, so the whole mesh goes
      // when any of its tiles is near enough. Roofs of one kind cover one
      // building far more often than they cover the whole island.
      const near =
        at !== null &&
        entry.placements.some(
          (placement) =>
            Math.abs(placement.tx - at.tx) <= reach && Math.abs(placement.tz - at.tz) <= reach,
        );

      entry.mesh.visible = this.roofsShowing && !near;
    }
  }

  private roofPatch: { tx: number; tz: number } | null = null;

  private roofsShowing = true;

  /**
   * Takes every piece of scenery away, or puts it back.
   *
   * Sculpting under a wood means sculpting blind. This hides what is standing on
   * the ground without touching the world: nothing is removed, and the moment it
   * is put back everything is where it was. Roofs keep their own rule, so walking
   * indoors while the scenery is hidden does not put them back.
   */
  showScenery(showing: boolean): void {
    if (this.sceneryShowing === showing) return;
    this.sceneryShowing = showing;

    for (const entry of this.objectMeshes) {
      if (this.roofIds.has(entry.id)) {
        entry.mesh.visible = showing && this.roofsShowing;
        continue;
      }

      entry.mesh.visible = showing;
    }
  }

  private sceneryShowing = true;

  /**
   * The height of whatever is walked on at a point: the ground, or a floor laid
   * over it. Markers and outlines use this so they sit on the boards rather than
   * disappearing into them.
   */
  /** Whether boards or a floor are laid where somebody stands, for the sound of their feet. */
  hasFloorAt(x: number, z: number, plane = 0): boolean {
    const region = this.region;
    if (!region || this.floorTiles.size === 0) return false;
    return this.floorTiles.has(
      this.floorKey(region, Math.floor(x) - region.originX, Math.floor(z) - region.originZ, plane),
    );
  }

  surfaceAt(x: number, z: number, plane = 0): number {
    const ground = this.heightAt(x, z);
    const region = this.region;
    if (!region || this.floorTiles.size === 0) return ground;

    const held = this.floorTiles.get(
      this.floorKey(region, Math.floor(x) - region.originX, Math.floor(z) - region.originZ, plane),
    );
    if (held === undefined) return ground;

    // A dock keeps the height it was built to. Boards laid on the ground follow
    // whatever they were laid on.
    return (Number.isNaN(held) ? ground : held) + FLOOR_LIFT;
  }

  /**
   * Where a click asked the character to go, and where they will actually stop.
   *
   * Both stay up until the walk is over rather than fading after a moment: a
   * marker that has gone is a marker that cannot answer "where am I going". They
   * settle to a quieter opacity and breathe, so they are still saying something
   * without being the brightest thing on the ground.
   */
  showClickMarker(x: number, z: number, stop?: { x: number; z: number }): void {
    if (!this.markerEnabled) return;

    this.clickMarker.position.set(x, this.surfaceAt(x, z) + 0.05, z);
    this.clickMarker.visible = true;
    this.markerBeat = 0;
    this.markerSince = performance.now();

    // Only worth drawing when it is somewhere else. A second ring on top of the
    // first says nothing except that there are two rings.
    const elsewhere =
      stop !== undefined && (Math.abs(stop.x - x) > 0.01 || Math.abs(stop.z - z) > 0.01);

    // A click somewhere that cannot be walked to is marked in red and fades
    // away, and the tile they are actually walking to takes the usual gold
    // ring: not there, here, and here is the one that stays.
    const click = this.clickMarker.material as THREE.MeshBasicMaterial;
    click.color.setHex(elsewhere ? MARKER_REFUSED : MARKER_ACCENT);

    if (elsewhere) {
      this.stopMarker.position.set(stop.x, this.surfaceAt(stop.x, stop.z) + 0.06, stop.z);
      this.stopMarker.visible = true;
    } else {
      this.stopMarker.visible = false;
    }
  }

  /** Takes the markers off, which arriving somewhere does. */
  hideClickMarker(): void {
    this.clickMarker.visible = false;
    this.stopMarker.visible = false;
  }

  /** Breathes the markers, so a walk under way still says where it is going. */
  private stepMarkers(delta: number): void {
    if (!this.clickMarker.visible && !this.stopMarker.visible) return;

    this.markerBeat += delta;

    // Bright for a moment, then down to a steady throb: loud on arrival of the
    // click, quiet company for the rest of the walk.
    const settled = Math.min(1, this.markerBeat / 0.5);
    const throb = 0.55 + Math.sin(this.markerBeat * 3.4) * 0.16;
    const opacity = 1 - settled * (1 - throb);

    const click = this.clickMarker.material as THREE.MeshBasicMaterial;
    (this.stopMarker.material as THREE.MeshBasicMaterial).opacity = opacity;

    // The refused tile has a few seconds to make its point and then goes, which
    // leaves only the ring on the tile they are actually walking to.
    if (this.stopMarker.visible) {
      const age = performance.now() - this.markerSince;
      const left = Math.max(0, 1 - age / MARKER_REFUSED_MS);

      click.opacity = opacity * left;
      if (left <= 0) this.clickMarker.visible = false;
    } else {
      click.opacity = opacity;
    }

    const size = 1 + Math.sin(this.markerBeat * 3.4) * 0.06 * settled;
    this.clickMarker.scale.set(size, size, size);
    this.stopMarker.scale.set(size, size, size);
  }

  // -------------------------------------------------------------------------
  // Tile overlays
  // -------------------------------------------------------------------------

  /**
   * Draws the tile grid and the occupied tile.
   *
   * The grid is a patch rather than a mesh over the whole region: a 128 by 128
   * region is sixteen thousand tiles, and almost none of them are ever near
   * enough to read. It is only rebuilt when its centre crosses into a new tile,
   * so standing still costs nothing.
   *
   * The two are centred separately on purpose. The mark under somebody's feet
   * belongs to their feet, but the grid belongs to whatever is being looked at:
   * a camera sent off across the island to build something was leaving the grid
   * behind with the character, which is a grid over nothing anybody is editing.
   */
  updateTileOverlay(
    centre: { tx: number; tz: number },
    showGrid: boolean,
    highlight: boolean,
    gridCentre?: { tx: number; tz: number },
    /**
     * Whether a tile is closed to walking, when the closed ones are being shown.
     *
     * Asked rather than worked out here, because the answer is the same one
     * movement uses: an overlay that decided for itself what counts as blocked
     * would be an overlay that disagreed with the world it is drawn over, which
     * is worse than not drawing it.
     */
    blocked?: (tx: number, tz: number) => boolean,
  ): void {
    const wasShowingGrid = this.grid.visible;
    const wasHighlighting = this.tileMarker.visible;

    this.grid.visible = showGrid && this.region !== null;
    this.tileMarker.visible = highlight && this.region !== null;

    const around = gridCentre ?? centre;
    const moved = centre.tx !== this.overlayTile.tx || centre.tz !== this.overlayTile.tz;
    const gridMoved = around.tx !== this.gridTile.tx || around.tz !== this.gridTile.tz;

    if (this.grid.visible && (!this.overlayBuilt || gridMoved || !wasShowingGrid)) {
      this.rebuildGrid(around);
      this.gridTile = { tx: around.tx, tz: around.tz };
    }

    if (this.tileMarker.visible && (!this.overlayBuilt || moved || !wasHighlighting)) {
      this.placeTileMarker(centre);
    }

    this.blockedTiles.visible = blocked !== undefined && this.region !== null;

    // Rebuilt on a beat as well as on a move: placing a wall closes tiles, and
    // an overlay that only followed the camera would show yesterday's answer
    // until somebody walked.
    const beat = performance.now();
    if (
      this.blockedTiles.visible &&
      blocked &&
      (gridMoved || beat - this.blockedAt > 400 || this.blockedAt === 0)
    ) {
      this.rebuildBlocked(around, blocked);
      this.blockedAt = beat;
    }

    this.overlayTile = { tx: centre.tx, tz: centre.tz };
    this.overlayBuilt = true;
  }

  /** A square inside each closed tile, over the patch the grid covers. */
  private rebuildBlocked(
    centre: { tx: number; tz: number },
    blocked: (tx: number, tz: number) => boolean,
  ): void {
    const half = GRID_PATCH_TILES / 2;
    const inset = 0.16;
    const lines: number[] = [];

    for (let dz = 0; dz < GRID_PATCH_TILES; dz++) {
      for (let dx = 0; dx < GRID_PATCH_TILES; dx++) {
        const tx = centre.tx - half + dx;
        const tz = centre.tz - half + dz;
        if (!blocked(tx, tz)) continue;

        const near = tx + inset;
        const far = tx + 1 - inset;
        const low = tz + inset;
        const high = tz + 1 - inset;

        // Each side is one segment, lifted onto the ground under its own ends,
        // so the square follows a slope rather than sinking into it.
        const corners: [number, number][] = [
          [near, low],
          [far, low],
          [far, high],
          [near, high],
        ];

        for (let at = 0; at < 4; at++) {
          const from = corners[at] as [number, number];
          const to = corners[(at + 1) % 4] as [number, number];
          lines.push(
            from[0],
            this.heightAt(from[0], from[1]) + GRID_LIFT,
            from[1],
            to[0],
            this.heightAt(to[0], to[1]) + GRID_LIFT,
            to[1],
          );
        }
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(lines, 3));

    this.blockedTiles.geometry.dispose();
    this.blockedTiles.geometry = geometry;
  }

  /**
   * Which storey the tools are building on.
   *
   * Nought is the ground. Above that, the grid and the tile outline have to
   * follow the storey rather than the ground under it, or somebody laying a
   * first floor is working from a grid drawn on the earth two and a half tiles
   * below where the boards are going: it says which tile the pointer is over
   * and nothing at all about where the thing will end up.
   */
  private buildStorey = 0;

  buildOn(storey: number): void {
    if (this.buildStorey === storey) return;
    this.buildStorey = storey;

    // Both are drawn to a height, so both are drawn again at the new one.
    this.overlayBuilt = false;
    this.gridTile = { tx: Number.NaN, tz: Number.NaN };
    this.overlayTile = { tx: Number.NaN, tz: Number.NaN };
  }

  /**
   * How high the grid and the outline sit at a point.
   *
   * On the storey being built on: the floor there if one has been laid, and
   * otherwise the ground carried up to that storey, which is what a floor laid
   * there would sit on.
   */
  private buildingHeightAt(x: number, z: number): number {
    if (this.buildStorey === 0) return this.heightAt(x, z);
    return this.surfaceAt(x, z, this.buildStorey) + this.buildStorey * STOREY_HEIGHT;
  }

  private rebuildGrid(centre: { tx: number; tz: number }): void {
    const half = GRID_PATCH_TILES / 2;
    const minX = centre.tx - half;
    const minZ = centre.tz - half;
    const lines: number[] = [];

    // Each grid line is drawn as a run of short segments, because the ground beneath
    // it is not flat and a single segment would sink into every rise it crosses.
    for (let step = 0; step <= GRID_PATCH_TILES; step++) {
      const x = minX + step;
      const z = minZ + step;

      for (let along = 0; along < GRID_PATCH_TILES; along++) {
        const z0 = minZ + along;
        const z1 = z0 + 1;
        lines.push(
          x,
          this.buildingHeightAt(x, z0) + GRID_LIFT,
          z0,
          x,
          this.buildingHeightAt(x, z1) + GRID_LIFT,
          z1,
        );

        const x0 = minX + along;
        const x1 = x0 + 1;
        lines.push(
          x0,
          this.buildingHeightAt(x0, z) + GRID_LIFT,
          z,
          x1,
          this.buildingHeightAt(x1, z) + GRID_LIFT,
          z,
        );
      }
    }

    this.grid.geometry.dispose();
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(lines, 3));
    this.grid.geometry = geometry;
  }

  /**
   * The height to trace an outline at, sampled just inside the shape.
   *
   * A point on a tile's far edge belongs to the next tile along as far as any
   * lookup by whole numbers is concerned, so the far corners of a dock read the
   * height of the water beside it and half the outline fell into the sea.
   * Nudged towards the middle of what is being drawn, every sample lands on the
   * tile actually being outlined.
   */
  private outlineAt(x: number, z: number, midX: number, midZ: number, plane = 0): number {
    const nudge = 0.02;

    return this.surfaceAt(x + Math.sign(midX - x) * nudge, z + Math.sign(midZ - z) * nudge, plane);
  }

  private placeTileMarker(tile: { tx: number; tz: number }): void {
    const { tx, tz } = tile;
    // Traced as a loop of the tile's four corners, each sampled off the surface,
    // so the outline sits on sloped ground, and on the boards when there are
    // boards, instead of hovering over one or sinking through the other.
    const corners = [
      [tx, tz],
      [tx + 1, tz],
      [tx + 1, tz + 1],
      [tx, tz + 1],
      [tx, tz],
    ] as const;

    const points: number[] = [];
    for (const corner of corners) {
      const x = corner[0];
      const z = corner[1];
      const height =
        this.buildStorey === 0
          ? this.outlineAt(x, z, tx + 0.5, tz + 0.5)
          : this.buildingHeightAt(x, z);
      points.push(x, height + GRID_LIFT * 2, z);
    }

    this.tileMarker.geometry.dispose();
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    this.tileMarker.geometry = geometry;
  }

  /**
   * Draws a border around whatever the pointer is over.
   *
   * Traced off the surface a corner at a time, like every other overlay, so it
   * follows sloped ground and sits on a floor when there is one laid.
   */
  showHover(
    tile: { tx: number; tz: number } | null,
    onSomething: boolean,
    brush = 1,
    erasing = false,
  ): void {
    if (!tile) {
      this.hoverMarker.visible = false;
      return;
    }

    // A brush wider than a tile outlines what it would actually cover, because a
    // one tile marker under a nine tile stroke is a lie about where it lands.
    const reach = Math.floor(Math.max(1, brush) / 2);
    const minX = tile.tx - reach;
    const minZ = tile.tz - reach;
    const maxX = tile.tx + reach + 1;
    const maxZ = tile.tz + reach + 1;

    // Followed a tile at a time along each side, so a wide outline over a hill
    // sits on the ground rather than cutting through it.
    const midX = (minX + maxX) / 2;
    const midZ = (minZ + maxZ) / 2;

    const points: number[] = [];
    const edge = (x0: number, z0: number, x1: number, z1: number): void => {
      const steps = Math.max(1, Math.abs(x1 - x0) + Math.abs(z1 - z0));
      for (let step = 0; step <= steps; step++) {
        const x = x0 + ((x1 - x0) * step) / steps;
        const z = z0 + ((z1 - z0) * step) / steps;
        points.push(x, this.outlineAt(x, z, midX, midZ) + GRID_LIFT * 2, z);
      }
    };

    edge(minX, minZ, maxX, minZ);
    edge(maxX, minZ, maxX, maxZ);
    edge(maxX, maxZ, minX, maxZ);
    edge(minX, maxZ, minX, minZ);

    this.hoverMarker.geometry.dispose();
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    this.hoverMarker.geometry = geometry;

    // Red while the eraser is held, since a click there takes something away and
    // the outline is the last thing anybody sees before it does.
    const material = this.hoverMarker.material as THREE.LineBasicMaterial;
    material.color.set(erasing ? 0xe08a7a : onSomething ? 0xa8f0f7 : 0xd6e2ea);
    material.opacity = erasing || onSomething ? 0.95 : 0.5;

    this.hoverMarker.visible = true;
  }

  /**
   * Applies a change somebody made to the world.
   *
   * The terrain and the scenery are both rebuilt rather than patched in place.
   * A region is one mesh and one instanced draw per shape, so rebuilding costs a
   * few milliseconds and happens when a person edits something; patching the
   * buffers in place would be faster and would be wrong the first time two edits
   * arrived in the same frame.
   */
  applyPatch(patch: WorldPatchPacket): void {
    const region = this.region;
    if (!region) return;

    const localX = patch.tx - region.originX;
    const localZ = patch.tz - region.originZ;

    switch (patch.kind) {
      // What is standing where is the world's to decide, and it has already
      // decided by the time this runs. Redrawing is all that is left.
      case PatchKind.ObjectAdded:
      case PatchKind.ObjectRemoved:
        this.buildObjects(region);
        break;

      case PatchKind.TileChanged: {
        const tileIndex = localZ * region.width + localX;
        region.flags[tileIndex] = patch.value;
        if (patch.ground !== undefined) region.ground[tileIndex] = patch.ground;
        if (patch.water !== undefined) region.water[tileIndex] = patch.water;

        // A tile turning wet or dry moves the coastline, and the waves follow
        // the coastline, so the map of it is drawn again.
        this.forgetShoreMap();

        const stride = region.width + 1;
        const corners = [
          [0, 0],
          [1, 0],
          [0, 1],
          [1, 1],
        ] as const;

        patch.heights.forEach((height, index) => {
          const [dx = 0, dz = 0] = corners[index] ?? [0, 0];
          const corner = (localZ + dz) * stride + (localX + dx);

          // Everything standing on the ground sits at the height of the ground,
          // so scenery has to be placed again when the ground moves under it.
          // Painting does not move it, and painting is most of what happens.
          if (region.heights[corner] !== height) this.groundMoved = true;
          region.heights[corner] = height;
        });

        // Painting an area arrives as one patch per tile. Redrawing for each of
        // them would rebuild the same ground hundreds of times in a frame, so the
        // squares that changed are noted and the work is left until the next one.
        //
        // A tile on the edge of a square changes how its neighbour blends and how
        // its neighbour is lit, so the squares around it are noted too. Only the
        // ones that exist end up being drawn.
        for (let dz = -1; dz <= 1; dz++) {
          for (let dx = -1; dx <= 1; dx++) {
            this.terrainDirty.add(pieceOf(localX + dx, localZ + dz));
          }
        }
        break;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Ground items
  // -------------------------------------------------------------------------

  /**
   * Puts a pile on a tile.
   *
   * Placeholder art, coloured from the item id the same way the inventory icon is,
   * so a dropped item is recognisable as the thing that left the bag.
   */
  addGroundItem(pile: {
    id: number;
    itemId: number;
    tx: number;
    tz: number;
    /** How high it lies, for a pile somebody left on a table rather than dropped. */
    lift?: number;
  }): void {
    this.removeGroundItem(pile.id);

    const colour = new THREE.Color();
    colour.setHSL(((pile.itemId * 47) % 360) / 360, 0.38, 0.42);

    const mesh = new THREE.Mesh(
      this.groundGeometry,
      new THREE.MeshLambertMaterial({ color: colour }),
    );

    const x = pile.tx + 0.5;
    const z = pile.tz + 0.5;

    /*
     * On whatever is underfoot, which on a dock is the dock. Placed against the
     * bare ground, anything dropped while standing on boards fell through them
     * and lay on the sea floor.
     *
     * A pile somebody left carries its own height on top of that, which is what
     * puts it on the table it was meant to be on rather than on the floor
     * underneath it.
     */
    mesh.position.set(x, this.surfaceAt(x, z) + 0.08 + (pile.lift ?? 0), z);
    mesh.rotation.y = ((pile.itemId * 37) % 90) * (Math.PI / 180);

    this.groundItems.set(pile.id, mesh);
    this.scene.add(mesh);
  }

  removeGroundItem(id: number): void {
    const mesh = this.groundItems.get(id);
    if (!mesh) return;

    this.scene.remove(mesh);
    // The geometry is shared between every pile, so only the material is disposed.
    (mesh.material as THREE.Material).dispose();
    this.groundItems.delete(id);
  }

  clearGroundItems(): void {
    for (const id of [...this.groundItems.keys()]) this.removeGroundItem(id);
  }

  /** Every pile under a screen point, nearest first. */
  pickGroundItems(screenX: number, screenY: number): { id: number; distance: number }[] {
    this.raycaster.setFromCamera(this.pointerAt(screenX, screenY), this.camera);

    const hits: { id: number; distance: number }[] = [];
    for (const [id, mesh] of this.groundItems) {
      const hit = this.raycaster.intersectObject(mesh, false)[0];
      if (hit) hits.push({ id, distance: hit.distance });
    }

    return hits.sort((a, b) => a.distance - b.distance);
  }

  // -------------------------------------------------------------------------
  // Picking
  // -------------------------------------------------------------------------

  private pointerAt(screenX: number, screenY: number): THREE.Vector2 {
    // Measured inside the canvas, not inside the window: a click has to land on
    // the tile under it whether the world fills the screen or sits in a panel
    // with the interface docked round it.
    const box = this.renderer.domElement.getBoundingClientRect();
    const width = box.width || window.innerWidth;
    const height = box.height || window.innerHeight;

    return new THREE.Vector2(
      ((screenX - box.left) / width) * 2 - 1,
      -((screenY - box.top) / height) * 2 + 1,
    );
  }

  /**
   * Turns a screen point into a ground tile, or null when the ray misses.
   *
   * Walked along the ray rather than tested against the ground mesh. The ground
   * is a hundred thousand triangles and a ray test walks every one of them,
   * which is a third of a frame spent working out which tile the pointer is
   * over; this samples the heights the ground is built from, a few dozen times,
   * and lands on the same answer.
   *
   * What it samples is the surface, boards included, not the bare terrain. On
   * land the two are the same. Over water they are not: a dock stands well
   * above the bed under it, so a ray aimed at the boards used to sail over them
   * and come down on the sea floor somewhere past the end of the jetty, which
   * is why a dock in deep water could not be pointed at or walked onto.
   */
  pickGround(
    screenX: number,
    screenY: number,
  ): { tx: number; tz: number; x: number; z: number; distance: number } | null {
    if (!this.region) return null;

    this.raycaster.setFromCamera(this.pointerAt(screenX, screenY), this.camera);
    const { origin, direction } = this.raycaster.ray;

    // Nothing is worth looking at beyond the fog.
    const reach = Math.min(this.camera.far, 320);
    const step = 0.35;

    const point = new THREE.Vector3();
    let last = 0;

    for (let along = step; along <= reach; along += step) {
      point.copy(origin).addScaledVector(direction, along);
      if (point.y - this.surfaceAt(point.x, point.z) > 0) {
        last = along;
        continue;
      }

      // Somewhere between the last sample and this one. A handful of halvings
      // puts it on the surface closely enough for a tile and for a marker.
      let above = last;
      let below = along;
      for (let tries = 0; tries < 12; tries++) {
        const middle = (above + below) / 2;
        point.copy(origin).addScaledVector(direction, middle);

        if (point.y - this.surfaceAt(point.x, point.z) > 0) above = middle;
        else below = middle;
      }

      point.copy(origin).addScaledVector(direction, below);

      return {
        x: point.x,
        z: point.z,
        tx: Math.floor(point.x),
        tz: Math.floor(point.z),
        distance: below,
      };
    }

    // Never came down to it: the pointer is on the sky, or out past the island.
    return null;
  }

  /** Scenery under a screen point, as a definition id and the tile it stands on. */
  pickObject(
    screenX: number,
    screenY: number,
    /**
     * Whether this one is worth stopping at.
     *
     * A wall in front of a door is scenery to look through rather than
     * something to click, and only the caller knows which is which. Said no to,
     * the search carries on to whatever stands behind it.
     */
    wanted?: (id: number) => boolean,
  ): {
    id: number;
    tx: number;
    tz: number;
    rotation: number;
    distance: number;
    /** Which of the instances it was, for anything that wants to light one up. */
    index: number;
    /** What was done to this one: its size, and the height it was built to. */
    scale: number;
    lift?: number;
  } | null {
    this.raycaster.setFromCamera(this.pointerAt(screenX, screenY), this.camera);

    let closest: {
      id: number;
      tx: number;
      tz: number;
      rotation: number;
      distance: number;
      index: number;
      scale: number;
      lift?: number;
    } | null = null;

    for (const entry of this.objectMeshes) {
      if (wanted && !wanted(entry.id)) continue;
      // Nothing that is not being drawn. A roof taken away so somebody can see
      // into a house is a roof they should be able to click through as well.
      if (!entry.mesh.visible) continue;

      for (const hit of this.raycaster.intersectObject(entry.mesh, false)) {
        const placement = entry.placements[hit.instanceId ?? -1];
        if (!placement) continue;
        if (!closest || hit.distance < closest.distance) {
          closest = {
            id: entry.id,
            tx: placement.tx,
            tz: placement.tz,
            rotation: placement.rotation,
            distance: hit.distance,
            index: hit.instanceId ?? 0,
            scale: placement.scale,
            lift: placement.lift,
          };
        }
        break;
      }
    }

    return closest;
  }

  /**
   * Which instance of a kind of scenery stands on a tile.
   *
   * For anything picked by its tile rather than by its shape: the pick knows
   * what is there but not which of the thousand copies it is, and the outline
   * needs the copy.
   */
  instanceOn(objectId: number, tx: number, tz: number): number | null {
    for (const entry of this.objectMeshes) {
      if (entry.id !== objectId || entry.unseen) continue;

      const at = entry.placements.findIndex(
        (placement) => placement.tx === tx && placement.tz === tz,
      );
      if (at >= 0) return at;
    }

    return null;
  }

  /** Every entity under a screen point, nearest first. */
  pickEntities(screenX: number, screenY: number): { id: number; distance: number }[] {
    this.raycaster.setFromCamera(this.pointerAt(screenX, screenY), this.camera);

    const hits: { id: number; distance: number }[] = [];
    for (const [id, view] of this.entityViews) {
      // Whatever they are actually made of: the model once it has come down, and
      // the stand-in shape until then. Picking against a skeleton costs more than
      // picking against a capsule, and is the only honest answer once the capsule
      // is no longer what is standing there.
      const against = view.body.parent ? view.body : (view.avatar?.group ?? null);
      if (!against) continue;

      const hit = this.raycaster.intersectObject(against, true)[0];
      if (hit) hits.push({ id, distance: hit.distance });
    }

    return hits.sort((a, b) => a.distance - b.distance);
  }

  /** How far the ripples have run, which is one clock for all of them. */
  get rippleClock(): number {
    return RIPPLE_CLOCK.value;
  }

  /**
   * Which square waiting to be drawn is nearest the reader.
   *
   * Only one is drawn a frame, so which one matters: the ground somebody is
   * about to walk onto should appear before the ground behind them.
   */
  private nearestDirty(region: LocalRegion, waiting: Set<string>): string | null {
    let best: string | null = null;
    let closest = Infinity;

    const here = {
      tx: this.focusTile.tx - region.originX,
      tz: this.focusTile.tz - region.originZ,
    };

    for (const key of waiting) {
      const [cx = 0, cz = 0] = key.split('.').map(Number);
      const from = Math.hypot(
        cx * PIECE_TILES + PIECE_TILES / 2 - here.tx,
        cz * PIECE_TILES + PIECE_TILES / 2 - here.tz,
      );

      if (from < closest) {
        closest = from;
        best = key;
      }
    }

    return best;
  }

  /**
   * Whether a square of ground has been built into a mesh yet.
   *
   * Asked in the region's own tiles. Ground that has arrived is not ground that
   * is drawn: at most one square is built in a frame, so somebody arriving
   * somewhere new is standing on a hole for a moment and should be told so
   * rather than shown it.
   */
  hasTerrain(tx: number, tz: number): boolean {
    return this.terrain.has(`${Math.floor(tx / PIECE_TILES)}.${Math.floor(tz / PIECE_TILES)}`);
  }

  render(): void {
    // At most one square of ground and one of water are built in a frame.
    //
    // Building all of them at once is what dropped a frame every few paces: a
    // square of terrain is forty milliseconds and a square of water more, and
    // walking into new ground asks for several at a time. Spread out, the work
    // is the same and nobody sees it happen.
    if (this.terrainDirty.size > 0 && this.region) {
      const region = this.region;
      const nearest = this.nearestDirty(region, this.terrainDirty);
      const wanted = nearest === null ? new Set<string>() : new Set([nearest]);
      for (const key of wanted) this.terrainDirty.delete(key);

      for (const piece of piecesOf(region)) {
        if (!wanted.has(piece.key)) continue;
        // A square that has not arrived is not drawn as flat ground at nought;
        // it is not drawn at all, and the sea or the fog stands in for it.
        if (!region.squares.has(`${piece.tx}.${piece.tz}`)) continue;

        this.buildPiece(region, piece);
      }

      // The water and the overlays are sampled off the ground, so they follow it.
      for (const key of wanted) if (this.waters.has(key)) this.waterDirty.add(key);
      this.overlayBuilt = false;

      if (this.groundMoved) {
        this.groundMoved = false;
        this.buildObjects(region);
      }
    }

    if (this.waterDirty.size > 0 && this.region) {
      const key = this.nearestDirty(this.region, this.waterDirty);
      if (key !== null) {
        this.waterDirty.delete(key);
        this.buildWaterPiece(this.region, key);
      }
    }

    const now = performance.now();
    // Clamped, so a tab that was in the background does not come back with every
    // animation half a minute further on.
    const delta = Math.min(0.1, (now - this.lastDrawn) / 1000);
    this.lastDrawn = now;
    this.stepAvatars(delta);
    this.stepHits();
    this.stepFlights();
    this.stepQuestMarks(delta);
    this.stepMarkers(delta);

    // Everything that ripples reads the same clock, so a shore of fishing spots
    // is a shore rather than a row of identical pulses.
    RIPPLE_CLOCK.value += delta;

    this.stepLamps(delta);

    // The same sun the rest of the world is lit by, so the glint off the water
    // is where somebody would expect it. Its direction moves with the time of
    // day, so it has to be handed over rather than baked in.
    // The backdrop is cut away around whoever is reading it, so it has to be
    // told where they are.
    if (this.farNear) {
      const at = this.localView?.group.position ?? this.camera.position;
      this.farNear.set(at.x, at.y, at.z);
    }

    const wet = [...this.waters.values(), this.sea];
    stepWater(wet, delta);
    aimWater(wet, this.sun.position, this.sun.intensity / 1.9);
    this.keepShoreMap(wet);

    // Straight to the canvas, which is the only path there is: the stroke is
    // part of the picture rather than something done to it afterwards, so
    // hovering cannot change how anything else is drawn.
    this.renderer.render(this.scene, this.camera);
  }
}

// ---------------------------------------------------------------------------
// Placeholder shapes
// ---------------------------------------------------------------------------

export interface ShapePart {
  geometry: () => THREE.BufferGeometry;
  material: () => THREE.Material;
  /**
   * A real light this part throws, and how far it reaches in tiles.
   *
   * Separate from the glow, which only makes the part itself bright. A hearth
   * needs both: the embers are their own colour and the room they stand in is
   * lit by them.
   */
  casts?: number;
  /** What colour that light is, and whether it burns, cycles, or waits for dusk. */
  lightColour?: number;
  lightAt?: 'always' | 'night';
  flickers?: boolean;
  shifts?: boolean;
  /** Vertical offset of the shape's centre, in units of its own scale. */
  offset: number;
  /** And where it sits across the tile, in the same units. Zero is the middle. */
  across?: number;
  along?: number;
  /**
   * How far this part was pushed out to the edge of its tile when it was built.
   *
   * Only walls have it, and it is here so a wall can be taken off its edge
   * again: placed corner to corner it belongs across the middle of the tile,
   * not along one side of it.
   */
  edged?: number;
  scale: number;
  /**
   * Drawn exactly where it was placed, with no jitter in size or angle.
   *
   * Right for anything built: a wall that wanders half a degree from its
   * neighbour reads as a mistake, where a forest of identical trees reads as one.
   */
  aligned?: boolean;
  /**
   * Stretched down until it meets the ground below.
   *
   * A dock support is a post of no particular length: how long it is depends on
   * how far the boards are above the sea bed, which is known where it is drawn
   * and nowhere else.
   */
  spans?: boolean;
  /**
   * There to be clicked on, and nothing else.
   *
   * A fishing spot is a set of ripples over a plate that covers the whole tile,
   * so anywhere on the water can be clicked. The plate is not the spot, though,
   * and a stroke round it is a stroke round a square of water.
   */
  unseen?: boolean;
}

/** Geometry moved onto the north edge of a tile, which rotation then carries round. */
function onEdge(geometry: THREE.BufferGeometry, depth: number): THREE.BufferGeometry {
  geometry.translate(0, 0, 0.5 - depth / 2);
  return geometry;
}

/** How far out `onEdge` pushes something of a given depth. */
function edgedBy(depth: number): number {
  return 0.5 - depth / 2;
}

/**
 * How tall a wall stands, and where its top is.
 *
 * Written down once, because a roof has to sit on it. A wall a little taller
 * than a person is what a room feels like; the roof goes on the top of that and
 * not at some other number that was chosen separately.
 */
const WALL_HIGH = 1.9;
const WALL_TOP = WALL_HIGH;

const STONE = (): THREE.Material =>
  new THREE.MeshLambertMaterial({ color: 0x7d7468, flatShading: true });
const TIMBER = (): THREE.Material =>
  new THREE.MeshLambertMaterial({ color: 0x6b5136, flatShading: true });

/**
 * Something to see through: a pane, a lantern's sides, the lip of a wave.
 *
 * Faintly tinted rather than clear, so the opening still reads as glazed, and
 * it writes no depth, because glass that hides what is behind it from
 * everything drawn afterwards is a wall with a shine on it.
 */
const GLASS = (): THREE.Material =>
  new THREE.MeshLambertMaterial({
    color: 0xcfe2ec,
    transparent: true,
    opacity: 0.22,
    depthWrite: false,
    flatShading: true,
  });

/**
 * The parts one kind of scenery is drawn from.
 *
 * Handed out so the browser can draw the same shapes the world does, rather than
 * keeping a second set of pictures that drift away from what is actually there.
 */
export function shapeParts(objectId: number): ShapePart[] {
  return builtShapes.get(objectId) ?? PLACEHOLDER_SHAPES[objectId] ?? PLACEHOLDER_SHAPES.default;
}

/** Shapes built in the tools, by the id of the thing they belong to. */
const builtShapes = new Map<number, ShapePart[]>();

/**
 * Whether anything in the world has a light that comes on at dusk.
 *
 * Counted so the clock can skip walking the scene entirely in a world where
 * nothing does, which is every world before this and most of them after.
 */
let litByNight = 0;

/**
 * How light it was the last time the clock said anything.
 *
 * Read when a material is made so scenery that arrives at midnight arrives lit,
 * rather than dark until the next time the sky changes enough to be worth
 * redrawing.
 */
let daylightForGlows = 1;

/**
 * Turns what the tools wrote into something that can be drawn.
 *
 * A handful of primitives with a size, a colour and a place to sit. Kept here
 * beside the hand-written shapes so both kinds are drawn by exactly the same
 * code, which is what stops a thing made in the tools looking like a guest.
 */
export function setBuiltShapes(written: Iterable<[number, string]>): void {
  builtShapes.clear();

  for (const [id, text] of written) {
    if (text.length === 0) continue;

    try {
      const parsed = JSON.parse(text) as {
        shape: string;
        colour: string;
        width: number;
        height: number;
        depth: number;
        offset: number;
        across?: number;
        along?: number;
        turn: number;
        lean?: number;
        leanTo?: number;
        glow?: number;
        glowsAt?: string;
        opacity?: number;
        casts?: number;
        flickers?: boolean;
        shifts?: boolean;
        sways?: number;
        turns?: number;
        turnsOn?: string;
        bobs?: number;
      }[];

      // Twelve, which is what the schema allows. It was eight here, so anything
      // authored with more quietly lost its last few parts: a fire came out as a
      // ring of stones with no fire in it.
      const parts: ShapePart[] = parsed.slice(0, 12).map((part) => ({
        geometry: () => {
          const geometry =
            part.shape === 'cylinder'
              ? new THREE.CylinderGeometry(part.width / 2, part.width / 2, part.height, 10)
              : part.shape === 'cone'
                ? new THREE.ConeGeometry(part.width / 2, part.height, 10)
                : part.shape === 'sphere'
                  ? new THREE.SphereGeometry(part.width / 2, 12, 8)
                  : new THREE.BoxGeometry(part.width, part.height, part.depth);

          /*
           * Tilted at the foot rather than through the middle.
           *
           * A shape is built standing on its own centre, so turning it where
           * it is swings the foot out from under it and leaves a reed hovering
           * beside the mud it grows in. Lifting it so the foot sits at the
           * origin, turning it there and putting it back leaves the foot where
           * it was and swings the tip, which is what leaning is.
           */
          const lean = part.lean ?? 0;
          if (lean !== 0) {
            geometry.translate(0, part.height / 2, 0);
            geometry.rotateZ((lean * Math.PI) / 180);
            geometry.rotateY(((part.leanTo ?? 0) * Math.PI) / 180);
            geometry.translate(0, -part.height / 2, 0);
          }

          if (part.turn > 0) geometry.rotateY((part.turn * Math.PI) / 2);
          return geometry;
        },
        material: () => {
          const colour = Number.parseInt(part.colour, 16);
          const glow = Math.min(1, Math.max(0, part.glow ?? 0));

          /*
           * Its own light, mixed in on top of the sun's.
           *
           * Emissive rather than a brighter colour, because the problem is a
           * face the sun never reaches: a lighter grey in shadow is still a
           * grey in shadow. The same colour is used for the glow as for the
           * surface, so a part lights up as itself rather than washing out.
           */
          const clear = Math.min(1, Math.max(0.05, part.opacity ?? 1));

          const material = new THREE.MeshLambertMaterial({
            color: colour,
            emissive: glow > 0 ? colour : 0x000000,
            emissiveIntensity: glow,
            flatShading: true,
            transparent: clear < 1,
            opacity: clear,
            // Glass that writes depth hides whatever is behind it from
            // everything drawn afterwards, which for a window is the room.
            depthWrite: clear >= 1,
          });

          // Whatever it was told to do: burn, lean on the wind, turn, or
          // float. One motion for all of them, on the same clock everything
          // else that moves reads.
          const stir = motionFor(part);
          if (stir) moves(material, stir);

          /*
           * A light that comes on at dusk, marked so the clock can find it.
           *
           * Kept on the material rather than in a list of its own, because the
           * materials are made one per batch of scenery and thrown away when
           * the ground around somebody changes. A list would have to be kept in
           * step with that; the scene already is.
           */
          if (glow > 0 && part.glowsAt === 'night') {
            material.userData.nightGlow = glow;
            material.emissiveIntensity = glow * (1 - daylightForGlows);
            litByNight++;
          }

          return material;
        },
        offset: part.offset,
        across: part.across ?? 0,
        along: part.along ?? 0,
        scale: 1,
        aligned: true,
        casts: part.casts && part.casts > 0 ? part.casts : undefined,
        lightColour: Number.parseInt(part.colour, 16),
        lightAt: part.glowsAt === 'night' ? 'night' : 'always',
        flickers: part.flickers === true,
        shifts: part.shifts === true,
      }));

      if (parts.length > 0) builtShapes.set(id, parts);
    } catch {
      // Written by an older build, or by hand. Drawn with the usual shape.
    }
  }
}

/** The kind number the ground carries where it is under snow. */
const SNOW_GROUND = 6;

/**
 * Puts snow on whatever faces the sky, for scenery standing in snow.
 *
 * A winter wood drawn from the same trees as a summer one is a summer wood on a
 * white floor: the ground says snow and every branch above it says July. Snow
 * does not fall on a tree evenly, though, it settles on what is pointing up, so
 * that is what this does. The upward part of each face, in the world rather
 * than in the model, decides how much white is laid over the colour it already
 * has, and the colour underneath is kept for the sides, which is why a trunk
 * stays a trunk.
 *
 * It follows the light rather than replacing it: the white is scaled by how lit
 * the pixel already was, so a snowy wood at midnight is a dark wood rather than
 * a field of glowing sugar.
 *
 * One flag per instance, so a wood that straddles a snow line has snow on the
 * half of it that is standing in snow and none on the half that is not.
 */
export function underSnow(material: THREE.Material): THREE.Material {
  /*
   * Whatever the part was already told to do, carried through.
   *
   * This used to replace the material's onBeforeCompile outright, which threw
   * away every motion a part carried: flames stood still, sails did not turn
   * and reeds did not move, silently, because the shader compiled and drew
   * perfectly well without them. Everything scenery is drawn with comes through
   * here, so this is where the two have to be put together.
   */
  const motion = motionOf(material);

  material.onBeforeCompile = (shader) => {
    if (motion) {
      Object.assign(shader.uniforms, motion.uniforms);

      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\n${motion.shared}`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>\n${motion.vertex}`);

      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>\n${motion.shared}`)
        .replace(
          '#include <dithering_fragment>',
          `#include <dithering_fragment>\n${motion.fragment}`,
        );
    }

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute float aSnow;
varying float vSnowUp;`,
      )
      .replace(
        '#include <beginnormal_vertex>',
        `#include <beginnormal_vertex>

// How much this face looks at the sky, after the instance has been turned and
// stood where it stands. Nought on anything vertical, one on anything flat.
vec3 facingUp = normalize(mat3(instanceMatrix) * objectNormal);
vSnowUp = aSnow * smoothstep(0.32, 0.78, facingUp.y);`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying float vSnowUp;`,
      )
      /*
       * Mixed into the colour rather than over the picture.
       *
       * Laid over the finished pixel, snow has to invent its own brightness,
       * and whatever it invents is wrong twice a day: bright enough to read at
       * noon and it glows at midnight. Mixed into the surface colour before the
       * light is applied, it is lit by the same sun as the snow on the ground
       * beside it, which is the whole of what makes it look like the same snow.
       */
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>

if (vSnowUp > 0.004) {
  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.94, 0.96, 1.0), vSnowUp * 0.92);
}`,
      );
  };

  // Two materials with the same program but different code have to be told
  // apart, or three keeps the first one it compiled and every tree in the world
  // is snowed. The motion is part of what makes them different: without it in
  // the key, the first flame compiled was the shader every other moving thing
  // in the world got.
  const key = `scenery-under-snow:${motion?.id ?? 'still'}`;
  material.customProgramCacheKey = () => key;
  return material;
}

/** A shape laid down on the water rather than standing up out of it. */
function flat(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}

/**
 * A shape that moves in the shader rather than by anything moving it.
 *
 * Fishing spots are drawn a hundred at a time out of one instanced mesh, so
 * there is nothing to move: the ripples travel and the specks rise inside the
 * vertex shader. Written down as snippets rather than buried in one material,
 * because the stroke drawn around a spot has to move exactly the same way, and
 * a second copy of the sums is a second copy that can drift.
 */
export interface ShapeMotion {
  /** Names it, so materials built from it can be kept rather than rebuilt. */
  id: string;
  /** Declarations both shaders need. */
  shared: string;
  /** Runs after the position is worked out, and may change `transformed`. */
  vertex: string;
  /** Runs at the end of the fragment shader, and may change the alpha. */
  fragment: string;
  uniforms: Record<string, THREE.IUniform>;
}

/** Puts a motion into a material, and remembers it there for the stroke to find. */
function moves(material: THREE.Material, motion: ShapeMotion): THREE.Material {
  material.userData.motion = motion;

  // Named, so two motions written into the same kind of material are two
  // programs rather than whichever of them three happened to compile first.
  material.customProgramCacheKey = () => `moves:${motion.id}`;

  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, motion.uniforms);

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${motion.shared}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${motion.vertex}`);

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${motion.shared}`)
      .replace(
        '#include <dithering_fragment>',
        `#include <dithering_fragment>\n${motion.fragment}`,
      );
  };

  return material;
}

/** Whatever a material moves by, if it moves at all. */
export function motionOf(material: THREE.Material | THREE.Material[]): ShapeMotion | null {
  const held = Array.isArray(material) ? material[0] : material;
  const motion = held?.userData.motion as ShapeMotion | undefined;

  return motion ?? null;
}

/**
 * A ring of broken water, travelling out from the middle and fading as it goes.
 *
 * The offset staggers the three rings so one is always going out.
 */
const RIPPLE = (opacity: number, offset: number): THREE.Material => {
  const material = new THREE.MeshBasicMaterial({
    color: 0xdff0f6,
    transparent: true,
    opacity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  return moves(material, {
    id: `ripple:${offset.toFixed(2)}`,
    shared: `uniform float uTime;
varying float vRipple;`,
    vertex: `// How far through its life this ring is, from the middle out.
vRipple = fract(uTime * 0.45 + ${offset.toFixed(2)});

// Out from the centre of the tile, growing as it goes.
transformed.xz *= 0.35 + vRipple * 2.6;`,
    fragment: `// Brightest as it leaves, gone by the time it reaches the edge of the tile.
gl_FragColor.a *= (1.0 - vRipple) * (1.0 - vRipple);`,
    uniforms: { uTime: RIPPLE_CLOCK },
  });
};

/** A speck rising to the surface and starting again from the bottom. */
const BUBBLE = (offset: number): THREE.Material => {
  const material = new THREE.MeshBasicMaterial({
    color: 0xeaf7fb,
    transparent: true,
    opacity: 0.75,
    depthWrite: false,
  });

  return moves(material, {
    id: `bubble:${offset.toFixed(2)}`,
    shared: `uniform float uTime;
varying float vRipple;`,
    vertex: `vRipple = fract(uTime * 0.35 + ${offset.toFixed(2)});
transformed.y += vRipple * 0.35 - 0.28;`,
    fragment: `// Faint at the bottom, bright at the top, gone as it breaks the surface.
gl_FragColor.a *= smoothstep(0.0, 0.3, vRipple) * (1.0 - smoothstep(0.75, 1.0, vRipple));`,
    uniforms: { uTime: RIPPLE_CLOCK },
  });
};

/**
 * How many real lights the scene keeps, and how many places it will remember.
 *
 * Six because that is about what a browser will carry without the shading
 * falling over on a laptop, and because the seventh nearest light in a town is
 * never the one somebody is looking at. The cap on places remembered is there
 * so a city of lamps cannot turn one frame into a sort of ten thousand things.
 */
const LIGHTS_TO_GO_ROUND = 6;
const MOST_LAMPS_KNOWN = 2048;

/** How often the few real lights look again at what is nearest, in seconds. */
const LAMPS_PICKED_EVERY = 0.4;

/** One clock for everything that ripples, so no two spots beat against each other. */
const RIPPLE_CLOCK = { value: 0 };

/**
 * Everything a part was told to do, assembled into one motion.
 *
 * One rather than several, because a material carries a single compiled shader:
 * a banner that both sways and flickers has to be told both in the same breath
 * or the second one replaces the first. So whatever the author asked for is
 * gathered here and written out together.
 *
 * Every one of them is seeded off where the thing stands, so a row of torches
 * is a row of separate fires, a wood sways as a wood rather than as one tree
 * drawn a thousand times, and two signs on the same street are not in step.
 */
function motionFor(
  part: {
    colour: string;
    height: number;
    flickers?: boolean;
    sways?: number;
    turns?: number;
    turnsOn?: string;
    bobs?: number;
  },
): ShapeMotion | null {
  const flickers = part.flickers === true;
  const sways = Math.max(0, part.sways ?? 0);
  const turns = part.turns ?? 0;
  const bobs = Math.max(0, part.bobs ?? 0);

  if (!flickers && sways === 0 && turns === 0 && bobs === 0) return null;

  const tint = new THREE.Color(Number.parseInt(part.colour, 16));
  const num = (value: number): string => value.toFixed(4);

  const vertex: string[] = [
    `// Where this one stands, so no two of them move together.
float vSeed = fract(dot(vec3(instanceMatrix[3].xyz), vec3(0.137, 0.0, 0.631)));`,
  ];
  const fragment: string[] = [];
  const shared = 'uniform float uTime;\nvarying float vLick;';

  if (turns !== 0) {
    // Turns a minute into radians a second.
    const rate = (turns * Math.PI * 2) / 60;

    // Which pair of axes it sweeps round: upright is a sign on a post, the
    // other two lay the same wheel on its side.
    const pair =
      part.turnsOn === 'across' ? 'xy' : part.turnsOn === 'along' ? 'zy' : 'xz';

    vertex.push(`// Round and round, from wherever it happened to be facing.
float vTurn = uTime * ${num(rate)} + vSeed * 6.283;
float vCos = cos(vTurn);
float vSin = sin(vTurn);
transformed.${pair} = vec2(
  transformed.${pair[0]} * vCos - transformed.${pair[1]} * vSin,
  transformed.${pair[0]} * vSin + transformed.${pair[1]} * vCos
);`);
  }

  if (sways > 0) {
    /*
     * Pivoting at the foot, like the lean a part can be built with: a reed
     * bends out of the mud rather than sliding across it. Two waves again, so
     * the wind has gusts in it rather than a beat.
     */
    vertex.push(`float vFoot = max(0.0, transformed.y + ${num(part.height / 2)});
float vGust = sin(uTime * 0.9 + vSeed * 6.283) * 0.7 + sin(uTime * 1.47 + vSeed * 11.0) * 0.3;
transformed.x += vGust * ${num(sways)} * vFoot;
transformed.z += sin(uTime * 0.73 + vSeed * 7.1) * ${num(sways * 0.55)} * vFoot;`);
  }

  if (bobs > 0) {
    vertex.push(`transformed.y += sin(uTime * 1.1 + vSeed * 6.283) * ${num(bobs)};`);
  }

  if (flickers) {
    vertex.push(`// Two waves that do not divide into one another, so it never settles into a
// pattern anybody can count.
float vBeat = uTime * 3.1 + vSeed * 6.283;
vLick = sin(vBeat) * 0.5 + sin(vBeat * 1.7 + 1.3) * 0.3;

// Taller and thinner as it licks up, and leaning as it goes. Measured from the
// foot, so the fire stays in its fire rather than lifting off the wick.
float vUp = clamp(transformed.y + ${num(part.height / 2)}, 0.0, 4.0);
transformed.y *= 1.0 + vLick * 0.22;
transformed.xz *= 1.0 - vLick * 0.12;
transformed.x += vLick * vUp * 0.12;
transformed.z += sin(vBeat * 1.3) * vUp * 0.09;`);

    fragment.push(`// Hottest at the top of the lick, which is what makes it read as fire from
// across a street rather than as an orange cone.
gl_FragColor.rgb = mix(
  gl_FragColor.rgb,
  vec3(${tint.r.toFixed(3)}, ${tint.g.toFixed(3)}, ${tint.b.toFixed(3)}) * 1.6 + vec3(0.25),
  clamp(vLick * 0.45 + 0.25, 0.0, 1.0)
);`);
  } else {
    // The varying is declared in both shaders whether or not it is used, so
    // something has to write it or the fragment side reads whatever is there.
    vertex.push('vLick = 0.0;');
  }

  return {
    id: `stir:${part.colour}:${num(part.height)}:${flickers ? 1 : 0}:${num(sways)}:${num(turns)}:${part.turnsOn ?? 'upright'}:${num(bobs)}`,
    shared,
    vertex: vertex.join('\n'),
    fragment: fragment.join('\n'),
    uniforms: { uTime: RIPPLE_CLOCK },
  };
}

const PLACEHOLDER_SHAPES: Record<number | 'default', ShapePart[]> = {
  // Stone wall, standing on the north edge of its tile.
  20: [
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, WALL_HIGH, 0.22), 0.22),
      edged: edgedBy(0.22),
      material: STONE,
      offset: WALL_HIGH / 2,
      scale: 1,
      aligned: true,
    },
  ],

  // Timber fence: a rail with a post at each end.
  21: [
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, 0.16, 0.1), 0.1),
      edged: edgedBy(0.1),
      material: TIMBER,
      offset: 0.95,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, 0.16, 0.1), 0.1),
      edged: edgedBy(0.1),
      material: TIMBER,
      offset: 0.55,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => onEdge(new THREE.BoxGeometry(0.14, 1.15, 0.14), 0.14),
      edged: edgedBy(0.14),
      material: TIMBER,
      offset: 0.58,
      scale: 1,
      aligned: true,
    },
  ],

  // A gate, shut: the same span as a fence, filled in.
  22: [
    {
      geometry: () => onEdge(new THREE.BoxGeometry(0.94, 1.25, 0.14), 0.14),
      edged: edgedBy(0.14),
      material: () => new THREE.MeshLambertMaterial({ color: 0x8a6a42, flatShading: true }),
      offset: 0.65,
      scale: 1,
      aligned: true,
    },
  ],

  // The same gate, swung open: the leaf turned back against its post.
  23: [
    {
      geometry: () => {
        const leaf = new THREE.BoxGeometry(0.94, 1.25, 0.14);
        // Hinged at one end, so opening swings it out rather than sliding it.
        leaf.translate(0.47, 0, 0);
        leaf.rotateY(Math.PI / 2);
        leaf.translate(-0.47, 0, 0.43);
        return leaf;
      },
      material: () => new THREE.MeshLambertMaterial({ color: 0x8a6a42, flatShading: true }),
      offset: 0.65,
      scale: 1,
      aligned: true,
    },
  ],

  /*
   * Thatch roof: a panel overhead, with a little pitch to it.
   *
   * Sitting on the top of the wall, which is where a roof goes. It used to sit
   * at two and six tenths, and a wall is one and nine tenths tall: every
   * building in the world had a hand's breadth of daylight between its walls
   * and its roof, whatever ground it stood on.
   */
  25: [
    {
      geometry: () => {
        const panel = new THREE.BoxGeometry(1, 0.12, 1);
        panel.rotateX(0.12);
        return panel;
      },
      material: () => new THREE.MeshLambertMaterial({ color: 0x9a7f4a, flatShading: true }),
      offset: WALL_TOP + 0.06,
      scale: 1,
      aligned: true,
    },
  ],

  // Slate: the same shape, colder.
  26: [
    {
      geometry: () => {
        const panel = new THREE.BoxGeometry(1, 0.1, 1);
        panel.rotateX(0.12);
        return panel;
      },
      material: () => new THREE.MeshLambertMaterial({ color: 0x5d626a, flatShading: true }),
      offset: WALL_TOP + 0.05,
      scale: 1,
      aligned: true,
    },
  ],

  // Board floor, a plate laid on the ground.
  24: [
    {
      geometry: () => new THREE.BoxGeometry(1, 0.08, 1),
      material: () => new THREE.MeshLambertMaterial({ color: 0x6f5940 }),
      offset: 0.04,
      scale: 1,
      aligned: true,
    },
  ],

  // A dock support: a post under one corner of the boards, running down to
  // whatever is beneath them. How long it is depends on how deep the water is
  // there, so the post is drawn a unit long and stretched where it stands.
  32: [
    {
      geometry: () => new THREE.BoxGeometry(1, 1, 1),
      material: () => new THREE.MeshLambertMaterial({ color: 0x5a452e, flatShading: true }),
      offset: 0,
      across: 1.9,
      along: 1.9,
      scale: 0.18,
      aligned: true,
      spans: true,
    },
    // A collar where the post meets the boards, so the join reads as built
    // rather than as a stick pushed through a plank.
    {
      geometry: () => new THREE.BoxGeometry(1.5, 0.35, 1.5),
      material: () => new THREE.MeshLambertMaterial({ color: 0x6b5136, flatShading: true }),
      offset: -0.2,
      across: 1.9,
      along: 1.9,
      scale: 0.18,
      aligned: true,
    },
  ],

  // A door: a panel across the edge with a frame round it, and the same panel
  // swung back on its hinges when it is open.
  27: [
    {
      geometry: () => onEdge(new THREE.BoxGeometry(0.78, WALL_HIGH, 0.1), 0.1),
      edged: edgedBy(0.1),
      material: () => new THREE.MeshLambertMaterial({ color: 0x7a5a38, flatShading: true }),
      offset: 0.95,
      scale: 1,
      aligned: true,
    },
    // A frame, not a panel: two posts and a lintel, with the gap between them
    // left open. A solid box here read as a second door, permanently shut.
    {
      geometry: () => {
        const post = new THREE.BoxGeometry(0.11, 2, 0.18);
        post.translate(-0.445, 0, 0);
        return onEdge(post, 0.18);
      },
      material: TIMBER,
      offset: 1,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => {
        const post = new THREE.BoxGeometry(0.11, 2, 0.18);
        post.translate(0.445, 0, 0);
        return onEdge(post, 0.18);
      },
      material: TIMBER,
      offset: 1,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, 0.12, 0.18), 0.18),
      edged: edgedBy(0.18),
      material: TIMBER,
      offset: 1.94,
      scale: 1,
      aligned: true,
    },
  ],

  28: [
    {
      geometry: () => {
        const leaf = new THREE.BoxGeometry(0.78, 1.9, 0.1);
        leaf.translate(0.39, 0, 0);
        leaf.rotateY(Math.PI / 2);
        leaf.translate(-0.42, 0, 0.4);
        return leaf;
      },
      material: () => new THREE.MeshLambertMaterial({ color: 0x7a5a38, flatShading: true }),
      offset: 0.95,
      scale: 1,
      aligned: true,
    },
    // A frame, not a panel: two posts and a lintel, with the gap between them
    // left open. A solid box here read as a second door, permanently shut.
    {
      geometry: () => {
        const post = new THREE.BoxGeometry(0.11, 2, 0.18);
        post.translate(-0.445, 0, 0);
        return onEdge(post, 0.18);
      },
      material: TIMBER,
      offset: 1,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => {
        const post = new THREE.BoxGeometry(0.11, 2, 0.18);
        post.translate(0.445, 0, 0);
        return onEdge(post, 0.18);
      },
      material: TIMBER,
      offset: 1,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, 0.12, 0.18), 0.18),
      edged: edgedBy(0.18),
      material: TIMBER,
      offset: 1.94,
      scale: 1,
      aligned: true,
    },
  ],

  // Timber wall: planks laid one over another, for anybody who does not want
  // stone everywhere.
  29: [
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, WALL_HIGH, 0.16), 0.16),
      edged: edgedBy(0.16),
      material: TIMBER,
      offset: WALL_HIGH / 2,
      scale: 1,
      aligned: true,
    },
  ],

  // Stone floor: flags, sitting where boards would.
  30: [
    {
      geometry: () => new THREE.BoxGeometry(1, 0.08, 1),
      material: () => new THREE.MeshLambertMaterial({ color: 0x6f6b64 }),
      offset: 0.04,
      scale: 1,
      aligned: true,
    },
  ],

  // A window: the wall above and below the gap, and a sill across it. The same
  // thickness as the wall it stands in line with, or a run of walling steps in
  // and out wherever there is a window in it.
  31: [
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, 0.75, 0.22), 0.22),
      edged: edgedBy(0.22),
      material: STONE,
      offset: 0.375,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, 0.55, 0.22), 0.22),
      edged: edgedBy(0.22),
      material: STONE,
      offset: 1.625,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => onEdge(new THREE.BoxGeometry(1, 0.07, 0.28), 0.28),
      edged: edgedBy(0.28),
      material: TIMBER,
      offset: 0.78,
      scale: 1,
      aligned: true,
    },
    /*
     * The glass, which is the point of a window and was never drawn.
     *
     * Glass rather than a lit board. A bright plane in the opening makes a town
     * look occupied from a distance and is a lie close up: it is nailed over
     * the hole it is meant to be a view through, so a room with a fire in it
     * reads as a room with a yellow panel in the wall. This is something to see
     * through, and what shows through it is whatever is actually burning
     * inside.
     */
    {
      geometry: () => onEdge(new THREE.BoxGeometry(0.86, 0.52, 0.04), 0.04),
      material: GLASS,
      offset: 1.09,
      scale: 1,
      aligned: true,
    },
  ],

  // Ashen tree
  1: [
    {
      geometry: () => new THREE.CylinderGeometry(0.12, 0.18, 1.6, 6),
      material: () => new THREE.MeshLambertMaterial({ color: 0x4a3b2a }),
      offset: 0.8,
      scale: 1,
    },
    {
      geometry: () => new THREE.ConeGeometry(0.85, 2.2, 7),
      material: () => new THREE.MeshLambertMaterial({ color: 0x3f5c33 }),
      offset: 2.4,
      scale: 1,
    },
  ],
  // Copper vein
  2: [
    {
      geometry: () => new THREE.IcosahedronGeometry(0.55, 0),
      material: () => new THREE.MeshLambertMaterial({ color: 0x6b6257, flatShading: true }),
      offset: 0.4,
      scale: 1,
    },
  ],
  // Riverbank fishing spot
  // A fishing spot: broken water lying flat on the surface rather than a hoop
  // standing out of it. The rings travel outward and fade, and specks rise from
  // below, which together read as something moving down there.
  //
  // The first part is a plate covering the whole tile. It is never seen, and it
  // is what the pointer actually hits: a ring is mostly hole, and a spot you can
  // only click on the line of is a spot nobody can click.
  3: [
    {
      geometry: () => flat(new THREE.PlaneGeometry(1, 1)),
      material: () => new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }),
      offset: 0.02,
      scale: 1,
      aligned: true,
      unseen: true,
    },
    {
      geometry: () => flat(new THREE.RingGeometry(0.16, 0.24, 20)),
      material: () => RIPPLE(0.6, 0),
      offset: 0.05,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => flat(new THREE.RingGeometry(0.16, 0.24, 20)),
      material: () => RIPPLE(0.6, 0.33),
      offset: 0.05,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => flat(new THREE.RingGeometry(0.16, 0.24, 20)),
      material: () => RIPPLE(0.6, 0.66),
      offset: 0.05,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => new THREE.SphereGeometry(0.035, 6, 5),
      material: () => BUBBLE(0),
      offset: 0.06,
      scale: 1,
      aligned: true,
    },
    {
      geometry: () => new THREE.SphereGeometry(0.028, 6, 5),
      material: () => BUBBLE(0.5),
      offset: 0.06,
      across: 0.12,
      along: -0.1,
      scale: 1,
      aligned: true,
    },
  ],
  // Cooking fire. The plate under the flame is never seen and is what the
  // pointer hits, so a fire somebody is standing in can still be cooked on
  // while the flame itself is not drawn for them.
  4: [
    {
      geometry: () => flat(new THREE.PlaneGeometry(1, 1)),
      material: () => new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }),
      offset: 0.02,
      scale: 1,
      aligned: true,
      unseen: true,
    },
    {
      geometry: () => new THREE.ConeGeometry(0.4, 0.7, 6),
      material: () => new THREE.MeshBasicMaterial({ color: 0xd08b3a }),
      offset: 0.35,
      scale: 1,
    },
  ],
  // Strongbox
  5: [
    {
      geometry: () => new THREE.BoxGeometry(0.9, 0.8, 0.6),
      material: () => new THREE.MeshLambertMaterial({ color: 0x5a4a38 }),
      offset: 0.4,
      scale: 1,
    },
  ],
  default: [
    {
      geometry: () => new THREE.BoxGeometry(0.6, 0.6, 0.6),
      material: () => new THREE.MeshLambertMaterial({ color: 0x7a7368 }),
      offset: 0.3,
      scale: 1,
    },
  ],
};

/**
 * The ground, with a grain in it.
 *
 * Bare earth painted one flat brown across forty tiles reads as a plane with a
 * colour rather than as earth, and no amount of choosing the brown fixes that:
 * what is missing is not hue, it is that real ground is not uniform. So a slow
 * world-space noise lightens and darkens it, at two scales, strongest on dirt
 * and a worn path and absent on grass.
 *
 * Faded out as the pixels run short, and this is the whole difficulty rather
 * than an afterthought. A per pixel pattern with features smaller than a pixel
 * does not disappear when it stops being drawable, it beats against the pixel
 * grid and comes back as static, and static crawling over the ground as the
 * camera moves is worse than a flat brown by a wide margin. Each scale is
 * measured against how much ground one pixel covers and faded before it can do
 * that, the same way the water fades its own octaves.
 */
function groundMaterial(): THREE.MeshLambertMaterial {
  const material = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: false });

  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute float aGrain;
attribute float aWet;
varying float vGrain;
varying vec3 vGroundAt;`,
      )
      .replace(
        '#include <beginnormal_vertex>',
        `#include <beginnormal_vertex>

/*
 * The bed of a lake is lit as though it were flat.
 *
 * Ground is written in thirty-seconds of a tile and comes out as wide level
 * shelves with steps between them. Above water that is scenery. Under water it
 * is the one thing that shows: the shelves face the sun and the steps do not,
 * so a lake read as a patchwork of rounded squares, and no amount of colouring
 * the bed or the water over it made any difference, because it was never the
 * colour. It was the light.
 *
 * Light reaching the bottom of anything has been through the water first and
 * has very little direction left in it, so flattening the normal is nearer the
 * truth than the shelves were. The surface of the water does its own lighting,
 * and that is what should be catching the sun here.
 */
objectNormal = normalize(mix(objectNormal, vec3(0.0, 1.0, 0.0), aWet * 0.92));`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vGrain = aGrain;
vGroundAt = (modelMatrix * vec4(transformed, 1.0)).xyz;`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying float vGrain;
varying vec3 vGroundAt;

float groundHash(vec2 at) {
  vec3 held = fract(vec3(at.xyx) * 0.1031);
  held += dot(held, held.yzx + 33.33);
  return fract((held.x + held.y) * held.z);
}

float groundNoise(vec2 at) {
  vec2 cell = floor(at);
  vec2 into = fract(at);
  // Eased to the fifth, not the third. A cubic ease leaves a crease along every
  // cell edge where its slope changes; this one is smooth there too, which is
  // the other half of why the grid was showing.
  vec2 eased = into * into * into * (into * (into * 6.0 - 15.0) + 10.0);

  float a = groundHash(cell);
  float b = groundHash(cell + vec2(1.0, 0.0));
  float c = groundHash(cell + vec2(0.0, 1.0));
  float d = groundHash(cell + vec2(1.0, 1.0));

  return mix(mix(a, b, eased.x), mix(c, d, eased.x), eased.y);
}

/* How much of a pattern this fine can be drawn from where the camera is. */
float groundResolved(float tiles, float footprint) {
  return smoothstep(2.5, 7.0, tiles / max(footprint, 0.0001));
}`,
      )
      .replace(
        '#include <dithering_fragment>',
        `#include <dithering_fragment>

if (vGrain > 0.001) {
  float footprint = max(fwidth(vGroundAt.x), fwidth(vGroundAt.z));

  /*
   * Three scales. Broad patches a dozen tiles across that read as ground that
   * has dried unevenly, smaller ones a few tiles across, and a tread just under
   * a tile that only shows when somebody is standing on it. Each bows out on
   * its own as it stops being drawable, so what is left far away is an even
   * colour rather than a crawl.
   *
   * And each turned off the grid, by a different angle. This noise is built on
   * a square lattice, so everything it makes is square: read along the world's
   * own axes, the finest scale came out as a field of little blocks a tile
   * across, lined up with the tiles, which on a path reads as tiling rather
   * than as earth. Turned, the lattice no longer agrees with anything else in
   * the picture and the patches are patches.
   */
  vec2 turned = vGroundAt.xz;
  vec2 broadAt = vec2(turned.x * 0.936 - turned.y * 0.352, turned.x * 0.352 + turned.y * 0.936);
  vec2 wideAt = vec2(turned.x * 0.454 + turned.y * 0.891, -turned.x * 0.891 + turned.y * 0.454);
  vec2 closeAt = vec2(turned.x * 0.771 - turned.y * 0.637, turned.x * 0.637 + turned.y * 0.771);

  /*
   * And the ground the finer scales are read on is itself pushed about.
   *
   * Turning the lattice off the world's axes was not enough on its own: a
   * single scale of this noise is a regular tiling of blobs whichever way it
   * faces, and turning it produced a diagonal tiling instead of a square one.
   * What breaks a tiling is bending the ground under it, so the finer scales
   * are read at a point shifted by a slow reading of the coarse one. The cells
   * stop lining up with each other and the pattern stops being a pattern.
   */
  vec2 pushed =
    vec2(groundNoise(broadAt * 0.11), groundNoise(broadAt * 0.11 + 41.3)) - vec2(0.5);

  float broad = groundNoise(broadAt * 0.075) - 0.5;
  float wide = groundNoise(wideAt * 0.26 + pushed * 1.4) - 0.5;
  float close = groundNoise(closeAt * 1.15 + pushed * 4.6) - 0.5;

  float seen =
    broad * 0.34 * groundResolved(13.0, footprint) +
    wide * 0.36 * groundResolved(3.8, footprint) +
    close * 0.30 * groundResolved(0.87, footprint);

  /*
   * Multiplied rather than added, so a dark path grains darkly and a pale one
   * grains palely instead of both drifting towards the same grey.
   *
   * And lopsided on purpose. Ground varies mostly by drying lighter, not by
   * going darker: an even swing came out as dark splotches through a path, and
   * a splotch is a stain rather than a texture. So the light half is given most
   * of the range and the dark half a third of it.
   */
  float lighter = max(seen, 0.0);
  float darker = min(seen, 0.0);
  gl_FragColor.rgb *= 1.0 + (lighter * 0.78 + darker * 0.3) * vGrain;
}`,
      );
  };

  return material;
}

/**
 * How much grain a kind of ground takes, from nought to one.
 *
 * Bare earth and a worn path are the two that need it: both are one flat brown
 * over a large area, and a large area of one flat brown reads as a painted
 * plane however well the brown is chosen. Sand and ash take a little. Grass
 * takes none, because it has the clutter standing on it and does not need to be
 * told twice, and snow takes none because snow really is one flat white.
 */
function grainOf(made: number): number {
  if (made === 2) return 1;
  if (made === 5) return 0.85;
  if (made === 3) return 0.4;
  if (made === 7) return 0.5;
  if (made === 4) return 0.35;
  return 0;
}

/**
 * What a patch of ground is made of, worked out from where it sits relative to
 * the sea: sea floor, then beach, then grass, then bare rock high up.
 */
function groundColour(height: number, made = 0): THREE.Color {
  const colour = new THREE.Color();

  // Painted ground overrides where it sits, which is the point of painting it.
  if (made === 1) return colour.copy(COLORS.grass);
  if (made === 2) return colour.copy(COLORS.dirt);
  if (made === 3) return colour.copy(COLORS.sand);
  if (made === 4) return colour.copy(COLORS.rock);
  if (made === 5) return colour.copy(COLORS.path);
  if (made === 6) return colour.copy(COLORS.snow);
  if (made === 7) return colour.copy(COLORS.ash);
  if (made === 8) return colour.copy(COLORS.lava);
  if (made === 9) return colour.copy(COLORS.shingle);
  if (made === 10) return colour.copy(COLORS.mud);

  const aboveWater = height - SEA_LEVEL;
  if (aboveWater < SHORE_BAND) {
    // Held at the sand below the waterline rather than run on past it. Ground
    // under water is still ground and wants a colour of its own, and carrying
    // the band on downwards took it away from the grass in the only direction
    // left, which was away from any colour sand has ever been.
    const up = Math.max(0, Math.min(1, aboveWater / SHORE_BAND));
    return colour.copy(COLORS.sand).lerp(COLORS.grass, up);
  }

  if (height > 5.2) return colour.copy(COLORS.rock);
  return colour.copy(COLORS.grass).lerp(COLORS.grassDry, Math.min(height / 6, 1));
}

/** How long a body is left where it fell before it is taken away. */
const DEATH_HELD_MS = 1600;

/** How long a hit splat hangs over somebody's head. */
const HIT_MARK_MS = 1300;

/**
 * What a stand-in for a piece of scenery is drawn with.
 *
 * Never seen: it writes depth so the stroke knows the shape, and no colour so
 * the thing it stands in for is what is actually looked at.
 */
/** How wide the stroke is, in pixels, whatever it is drawn around. */
const STROKE_PIXELS = 2.5;

/**
 * That width as a share of the screen, across and down.
 *
 * The shape is grown on the screen rather than in the world, which is the only
 * way a line comes out the same width everywhere: grown in the world, the far
 * side of a shape moves away from the camera as much as it moves outwards, and
 * the line thins to nothing wherever a surface turns away.
 */
const STROKE_WIDTH = { value: new THREE.Vector2(0.004, 0.004) };

/** Drawn after everything solid: the mask first, then the line around it. */
const MASK_ORDER = 2;
const STROKE_ORDER = 3;

/** Which bit of the stencil says "the thing being outlined is right here". */
const STROKE_STENCIL = 1;

/**
 * The shape itself, written into the stencil and nothing else.
 *
 * The line is then drawn everywhere the shape is not, which is what makes it a
 * line rather than a blob: without it the grown copy has to win a depth test
 * against whatever is behind it, and from above, that is the ground a few
 * inches away. Half of every outline went missing that way.
 */
const STROKE_MASKS = new Map<string, THREE.Material>();

function maskMaterial(motion: ShapeMotion | null): THREE.Material {
  const key = motion?.id ?? '';
  const held = STROKE_MASKS.get(key);
  if (held) return held;

  const material = new THREE.MeshBasicMaterial({
    colorWrite: false,
    depthWrite: false,
    // No depth test: a copy of a shape can never win a depth test against the
    // shape it copies, and trying left the stencil full of stripes. This marks
    // out the whole silhouette, and the line goes round the outside of it.
    depthTest: false,
    stencilWrite: true,
    stencilRef: STROKE_STENCIL,
    stencilFunc: THREE.AlwaysStencilFunc,
    stencilZPass: THREE.ReplaceStencilOp,
    // A ripple that has faded out is not there to be marked, and a stroke round
    // where it used to be is a stroke round nothing.
    ...(motion ? { transparent: false, alphaTest: STROKE_FADED } : {}),
  });

  if (motion) moves(material, motion);
  STROKE_MASKS.set(key, material);

  return material;
}

/** Below this much alpha, a shape counts as gone and takes no line. */
const STROKE_FADED = 0.25;

/**
 * The same shape with its normals smoothed, for the stroke to be grown along.
 *
 * Everything in the world is flat shaded, which means a box has four normals at
 * every corner rather than one. Growing a shape along those tears it open at
 * every hard edge, and the line comes out in pieces. Averaging the normals of
 * the faces that meet at a point closes it up again. Kept by shape, because a
 * thousand trees are one shape.
 */
const STROKE_SHAPES = new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>();

function strokeShape(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const held = STROKE_SHAPES.get(geometry);
  if (held) return held;

  const copy = geometry.clone();
  const position = copy.getAttribute('position');
  const normal = copy.getAttribute('normal');

  if (position && normal) {
    // Points that sit on top of each other share a normal: the average of all
    // the faces that meet there.
    const shared = new Map<string, [number, number, number]>();
    const keyOf = (index: number): string =>
      `${position.getX(index).toFixed(4)},${position.getY(index).toFixed(4)},${position.getZ(index).toFixed(4)}`;

    for (let index = 0; index < position.count; index++) {
      const key = keyOf(index);
      const sum = shared.get(key) ?? [0, 0, 0];
      sum[0] += normal.getX(index);
      sum[1] += normal.getY(index);
      sum[2] += normal.getZ(index);
      shared.set(key, sum);
    }

    for (let index = 0; index < position.count; index++) {
      const sum = shared.get(keyOf(index)) ?? [0, 1, 0];
      const length = Math.hypot(sum[0], sum[1], sum[2]) || 1;
      normal.setXYZ(index, sum[0] / length, sum[1] / length, sum[2] / length);
    }

    normal.needsUpdate = true;
  }

  STROKE_SHAPES.set(geometry, copy);
  return copy;
}

/** One material per colour: a hover is a colour, not a new material every frame. */
const STROKE_MATERIALS = new Map<string, THREE.Material>();

function strokeMaterial(colour: number, motion: ShapeMotion | null): THREE.Material {
  const key = `${colour}:${motion?.id ?? ''}`;
  const held = STROKE_MATERIALS.get(key);
  if (held) return held;

  // Inside out, so the shape itself covers everything but the rim. The colour
  // is emitted rather than lit: a line is a line, the same wherever the sun is.
  // Lambert rather than basic because a basic material only works out its
  // normals when something else needs them, and the normals are the whole
  // trick here.
  const material = new THREE.MeshLambertMaterial({
    color: 0x000000,
    emissive: colour,
    // Both sides: the stencil already keeps the line out of the shape, and the
    // near side of a shape is what its outline is made of. Drawing only the far
    // side put the line inside the silhouette, where nothing could be seen of
    // it until it was twenty pixels wide.
    side: THREE.DoubleSide,
    toneMapped: false,
    fog: false,
    // Drawn over whatever is behind it, but only outside the shape itself.
    depthTest: false,
    depthWrite: false,
    stencilWrite: true,
    stencilRef: STROKE_STENCIL,
    stencilFunc: THREE.NotEqualStencilFunc,
    // A shape that has faded out of the picture takes its line with it.
    ...(motion ? { alphaTest: STROKE_FADED } : {}),
  });

  material.onBeforeCompile = (shader) => {
    shader.uniforms.strokeWidth = STROKE_WIDTH;
    material.userData.shader = shader;

    // Whatever the shape does to itself, the line does too, or the line ends up
    // drawn round where the shape was when the pointer first landed on it.
    if (motion) {
      Object.assign(shader.uniforms, motion.uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\n${motion.shared}`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>\n${motion.vertex}`);

      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>\n${motion.shared}`)
        .replace(
          '#include <dithering_fragment>',
          `#include <dithering_fragment>\n${motion.fragment}`,
        );
    }
    shader.vertexShader = shader.vertexShader.replace(
      'void main() {',
      `uniform vec2 strokeWidth;
       void main() {`,
    );
    shader.vertexShader = shader.vertexShader.replace(
      '#include <project_vertex>',
      `vec4 mvPosition = vec4(transformed, 1.0);
       #ifdef USE_INSTANCING
         mvPosition = instanceMatrix * mvPosition;
       #endif
       mvPosition = modelViewMatrix * mvPosition;
       gl_Position = projectionMatrix * mvPosition;

       // The shape pushed outwards on the screen, along the direction its
       // surface faces. Multiplying by w keeps the width in pixels rather than
       // letting perspective divide it away with distance.
       vec2 strokeOut = (projectionMatrix * vec4(normalize(transformedNormal), 0.0)).xy;

       // A surface looking straight at the camera has nowhere to go that way:
       // a ripple lying flat on the water would grow towards the top of the
       // screen rather than outwards. Those grow away from their own middle.
       if (length(strokeOut) < 0.2) {
         vec4 strokeMiddle = projectionMatrix * modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
         strokeOut = gl_Position.xy / gl_Position.w - strokeMiddle.xy / strokeMiddle.w;
       }

       if (length(strokeOut) > 0.0001) {
         gl_Position.xy += normalize(strokeOut) * strokeWidth * gl_Position.w;
       }`,
    );
  };

  STROKE_MATERIALS.set(key, material);
  return material;
}

/**
 * How long a marker stays up when the walk it describes is already over.
 *
 * A click on the far side of a river while standing on the bank moves nobody,
 * and a marker that vanished on the same frame would leave the question of what
 * happened unanswered.
 */
const MARKER_GRACE_MS = 2400;

/**
 * The colour of a quest mark.
 *
 * The primary, and the same colour the marker at the edge of the screen uses:
 * the arrow over the thing and the arrow pointing off towards it are the same
 * hint seen two ways, so they read as one thing rather than two.
 */
const QUEST_MARK = 0x0cd5e3;

/** How large a splat is drawn, in world units. */
const SPLAT_SIZE = 0.72;

/** Which way a shaft is built, so it can be turned to point where it is going. */
const UP = new THREE.Vector3(0, 1, 0);

/**
 * What each thing that crosses the ground looks like.
 *
 * Named rather than numbered, because what can be thrown is content: a
 * character says it throws fire and this is where the client decides what fire
 * looks like. Anything it has never heard of is drawn as an arrow.
 */
/**
 * What the world is lit by at noon and in the dead of night.
 *
 * Noon is exactly what the scene was built with, so a world with the day and
 * night switched off is the world as it was, to the number.
 */
const NOON = {
  sun: new THREE.Color(0xffeccd),
  sunStrength: 1.9,
  above: new THREE.Color(0x9dc0e0),
  below: new THREE.Color(0x4a4030),
  skyStrength: 1.1,
  air: new THREE.Color(0x1d2a30),
};

const NIGHT = {
  sun: new THREE.Color(0x9fb6d8),
  sunStrength: 0.5,
  above: new THREE.Color(0x33507a),
  below: new THREE.Color(0x1a1c26),
  skyStrength: 0.55,
  air: new THREE.Color(0x0a1016),
};

const ARROW = { colour: 0xd8c48a, shaft: true };

/** How a spell written in the tools says its projectile should look. */
export interface FlyingStyle {
  colour?: string;
  colour2?: string;
  size?: number;
  arc?: number;
  model?: { file: string; scale: number; offsetX: number; offsetY: number; offsetZ: number };
}

const FLYING_LOOKS: Readonly<Record<string, { colour: number; shaft: boolean }>> = {
  arrow: ARROW,
  bolt: { colour: 0xb9c3c9, shaft: true },
  air: { colour: 0xd6f7ff, shaft: false },
  water: { colour: 0x3f8fd8, shaft: false },
  earth: { colour: 0x7d6a3a, shaft: false },
  fire: { colour: 0xf07a24, shaft: false },
};

/** And how long the bar under it stays up once nothing else has happened. */
const BAR_SHOWN_MS = 6000;

/**
 * How many pieces each tile of water is cut into along each side.
 *
 * Enough for the waterline to follow a slope instead of stepping across it, few
 * enough that a large lake is still a few thousand triangles.
 */
const WATER_PIECES = 6;

/**
 * How little water there has to be for a piece of ground to count as dry.
 *
 * Ground that lies a hair below the surface is under water by the numbers and
 * looks like haze on the grass. The waterline is put where there is enough water
 * to see, which is what gives a pond an edge rather than a halo.
 */
const WATER_EDGE = 0.07;

/**
 * How far up the bank the sheet of water is carried, in tiles.
 *
 * The surface is flat and the ground is not, so cutting the sheet exactly at the
 * waterline leaves a ragged line of triangle edges where the two meet. Instead it
 * is run on into the bank and left to be buried: ground in front of water hides
 * it, and what is left is a shoreline drawn by the hill rather than by the mesh.
 */
const WATER_SKIRT = 0.45;

/**
 * How far from the bank a swell still runs at it, in tiles.
 *
 * The same number the shader fades the swell out over. Past it there is no shore
 * to run at, and pretending otherwise is what put rings on the open water.
 */
const SWELL_REACH = 74;

/**
 * How many tiles of water make a pond, and how many make an open one.
 *
 * Below the first, a body only ripples. Above the second it breaks like the sea
 * does. Between them it laps, which is what a lake with a bit of weather on it
 * looks like from the bank.
 */
const SMALL_BODY = 900;
const OPEN_BODY = 9000;

/**
 * How much two neighbouring tiles of water may differ and still be one body.
 *
 * Wide enough for a river to run downhill. It used to be a tenth of this, which
 * meant a stream that fell any distance was cut into a chain of separate pools
 * with a seam between each: a body was drawn at one flat height, so a body had
 * to be flat. Now the surface follows the tiles, and this is only asking whether
 * two tiles of water are the same water.
 */
const SAME_BODY = 1.5;

interface WaterBody {
  /** The lowest surface in it, which is what its bank and its skirt are judged by. */
  level: number;
  /**
   * Whether this is lava rather than water.
   *
   * Told apart by what the ground under it is painted, which is the one thing a
   * tile already carries that says which liquid it is. Everything else about it
   * is the same: it is a surface at a height, it stops anybody walking into it,
   * and it is drawn by the same shader with different numbers.
   */
  lava: boolean;
  tiles: number[];
  /**
   * The surface over each of its tiles.
   *
   * One height for the whole body drew a river at the height of the pool it came
   * out of, hanging in the air above the sea it was running into. Water lies on
   * the ground; where the ground falls, so does it.
   */
  levels: Map<number, number>;
}

/** A body of water worked out ready to draw: its reach, its shore and its drift. */
interface PlacedWater {
  /** Its tiles and the bank it runs into, which is one tile past them. */
  covered: ReadonlySet<number>;
  flow: { x: number; z: number };
  shoreAt: (x: number, z: number) => number;
}

/**
 * The separate pieces of water in a region.
 *
 * Water is stored a tile at a time, and tiles that were painted at slightly
 * different moments end up at slightly different heights. Drawn as they are
 * stored that is a set of sheets at different levels with seams between them,
 * which is exactly what a pond does not look like. Neighbouring tiles close
 * enough in height are gathered here into one body and given a single surface,
 * while a genuine step down, a waterfall or a lower pool, stays its own body.
 */
/**
 * How far every point of a body of water is from the nearest bank, in tiles.
 *
 * Worked out on a grid finer than the tiles themselves, by marking every point
 * where the ground comes up through the surface and then letting that distance
 * spread inwards in two passes. Two passes rather than a search because it runs
 * whenever a region is built and a lake can be thousands of tiles: the answer is
 * within a few hundredths of the true distance, which is far closer than anything
 * drawn from it can show.
 */
function shoreField(
  covered: ReadonlySet<number>,
  level: number,
  width: number,
  bedAt: (tx: number, tz: number, alongX: number, alongZ: number) => number,
): (x: number, z: number) => number {
  let minTx = Infinity;
  let maxTx = -Infinity;
  let minTz = Infinity;
  let maxTz = -Infinity;

  for (const index of covered) {
    const tx = index % width;
    const tz = Math.floor(index / width);
    if (tx < minTx) minTx = tx;
    if (tx > maxTx) maxTx = tx;
    if (tz < minTz) minTz = tz;
    if (tz > maxTz) maxTz = tz;
  }

  if (minTx > maxTx) return () => 0;

  const step = 1 / WATER_PIECES;
  const gridWidth = (maxTx - minTx + 1) * WATER_PIECES + 1;
  const gridHeight = (maxTz - minTz + 1) * WATER_PIECES + 1;
  const distance = new Float32Array(gridWidth * gridHeight);

  const FAR = 1e6;

  for (let gz = 0; gz < gridHeight; gz++) {
    for (let gx = 0; gx < gridWidth; gx++) {
      const alongX = (gx % WATER_PIECES) * step;
      const alongZ = (gz % WATER_PIECES) * step;
      const tx = minTx + Math.floor(gx / WATER_PIECES);
      const tz = minTz + Math.floor(gz / WATER_PIECES);

      const dry =
        !covered.has(tz * width + tx) || bedAt(tx, tz, alongX, alongZ) >= level - WATER_EDGE;
      distance[gz * gridWidth + gx] = dry ? 0 : FAR;
    }
  }

  // Two sweeps, each carrying what the last one knew: down and right, then up
  // and left. What comes out is the distance to the nearest dry point.
  const diagonal = Math.SQRT2;

  const reach = (at: number, from: number, cost: number): void => {
    const through = (distance[from] ?? FAR) + cost;
    if (through < (distance[at] ?? FAR)) distance[at] = through;
  };

  for (let gz = 0; gz < gridHeight; gz++) {
    for (let gx = 0; gx < gridWidth; gx++) {
      const at = gz * gridWidth + gx;
      if (gx > 0) reach(at, at - 1, 1);
      if (gz > 0) reach(at, at - gridWidth, 1);
      if (gx > 0 && gz > 0) reach(at, at - gridWidth - 1, diagonal);
      if (gx + 1 < gridWidth && gz > 0) reach(at, at - gridWidth + 1, diagonal);
    }
  }

  for (let gz = gridHeight - 1; gz >= 0; gz--) {
    for (let gx = gridWidth - 1; gx >= 0; gx--) {
      const at = gz * gridWidth + gx;
      if (gx + 1 < gridWidth) reach(at, at + 1, 1);
      if (gz + 1 < gridHeight) reach(at, at + gridWidth, 1);
      if (gx + 1 < gridWidth && gz + 1 < gridHeight) reach(at, at + gridWidth + 1, diagonal);
      if (gx > 0 && gz + 1 < gridHeight) reach(at, at + gridWidth - 1, diagonal);
    }
  }

  return (x: number, z: number): number => {
    const gx = Math.round((x - minTx) * WATER_PIECES);
    const gz = Math.round((z - minTz) * WATER_PIECES);
    if (gx < 0 || gz < 0 || gx >= gridWidth || gz >= gridHeight) return 0;
    return Math.min(FAR, distance[gz * gridWidth + gx] ?? 0) * step;
  };
}

export function waterBodies(
  region: LocalRegion,
  from?: { tx: number; tz: number; width: number; height: number },
): WaterBody[] {
  // Only the stretch asked for. Walking a whole region to find its ponds takes
  // two seconds on a continent, and it was being done every time a square of
  // ground arrived: a two second freeze every few paces, which is exactly what
  // walking the mainland felt like.
  const stretch = {
    tx: Math.max(0, from?.tx ?? 0),
    tz: Math.max(0, from?.tz ?? 0),
    width: from?.width ?? region.width,
    height: from?.height ?? region.height,
  };
  stretch.width = Math.max(0, Math.min(region.width - stretch.tx, stretch.width));
  stretch.height = Math.max(0, Math.min(region.height - stretch.tz, stretch.height));

  const bodies: WaterBody[] = [];
  const seen = new Uint8Array(region.width * region.height);
  const stride = region.width + 1;

  /** Whether the liquid on a tile is lava, which is what the ground says. */
  const isLava = (index: number): boolean => (region.ground?.[index] ?? 0) === GroundKind.Lava;

  const levelOf = (index: number): number | null => {
    if (((region.flags[index] ?? 0) & WATER_FLAG) === 0) return null;

    const level = region.water[index] ?? SEA_LEVEL;
    if (Math.abs(level - SEA_LEVEL) < 0.01) return null;

    const tx = index % region.width;
    const tz = Math.floor(index / region.width);
    // Ground raised back over an old water tile is not water any more.
    const bed = Math.min(
      region.heights[tz * stride + tx] ?? 0,
      region.heights[tz * stride + tx + 1] ?? 0,
      region.heights[(tz + 1) * stride + tx] ?? 0,
      region.heights[(tz + 1) * stride + tx + 1] ?? 0,
    );
    return bed < level ? level : null;
  };

  const held: number[] = [];
  for (let row = 0; row < stretch.height; row++) {
    const line = (stretch.tz + row) * region.width + stretch.tx;
    for (let column = 0; column < stretch.width; column++) held.push(line + column);
  }

  for (const index of held) {
    if (seen[index] === 1) continue;

    const level = levelOf(index);
    if (level === null) {
      seen[index] = 1;
      continue;
    }

    const tiles: number[] = [];
    const levels = new Map<number, number>();
    const queue = [index];
    seen[index] = 1;
    let lowest = level;

    while (queue.length > 0) {
      const at = queue.pop() as number;
      const atLevel = levelOf(at);
      if (atLevel === null) continue;

      tiles.push(at);
      levels.set(at, atLevel);
      if (atLevel < lowest) lowest = atLevel;

      const tx = at % region.width;
      const tz = Math.floor(at / region.width);

      for (const [awayX, awayZ] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        const nx = tx + (awayX as number);
        const nz = tz + (awayZ as number);
        if (nx < stretch.tx || nz < stretch.tz) continue;
        if (nx >= stretch.tx + stretch.width || nz >= stretch.tz + stretch.height) continue;

        const next = nz * region.width + nx;
        if (seen[next] === 1) continue;

        const nextLevel = levelOf(next);
        if (nextLevel === null || Math.abs(nextLevel - atLevel) > SAME_BODY) continue;
        // Lava and water are not the same body however close their surfaces sit.
        if (isLava(next) !== isLava(index)) continue;

        seen[next] = 1;
        queue.push(next);
      }
    }

    // The lowest of them, for the bank and the skirt. The surface itself is
    // drawn from the height over each tile, so a body may run downhill.
    bodies.push({ level: lowest, lava: isLava(index), tiles, levels });
  }

  return bodies;
}

/**
 * A splash: a round middle with the edges torn out of it.
 *
 * Built from one path so the outline runs all the way round, which is what makes
 * it read as thrown rather than as a circle with spikes on.
 */
function drawSplash(context: CanvasRenderingContext2D, middle: number, radius: number): void {
  const points = 11;

  context.beginPath();
  for (let at = 0; at <= points * 2; at++) {
    const angle = (at / (points * 2)) * Math.PI * 2;
    // Alternating long and short, with a wobble, so no two arms match.
    // Shallow tears rather than deep points: a splash, not a star.
    const reach = at % 2 === 0 ? radius : radius * (0.82 + 0.08 * Math.sin(at * 2.7));
    const x = middle + Math.cos(angle) * reach;
    const y = middle + Math.sin(angle) * reach * 0.92;

    if (at === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  context.closePath();

  const fill = context.createRadialGradient(
    middle,
    middle - radius * 0.3,
    radius * 0.2,
    middle,
    middle,
    radius,
  );
  fill.addColorStop(0, '#d8382c');
  fill.addColorStop(1, '#7a1a14');

  context.fillStyle = fill;
  context.fill();

  context.lineWidth = 3;
  context.lineJoin = 'round';
  context.strokeStyle = 'rgba(12, 8, 6, 0.85)';
  context.stroke();
}

/** A shield: flat across the top, drawn to a point at the bottom. */
function drawShield(context: CanvasRenderingContext2D, middle: number, radius: number): void {
  const top = middle - radius * 0.95;
  const side = radius * 0.86;

  context.beginPath();
  context.moveTo(middle - side, top);
  context.lineTo(middle + side, top);
  context.lineTo(middle + side, middle + radius * 0.15);
  // The two sides sweep in to a point, which is the whole of a shield's shape.
  context.quadraticCurveTo(middle + side * 0.75, middle + radius, middle, middle + radius * 1.1);
  context.quadraticCurveTo(
    middle - side * 0.75,
    middle + radius,
    middle - side,
    middle + radius * 0.15,
  );
  context.closePath();

  const fill = context.createLinearGradient(0, top, 0, middle + radius);
  fill.addColorStop(0, '#8fc0dc');
  fill.addColorStop(1, '#2f6485');

  context.fillStyle = fill;
  context.fill();

  context.lineWidth = 3;
  context.lineJoin = 'round';
  context.strokeStyle = 'rgba(12, 8, 6, 0.85)';
  context.stroke();

  // A band across the top, which is what stops it reading as a plain pentagon.
  context.beginPath();
  context.moveTo(middle - side, top + radius * 0.34);
  context.lineTo(middle + side, top + radius * 0.34);
  context.lineWidth = 2;
  context.strokeStyle = 'rgba(12, 8, 6, 0.35)';
  context.stroke();
}

/**
 * How fast a body turns on the spot, in radians a second.
 *
 * Fast enough that facing something feels like an answer rather than a decision,
 * slow enough to be a turn rather than a snap. A quarter turn takes about a fifth
 * of a second, which is roughly how long it takes a person to notice one.
 */
const TURN_SPEED = 9;

/**
 * Eases a heading towards where it should be.
 *
 * Around the short way, always: turning from just west of north to just east of
 * it is a nudge, and a body that spins the long way round to do it looks broken.
 * Past a half turn the shortest way is genuinely the long way, and that is fine:
 * somebody spun around behind you does turn all the way round.
 */
function turnTowards(from: number, to: number, delta: number): number {
  const away = Math.atan2(Math.sin(to - from), Math.cos(to - from));
  if (Math.abs(away) < 0.001) return to;

  const step = TURN_SPEED * delta;
  if (Math.abs(away) <= step) return to;

  return from + Math.sign(away) * step;
}
